import * as THREE from 'three';
import { ISLAND_WORLD, BIOMES } from '@/world/types';
import { Region } from '@/world/region';
import { findLandmarks, nearestLandmark, describeLandmark } from '@/world/landmarks';
import { ChunkManager } from '@/world/chunkManager';
import { surfaceHeight, CHUNK_SIZE } from '@/world/chunk';
import { marchToGround } from '@/world/aim';
import { installAerialPerspective } from '@/render/aerial';
import { createPost } from '@/render/post';
import { createSky, paintSky } from '@/render/sky';
import { buildHorizon } from '@/render/horizon';
import { buildFarCanopy } from '@/render/farCanopy';
import { createClouds } from '@/render/clouds';
import { createBirds } from '@/render/birds';
import { createSea } from '@/render/water';
import { buildRivers } from '@/render/rivers';
import { buildFalls } from '@/render/falls';
import { buildRocks } from '@/render/rocks';
import { createRelics } from '@/render/relics';
import { createFireFx } from '@/render/fireFx';
import { createVerbFx } from '@/render/verbFx';
import { createMortals, type BandView } from '@/render/mortals';
import { glideStep, type GlideState } from '@/ui/glide';
import { createReticle } from '@/render/reticle';
import { createHeldThing } from '@/render/held';
import { createNearDetail } from '@/render/nearDetail';
import { createSound } from '@/ui/sound';
import { createStructures, type StructureView } from '@/render/structures';
import { createHabitation, campsFrom, clearingRadius, type CampView } from '@/render/habitation';
import { createSelection } from '@/render/selection';
import { idSeed } from '@/core/rng';
import { placeKey, namePlant, nameRock, nameLitter, nameStructure, namePerson, nameHerd,
  nameAnimal,
  nameTrail, nameAnchor, nameGround, channelRadius, isThing, describeTarget,
  type Target, type TargetKind, type SeenAnimal } from '@/ui/attention';
import { createVegetationKit } from '@/render/vegetation';
import { createPaths, type PathView } from '@/render/paths';
import { routeFor, standClosure } from '@/render/route';
import {
  createWildlife, herdSpread, standRadius, BODY_LENGTH, bodyAt, bodiesInView,
  type WildlifeView,
} from '@/render/wildlife';
import { createAnchors, type AnchorView } from '@/render/anchors';
import { toSocial, SOCIAL_CELL_SIZE } from '@/sim/social/socialGrid';
import { WorldClock, DAY, YEAR_DAYS } from '@/sim/clock';
import { perceive, type PerceivedEvent } from '@/sim/events';
import { ANCHOR_COST } from '@/sim/anchor';
import { GodState, IMPULSE_STONE, levelOf, reachOf } from '@/sim/god';
import { Simulation, focusFor, focusForRadius, GRASP_REACH,
  type Focus } from '@/sim/simulation';
import { OMEN_RANGE } from '@/sim/mortals/band';
import { saveLocal, loadLocal, decodeSnapshot,
  type SimulationSnapshot } from '@/sim/persistence/snapshot';
import { applyOverlay } from '@/world/overlay';
import { applyEnv } from '@/world/env';
import { applyEarth, scoopFor, scoopReads, handRadius, spreadForVolume,
  REPOSE } from '@/world/earth';
import { boundsOfChunks, buildCanopyField, farVersion, type FarBounds } from '@/world/farField';
import { costBand, costOf, costReasons, processViability,
  type CostBand } from '@/sim/leverage';
import { applyWear, wornAt } from '@/world/wear';
import { applyThings, sparing, PROMOTE } from '@/world/things';
import { applySere, type SereStore } from '@/world/sere';
import { litterNear, POINTABLE_FAR } from '@/world/litter';
import { relicNear, nameRelic, RELIC_KINDS, type RelicKind } from '@/world/relics';
import { weatherAt, seasonalDryness } from '@/world/weather';
import { boltsAtMinute } from '@/sim/lightning';
import { createRain } from '@/render/rain';
import { createCurtains } from '@/render/curtains';
import { displacing } from '@/world/carried';
import { DeityPresence, EYE_HEIGHT, R_MIN, awarenessRange, camForFocus } from '@/ui/presence';
import { MortalPresence, MORTAL_FOV } from '@/ui/mortalPresence';
import { PlayerModes } from '@/ui/inhabit';
import { MORTAL_VERBS, mortalSlotForKey } from '@/ui/mortalVerbs';
import { WorldSession } from '@/ui/session';
import { REGISTER_OF, REGISTER_WORD, apartFrom, doingWord, herdTrend, peopleNow,
  reinforceable, whyThey, type Register } from '@/ui/thread';
import { populationOf, readBeast, type BeastContext } from '@/sim/wildlife/reading';
import type { WildlifeSpeciesId } from '@/sim/wildlife/types';
import { beastNow, populationNow, populationWord, reinforceableBeast,
  type BeastSight } from '@/ui/beasts';
import { createHud, phrase, type CastVerb, type HudPerson, type Verb } from '@/ui/hud';
import type { CardStanding, CardVerb, CardView } from '@/ui/card';
import {
  anchorNow, animalNow, groundNow, herdNow, litterNow, plantNow, strainNow,
  significanceOf, structureNow, trailNow,
} from '@/ui/subject';
import { framingFor, travels, type Framing } from '@/ui/observe';
import { gatherHooks, planArrival, beginArrival, stepArrival, vantageOver, openingBand,
  WAKE_S, type Arrival } from '@/ui/arrival';
import { readAt, isSilent, reachFor, type Note } from '@/sim/lore';
import { CHRONICLE_CELL, FORGET_AFTER } from '@/sim/chronicle';
import { absenceSpan, runAbsence, REAL_SECONDS_PER_WORLD_YEAR }
  from '@/sim/persistence/catchup';
import { saying, gist, LEFT, type LoreLine } from '@/ui/lore';
import { createSettings } from '@/ui/settings';
import { createGodhoodPanel, loadWeb } from '@/ui/godhood';
import { chronicleOracle, worldHistory } from '@/sim/godhood/oracle';
import { standingOf } from '@/sim/godhood/level';
import { NODES } from '@/sim/godhood/web';
import { createMenu } from '@/ui/menu';
import { AURA_RADIUS, type AuraVerb } from '@/sim/aura';
import { describe, personCard } from '@/ui/lives';
import { attach, attachRequestFrom, type AttachedWorld, type AttachRequest }
  from '@/ui/attach';
import { crossThreshold, thresholdSkipped, type Entry } from '@/ui/threshold';
import { strainAbout, strainHerdPhenotype, strainPlantSource } from '@/render/strainPhenotype';
import type { PlantPhenotypeSource } from '@/render/phenotype';
import { createAura, DEFAULT_AURA, detailFor } from '@/render/aura';
import {
  CAPTURE_T, STATIONS, blessRequested, blessRig, burnRequested, burnRig,
  capDays, captureT, frameReading, probeRequested, requestedStation,
} from '@/ui/capture';
import { parsePhenoLab } from '@/ui/phenoLab';

/**
 * The capture station named in `?cap=…`, or null in ordinary play. Read once:
 * the query cannot change without a reload, and a station that could be armed
 * mid-session would be a player-facing time control, which it must never be.
 *
 * **It releases the moment a human touches anything, and that is not a nicety.**
 * A station re-parks the presence every frame and pins the clock, so on a URL
 * that still carries `?cap=` the world fights every key and every drag — the
 * camera shakes and cannot be flown, which is indistinguishable from the
 * renderer being broken. The station exists for unattended screenshots, and a
 * screenshot involves no input, so yielding on the first real one costs the
 * harness nothing and costs a person landing on a stale URL everything.
 */
let capStation: string | null =
  import.meta.env.DEV ? requestedStation(window.location.search) : null;
/**
 * The instant a station pins: the gate's midday unless `?tod=` moved the hour
 * (same day, same weather field — `ui/capture.ts`). Read once with the station.
 */
const capT = capStation ? captureT(window.location.search) : CAPTURE_T;
if (capStation) {
  const release = () => {
    if (!capStation) return;
    capStation = null;
    notice('capture station released — the world is yours');
  };
  for (const ev of ['keydown', 'pointerdown', 'wheel'] as const) {
    window.addEventListener(ev, release, { once: true, capture: true });
  }
}
const capProbe = import.meta.env.DEV && probeRequested(window.location.search);
/**
 * The phenotype lab, `?pheno=…` — dev-only, read once, null in ordinary play
 * (`ui/phenoLab.ts`). It hands the chunk streamer a plant phenotype source
 * and every herd view a wildlife phenotype, so biological expression can be
 * LOOKED AT under the capture stations before any simulation supplies it.
 * Like the stations it cannot be armed mid-session: the reload is the
 * mechanism, and the query dying with the URL is what keeps it a lab.
 */
const phenoLab = import.meta.env.DEV ? parsePhenoLab(window.location.search) : null;
let capFrames = 0;
/**
 * The reading's panel, created up front rather than on success. If it only
 * appeared once a reading landed, "no panel" would mean both "the probe is off"
 * and "the probe threw", which are different bugs and cost an hour to tell
 * apart once already.
 */
const capPanel = capProbe ? document.createElement('div') : null;
if (capPanel) {
  capPanel.style.cssText = 'position:fixed;left:8px;top:8px;z-index:9999;font:11px ' +
    'ui-monospace,monospace;color:#ffd479;background:rgba(0,0,0,.82);padding:5px 8px;white-space:pre';
  capPanel.textContent = 'probe armed — waiting for the ring to settle';
  document.addEventListener('DOMContentLoaded', () => document.body.appendChild(capPanel));
  if (document.body) document.body.appendChild(capPanel);
}
import { clamp, lerp, smoothstep } from '@/core/rng';
import { dryness } from '@/render/season';

/** One wind for everything: clouds, smoke, and the fire's preferred direction. */
const WIND = { x: 5.2, z: 1.6 };

const stage = document.getElementById('stage')!;

// before any material can compile: distance must desaturate and lift toward
// the sky, or the far massifs read as near geometry scaled down
installAerialPerspective();

const renderer = new THREE.WebGLRenderer({ antialias: true, logarithmicDepthBuffer: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.55;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
stage.appendChild(renderer.domElement);
/**
 * The post pass — bloom, light shafts, the grade and the vignette, done to the
 * finished frame (`render/post.ts`). It renders a few quads of its own after
 * the scene, so the draw counters are reset by hand at the top of the frame
 * rather than by three's per-render reset, or the stats would show the pass's
 * one draw instead of the scene's.
 */
const post = createPost();
renderer.info.autoReset = false;

const scene = new THREE.Scene();
const sceneFog = new THREE.Fog(0x9fb0bd, 400, 12000);
scene.fog = sceneFog;
const camera = new THREE.PerspectiveCamera(46, 1, 0.1, 400000);
// the camera is the presence's own eye: yaw about Y, then pitch about X
camera.rotation.order = 'YXZ';

const sky = createSky();
scene.add(sky);
// the sun is two lights at one angle: the caster carries slightly under half
// the energy, the fill carries the rest shadow-free — so a cloud's shadow is
// a soft grey dimming of the land, never a black hole punched in it
const sun = new THREE.DirectionalLight(0xfff0d6, 1.05);
// shadows exist for the cloud dapple: only clouds cast, the land receives
sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048);
/**
 * The shadow volume follows the camera instead of spanning the region.
 *
 * Fixed at ±12.5 km it covered the whole island on a 2048 map — 12 metres
 * per texel, which is coarser than a shelter is wide, so nothing smaller
 * than a weather system could ever cast anything. Tracking the camera puts
 * it under a metre per texel at valley scale, which is what lets close
 * ground read as lit rather than pasted together, while still opening up
 * for the cloud dapple when you pull out over the island.
 */
const shadowExtent = (camR: number) => clamp(camR * 2.6, SHADOW_MIN_EXTENT, 12500);
/**
 * The smallest half-extent of the shadow box, in metres. 700 was chosen when
 * the box was centred under the eye; with the box slid ahead of the eye
 * (frame loop) 380 covers the same ground in front and puts a 2048 map at
 * 0.37 m per texel — fine enough for a log or a stone to leave a shadow.
 */
const SHADOW_MIN_EXTENT = 380;
const _sr = new THREE.Vector3(), _su = new THREE.Vector3(), _st = new THREE.Vector3();
sun.shadow.camera.left = sun.shadow.camera.bottom = -12500;
sun.shadow.camera.right = sun.shadow.camera.top = 12500;
sun.shadow.camera.near = 2000;
sun.shadow.camera.far = 60000;
/*
 * Bias, and why it had to change the day anything but a cloud cast.
 *
 * `bias` is in NORMALISED depth, so what it costs in metres is the shadow
 * camera's whole near→far range times the number. At the fixed 2 km→60 km this
 * light used to run, -4e-4 was **twenty-three metres** of offset — invisible on
 * a cloud shadow a kilometre wide and soft at the edges, and utterly fatal to a
 * tree's, which it pushes clean off the tree. That is why flagging casters
 * changed nothing at first: the map filled up and every shadow was shoved out
 * from under the thing that made it.
 *
 * So the depth range now tracks the box (see the frame loop) instead of
 * spanning the island, and the offset moved to `normalBias`, which is in WORLD
 * metres and therefore means the same thing whatever the range is doing.
 */
sun.shadow.bias = -6e-5;
sun.shadow.normalBias = 0.55;
sun.shadow.radius = 4;             // soft penumbra, still kind to the cloud dapple
scene.add(sun, sun.target);
const sunFill = new THREE.DirectionalLight(0xfff0d6, 1.05);
sunFill.castShadow = false;
sunFill.target = sun.target;       // one target: the pair can never diverge
scene.add(sunFill);
/** How far the sun stands off, in metres — moves with the shadow box. */
let sunDistance = 28000;
/**
 * How much of the sun's energy a shadow can take away.
 *
 * 0.45 was right for the world this rig was built in, where **only clouds
 * cast** — it made a cloud shadow a soft grey dimming of the land rather than a
 * black hole punched in it, and the shadow-free `sunFill` carried the rest.
 *
 * The day trees, rocks, roofs and people began casting, it became the reason
 * you could not see any of it. Measured on a clear morning: sun 0.52, fill
 * 0.63, hemisphere 1.00 — so a full shadow removed **24% of the light**, and
 * the fill is a second directional light from the SAME direction that by
 * construction fills every shadow back in. Rendering one frame by hand with
 * the fill and hemisphere suppressed showed the shadows had been there and
 * correct the whole time; nothing was wrong with the map, the bias or the
 * casters. It was the balance.
 *
 * At 0.80 a shadow is a real absence and a cloud's is still soft, because a
 * cloud's own alphaTest silhouette and the shadow radius do that work.
 *
 * (2026-08-18: 0.80 became `LIGHT.fill = 0.08` — 0.92 of the sun casts. The
 * argument above is the same argument taken one step further: with the near
 * detail and the herds about to cast too, a fifth of the sun filling every
 * shadow back in was the difference between a tree with a shadow and a tree
 * with a smudge. Read on.)
 */
/**
 * THE LIGHT RIG'S DIALS (2026-08-18) — the numbers the frame loop below feeds
 * the sun, the fill and the hemisphere, gathered in one place and exposed as
 * `__terra.light` so a balance is judged by eye at the capture stations (dial
 * → look), then written down here. Every one has a comment at its reader.
 *
 * What was measured, at `?cap=close&tod=0.70` (an 18.6° sun over an oak
 * meadow), before this rig: near-band luminance sd 0.045, whole frame 0.069,
 * far band DARKER than the near (0.143 against 0.206). That is a hillside
 * with a warm cast and no faces, and the ratio was why — see the reader of
 * `hemiLow`.
 */
const LIGHT = {
  /** Multiplier on the sun's whole energy curve. 1 = the 2026-08-17 curve. */
  sun: 1.0,
  /** The shadow-free share of the sun, from the same direction. Was `SUN_SHADOW_SHARE`'s 0.20. */
  fill: 0.08,
  /** Multiplier on the clear-sky hemisphere by day (0.57 ≈ the old 0.45 → 0.26 at noon). */
  hemi: 0.57,
  /** How much of the hemisphere goes away as the sun drops (0 = none, the old rig went UP by 0.7). */
  hemiLow: 0.42,
  /** The sun's colour at the horizon (G and B; R stays 1). Was 0.70 / 0.42. */
  sunLowG: 0.78, sunLowB: 0.52,
  /** The sky half of the hemisphere by day, and where it goes at a low sun. */
  skyR: 0.58, skyG: 0.67, skyB: 0.80,
  skyLowR: 0.62, skyLowG: 0.64, skyLowB: 0.72,
  /** The ground half of the hemisphere: warm earth bounce onto undersides. Was 0x4a4633. */
  groundR: 0.34, groundG: 0.29, groundB: 0.20,
  /** Multiplier on the presence's glow (below); 0 switches it off for an A/B. */
  glow: 1.0,
};
const hemi = new THREE.HemisphereLight(0x9fb6cc, 0x4a4633, 1.0);
scene.add(hemi);
/**
 * THE PRESENCE'S GLOW (2026-08-18, Griffin's direction) — the player's own
 * light. One point light carried AT the eye — the camera is where the player
 * is, and the light comes from the player — warm ivory against the cool of
 * the night, so after dark the ground near the god can be seen without the
 * moon being turned up; close in it is a pool of light around what is being
 * looked at — the intimacy the brief names — and out over the country it is
 * gone, because a god's wide attention is not a lamp. Its colour is the divine palette
 * (`DIVINE_GLOW`): warm ivory, subtly luminous, unnaturally precise — and it
 * is the visual the Aura should be built from (`render/anchors.ts` draws an
 * anchor today; an aura's light is this light, standing where the god left it
 * rather than where the god is).
 *
 * The numbers. Three's lights are physical: a point light of intensity I (cd)
 * at distance d puts I/d² of irradiance on a facing surface. I = E·r² keeps
 * the pool's brightness at what is looked at CONSTANT as the wheel moves in,
 * out to GLOW.reach metres of orbit — what changes is the pool's size, which
 * is what "closer is more intimate" means in light — and past that reach the
 * lantern simply does not carry. E is chosen so the pool reads about 0.35 in
 * display luminance on a 0.15-albedo ground at night (E ≈ 1.4), a fifth of
 * that by day (a warmth under the sun, a real pool in a tree's shade), and it
 * fades to nothing between 120 and 320 m of orbit radius. Law 9 kept: this
 * writes nothing; it is drawing.
 */
const DIVINE_GLOW = 0xfbeedb;
/**
 * SOFTENED (2026-08-19, Griffin: "a more mystical vibe, not a harsh flashlight
 * feel"). Two things made it a flashlight. Decay 2 — the physical inverse
 * square — puts four times the light on ground half as far from the eye as
 * the target is, so whatever the eye hung over was a hot disc with the
 * target at its dim edge; and the pool's cutoff at 2.2× the reach gave it a
 * rim. The light now falls as 1/d (decay 1): the same irradiance at the
 * target, 1.7× rather than 4× at half the distance, no hot centre to read as
 * a beam; it carries to three reaches before the cutoff's own soft roll-off
 * begins, so the pool has no edge; it is a fifth dimmer at night, because a
 * presence is felt before it is seen; and the ivory is a touch less amber,
 * toward the pale of moonlight rather than a lamp's. The aura it is the
 * basis for inherits all of this.
 */
const presenceGlow = new THREE.PointLight(DIVINE_GLOW, 0, 0, 1);
presenceGlow.castShadow = false;
scene.add(presenceGlow);
/**
 * The pool's irradiance at what is being looked at, by night and by day; how
 * far out (orbit metres) that promise holds; and where the glow fades entirely.
 */
const GLOW = { night: 1.1, day: 0.22, reach: 60, fadeFrom: 120, fadeTo: 320 };

const region = new Region(ISLAND_WORLD);

/**
 * The island's remarkable places (Design III §12–§15) — derived from the
 * region, once, and never stored. `tools/landmarks.ts`: 403 places in ~35 ms,
 * of which nineteen are world-defining. They are baseline, not history: a
 * reload finds the same Watch on the same summit because the seed says so, and
 * finding one writes nothing anywhere (law 9).
 */
const landmarks = findLandmarks(region);

// one geometry per plant kind and one material, shared by every chunk's
// vegetation: broad crowns, conifer spires, shrub mounds, grass tufts. Vertex
// colour (white foliage, brown wood) multiplies with each instance's own
// colour, so trunks stay woody whatever the foliage tint. The kit owns the
// wind sway too; the frame loop only advances its clock.
const veg = createVegetationKit();
// world time, and the whole living world riding on top of the baseline:
// fire, environmental memory, wildlife, bands. Freshly built chunks pass
// through the state stamps on the main thread; workers stay pure.
const clock = new WorldClock();
// `let`: loading a save replaces the whole living world in place
let sim = new Simulation(region, WIND);
/**
 * …and everything this client holds ABOUT that world, in one place that knows
 * it has to be emptied when the world changes (`ui/session.ts`).
 *
 * `sim` itself stays a plain `let` because it is read on nearly every line of
 * this file and `session.world` at each of them would be noise. The session
 * owns the slots that were being forgotten: the Chronicle feed and its cursor,
 * the god's own record of its own acts, the two physical holds, and the
 * catch-up run that closes over a `Simulation` by value.
 */
const session = new WorldSession(sim);

/**
 * The capture rigs: a fixed burn and a fixed blessing, written straight into
 * the stores so a before/after pair differs in the RENDERING and in nothing
 * else. Dev-only, and behind an explicit query — see ui/capture.ts.
 *
 * It has to run AFTER `adoptWorld`, not at construction: the shipped game
 * always adopts `prehistory.json`, and adoption replaces `sim` wholesale with
 * one built by `fromSnapshot` — so a rig written at boot is thrown away with
 * the world it was written into. That is the same lifetime bug that left the
 * Godhood Web inert (see `adoptWorld`), met from the other side.
 */
function applyCaptureRigs(): void {
  if (!capStation || !import.meta.env.DEV) return;
  const gAt = (x: number, z: number) => surfaceHeight(region, x, z, 1);
  let touched = false;
  if (burnRequested(window.location.search)) {
    for (const c of burnRig(capStation, gAt)) sim.overlay.addScar(c.x, c.z, c.sev, CAPTURE_T);
    touched = true;
  }
  if (blessRequested(window.location.search)) {
    for (const c of blessRig(capStation, gAt)) {
      sim.env.stamp(c.x, c.z, 16, { biomass: c.d }, CAPTURE_T);
    }
    touched = true;
  }
  // the ground these rigs sit on was meshed before they existed
  if (touched) invalidateKeys([...sim.overlay.scarChunks(), ...sim.env.chunks()]);
}

// state stamps in order: env first, mortal wear over it, burn scars over all —
// a trampled camp stays trampled through a strengthen, and a scar chars over both.
// Promotion goes last, and is the one layer that is not a condition: the
// three beneath it are how wet, how walked, how burnt, and they all fade in
// weeks, while a thing history has made important stays important through
// them. A sacred tree in a burn should read as a sacred tree in a burn.
// and one thread runs back *down* the chain: `sparing` is the yes/no the
// culling layers ask before they take a plant, so the tree a god has
// been keeping is the one the camp builds around and the fire goes round.
// Undefined where nothing was ever promoted, which is the usual case.
// The sere goes outermost and may only OMIT: a stand growing back from a
// burn is the baseline stand minus everything too old to have regrown yet,
// whatever the four layers beneath it decided — and the spared tree stands
// through even the bare year.
/** The strain phenotype source over WHATEVER world is currently adopted. */
let strainSrcField: unknown = null;
let strainSrcFn: PlantPhenotypeSource | null = null;
const liveStrainSource = (): PlantPhenotypeSource => {
  if (strainSrcField !== sim.strains || !strainSrcFn) {
    strainSrcField = sim.strains;
    strainSrcFn = strainPlantSource(region, sim.strains, () => clock.t);
  }
  return strainSrcFn;
};

/**
 * THE AURA — the god's area of focus, drawn, and the radius the fine-detail
 * ring is spent inside (`render/aura.ts`).
 *
 * One circle for two things that were always the same circle: where a god's
 * attention is, and where the world is drawn finely. `?aura=` overrides the
 * radius for testing; the settings panel's own LOD multiplier still rides on
 * top, so a machine that cannot afford the ring can still pull it in.
 */
const aura = createAura(
  Math.max(96, Math.min(2400, Number(new URLSearchParams(location.search).get('aura'))
    || DEFAULT_AURA)));
scene.add(aura.group);

const chunks = new ChunkManager(region, veg, {
  transform: (d) => {
    const spare = sparing(sim.things, clock.t);
    return applySere(
      applyThings(
        applyOverlay(
          applyWear(
            // Earth goes innermost: it moves the ground itself, and every
            // layer above reads positions and tints what is already there —
            // none of them should have to know the land shifted under them.
            applyEnv(applyEarth(d, sim.earth), sim.env, clock.t, spare),
            { at: (wx, wz, now) => sim.bands.pressureAt(wx, wz, now) },
            clock.t,
            sim.bands.pressureSize(),
            spare,
          ),
          sim.overlay, clock.t, spare),
        sim.things, clock.t),
      sim.sere, clock.t, spare);
  },
  // the plants' phenotype: the dev lab's override when `?pheno=` named one,
  // else THE STRAINS (2026-08-19, the wake) — the ground's own varieties,
  // through the same composition the ecology multiplies. `liveStrainSource`
  // re-derives its closure whenever adoption swaps the world out from under
  // it, because `sim` is a `let` and a captured field would be the adopted
  // world's predecessor — the exact lifetime bug `applyCaptureRigs` documents.
  phenotype: phenoLab?.plant ?? ((kind, x, z) => liveStrainSource()(kind, x, z)),
});
scene.add(chunks.root);

// the whole region at coarse detail, out to the horizon; a moving hole is cut
// under the chunk ring so it never covers the chunks' real relief
// …and it reads the world's sparse history now, so a god-made hill, a burn
// scar and a watershed-scale blessing are all visible past the streamed ring
// instead of stopping at it (ROADMAP §22).
const horizon = buildHorizon(region, 320, {
  get earth() { return sim.earth; },
  get env() { return sim.env; },
  get overlay() { return sim.overlay; },
});
scene.add(horizon.mesh);

// and the forest that stands on it. Chunks stop growing plants at LOD 2, which
// left everything past the streamed ring bald — coarse green ground with rocks
// on it. This is the same idea as the horizon: one region-scale layer, two draw
// calls, holed per cell under the chunks that grow their own trees.
const farCanopy = buildFarCanopy(region, veg);
scene.add(farCanopy.group);

// an opaque abyss below the transparent surface: beyond the region's seabed
// there must still be something under the water, or it blends with the void
const abyss = new THREE.Mesh(
  new THREE.PlaneGeometry(region.extentMetres * 8, region.extentMetres * 8),
  new THREE.MeshStandardMaterial({ color: 0x0d2436, roughness: 1, metalness: 0 }),
);
abyss.rotation.x = -Math.PI / 2;
abyss.position.y = -14;
scene.add(abyss);

// depth-graded and shore-aware: translucent shallows, foam at the waterline
const sea = createSea(region.extentMetres * 8, {
  at: (wx, wz) => region.heightAt(wx, wz),
  worldExtent: region.extentMetres,
});
scene.add(sea.mesh);

// the rivers the region always knew about, finally visible as water — and
// running: the ripple uniform advances in the frame loop
const rivers = buildRivers(region);
scene.add(rivers.mesh);

// and where a river falls, it turns white. The island's 161 falls were all
// terrain-coloured until now: big enough (p50 118 m of drop, 42 px at 3 km)
// and the exact value of the hillside behind them.
const falls = buildFalls(region, landmarks);
scene.add(falls.group);

// geology: outcrops and scree earned by slope and elevation, so the mountains
// read as stone rather than as smooth green dough
const rocks = buildRocks(region);
scene.add(rocks.group);
// The rare things — one per 1.6 km cell, 207 on this island. Built once from
// the Region and never rebuilt: they are baseline, not history, so the same
// argument that makes `landmarks.ts` free makes these free. The pick chain
// already calls `relicNear`, and until this line existed it named 207 things
// nothing drew — a pointer that can find what the eye cannot is worse than no
// pointer, because the player learns to distrust it.
const relics = createRelics(region);
scene.add(relics.group);

const clouds = createClouds();
scene.add(clouds.group);
const birds = createBirds();
scene.add(birds.mesh);
const fireFx = createFireFx();
scene.add(fireFx.group);
const verbFx = createVerbFx();
scene.add(verbFx.group);
const reticle = createReticle();
scene.add(reticle.group);
// the thing in the hand while Grasp holds it — `render/held.ts`
const heldThing = createHeldThing();
scene.add(heldThing.group);
// the sub-metre litter of a real landscape — pebbles, twigs, deadfall, small
// growth — in a disc that travels with the focus. Chunk vegetation stops at
// ~0.4 m tufts, so without this the ground within 200 m has nothing on it.
const nearDetail = createNearDetail();
scene.add(nearDetail.group);
// the state of the air, when the front says so
const rainFx = createRain();
scene.add(rainFx.group);
// …and the rain that is falling on somewhere else. `tools/season.ts`: rain is
// on the presence 12.9% of a real hour and somewhere IN VIEW 80.3% of it, so
// four fifths of the time the sky was visibly doing something and the frame
// showed none of it.
const curtains = createCurtains();
scene.add(curtains.group);
const curtainEye = new THREE.Vector3();
const sound = createSound();
const mortals = createMortals();
// the glide: displayed party positions run continuously a couple of
// world-minutes behind the sim's hourly strides — `ui/glide.ts` is the law
const partyGlide = new Map<string, GlideState>();
let glidePrevT = 0;
scene.add(mortals.group);
const structuresFx = createStructures();
scene.add(structuresFx.group);
// the cleared ground under a living camp, carried past the streamed ring so
// that "where are the people?" has an answer from the air
const habitationFx = createHabitation();
scene.add(habitationFx.group);
const pathsFx = createPaths();
scene.add(pathsFx.group);
// the ring that says *this one* — what the pointer is over, and what the god
// has chosen. Draws nothing into the world; see render/selection.ts.
const selectionFx = createSelection();
scene.add(selectionFx.group);
const wildlifeFx = createWildlife();
scene.add(wildlifeFx.group);
// the god's stone, and whatever the people have built up around it
const anchorFx = createAnchors();
scene.add(anchorFx.group);

// The one answer to "where is the ground here" that the presence, the FX and
// everything the renderer stands on the land all read. It adds whatever a god
// has moved (`world/earth.ts`) so the camera does not float over a raised hill
// or sink into a dug pit. Note the simulation's own coarse height is NOT
// moved — bands still walk the baseline — which is written up in earth.ts.
const groundAt = (x: number, z: number) =>
  surfaceHeight(region, x, z, 1) + sim.earth.at(x, z);
// one reader, reused by every litter-cell resolve — `sim` is read at call
// time, so adopting a world swaps the pressure field underneath it
const wearReader = { at: (wx: number, wz: number, at: number) => sim.bands.pressureAt(wx, wz, at) };

/**
 * What the pointer may resolve to.
 *
 * Deliberately not the whole scene: the clouds, the smoke, the verb effects,
 * the reticle and the selection rings themselves are all things the god is
 * *looking through*, and a ring you cannot point past is a ring that has
 * captured the mouse.
 *
 * And deliberately not the terrain. Raycasting `chunks.root` costs **18–20 ms
 * a frame** — measured, in the running game, at every altitude — because a
 * shallow ray crosses dozens of chunk bounding spheres and each one then does
 * eight thousand triangle tests. That is the entire frame budget spent on
 * finding out what is under the mouse. The ground is a *function*, so it is
 * marched instead (see `groundAlong`), and the ray only ever intersects the
 * things that actually stand on it.
 *
 * And deliberately nothing that is drawn as *one instanced mesh spanning the
 * island* — the figures, the structures, the herds, the stones. Their
 * bounding sphere is the island, so every ray tests every instance in them,
 * which measured **eight milliseconds** for the rocks alone. Those are
 * resolved by proximity to the ground point instead, from lists the game
 * already keeps. What is left to raycast is the vegetation, which is
 * per-chunk and therefore has tight bounds — and which is also the one thing
 * whose *crown* you point at rather than its feet. Its list is rebuilt from
 * the mounted set whenever the streamer changes it.
 */
/**
 * The seconds the wildlife renderer last drew its bodies at.
 *
 * `render/wildlife.ts` drifts animals on `now / 1000` — **real seconds since
 * the page loaded**, not world seconds — because a grazing walk is a thing the
 * eye sees rather than a thing the world remembers. The pointer resolves
 * individual animals through the renderer's own `bodyAt`, so it has to be
 * handed the same number or it computes where the herd would be on a clock
 * nothing draws: at year 100, `clock.t` is 3.15e9 against a frame timestamp of
 * a few hundred, and every animal the pick named stood somewhere the player
 * could not see it.
 *
 * That is not a bug a probe catches by agreeing with the code — the first
 * version of `tools/animals.ts` and this branch made the identical mistake and
 * reported 63 of 63 bodies resolving perfectly. It shows up only against what
 * is actually ON SCREEN.
 */
let drawnAtSeconds = 0;

const pickable: THREE.Object3D[] = [];
let pickableFor = -1;
const refreshPickable = () => {
  if (chunks.stats.live === pickableFor) return;
  pickableFor = chunks.stats.live;
  pickable.length = 0;
  chunks.root.traverse((o) => { if (o.name.startsWith('veg-')) pickable.push(o); });
};

/**
 * Where a ray meets the ground, by marching the heightfield rather than
 * intersecting it. The march itself is `world/aim.ts` — pure, so `tools/` and
 * `test/` exercise the same code the game does rather than a copy of it.
 *
 * It resolves against the same `surfaceHeight` every other part of the game
 * places things with, so the point it returns agrees with where a structure,
 * a figure or a verb effect would stand — which the triangle hit never did.
 *
 * **It used to only *claim* that.** The march read the coarse 260 m bicubic
 * alone and lifted the point it found onto the true surface at the end,
 * on the reasoning that the skipped detail was "a couple of metres of
 * roughness" that "nothing downstream can tell". Measured (`tools/pick.ts`),
 * the two surfaces disagree by a median 7.4 m and lifting cannot move a
 * point sideways — so a 25° ray put the reference a median 15.3 m from the
 * thing being aimed at, against a 7.1 m tolerance, and **only 34% of aims
 * taken squarely at a subject came near enough to offer it at all.** It now
 * brackets on the cheap field and resolves on the true one: 34% → 98.4%, for
 * 35 µs against the 2.5 ms that made the shortcut attractive.
 */
const SKY = 2800;   // above every peak on the island, with room to spare
const groundAlong = (o: THREE.Vector3, d: THREE.Vector3): THREE.Vector3 | null => {
  const p = marchToGround(o, d, (x, z) => region.heightAt(x, z), groundAt, SKY);
  return p ? new THREE.Vector3(p.x, p.y, p.z) : null;
};

const presence = new DeityPresence(stage);

/* ── the other body: a mortal's feet on the ground ─────────────────────── */

/**
 * Where a person is standing NOW, through the truth the eye already follows
 * them by (`trackChosen`): their own drawn figure while the renderer has one
 * — the same `positionOf` the follow ring rides — and their party's ground,
 * then their band's, when it does not. The one person lookup in this file;
 * both the follow and INHABIT resolve through it, so they cannot disagree
 * about where somebody is. Null once the life has ended.
 */
const locatePerson = (id: string): { x: number; y: number; z: number } | null => {
  const band = sim.bands.bands().find((b) => b.people.some((q) => q.id === id));
  const person = band?.people.find((q) => q.id === id);
  if (!band || !person) return null;
  const at = mortals.positionOf(id);
  if (at) return at;
  const party = band.parties.find((q) => q.id === person.partyId);
  const x = party?.x ?? band.x, z = party?.z ?? band.z;
  return { x, y: groundAt(x, z), z };
};
/**
 * THE MORTAL BODY, and the one truth about which body the player is in
 * (`ui/inhabit.ts`, `ui/mortalPresence.ts`, 2026-08-19). `modes.mode` is
 * read wherever the two bodies differ; `enterMortal`/`ascend` below are the
 * only two places it changes, and each hands input ownership across — exactly
 * one of `presence.active` / `mortal.active` is true at any moment.
 *
 * CLIENT-SIDE EXPLORATION ONLY in this pass: the mortal is a viewpoint seated
 * on a real person's ground, and nothing it does reaches the simulation. The
 * person keeps their own AI life underneath it. FUTURE: authoritative
 * inhabitation must replace that person's movement rather than run a local
 * camera shell — `HANDOFF.md`, "the mortal skeleton", lists what remains.
 */
const mortal = new MortalPresence(stage);
const modes = new PlayerModes(presence, mortal, locatePerson);
/**
 * A person as the pick would offer them, by id — the dev door's way to choose
 * a life without a pointer (`__terra.inhabit(id)`), built in the pick's own
 * shape so that what the harness inhabits is what a click would have chosen.
 */
const personTarget = (id: string): Target | null => {
  const at = locatePerson(id);
  const band = sim.bands.bands().find((b) => b.people.some((q) => q.id === id));
  const person = band?.people.find((q) => q.id === id);
  if (!at || !band || !person) return null;
  const people = sim.cultures.get(band.cultureId)?.name;
  return { kind: 'person', id, x: at.x, y: at.y, z: at.z, size: 1.6,
           ...namePerson(person.name, people) };
};

/**
 * The Godhood, loaded before anything reads it.
 *
 * It lived 560 lines further down, beside the panel that spends it, which was
 * fine while nothing above that line asked it anything. `syncReach` asks it
 * two questions now and runs at boot (`beginAsPlayer`, below), so leaving it
 * there is a temporal-dead-zone `ReferenceError` on the first frame. It is
 * storage-only — `loadWeb()` reads localStorage and nothing else — so it can
 * be built as early as we like. The ORACLE still attaches later, because that
 * one needs `sim`.
 */
const godhoodWeb = loadWeb();

/**
 * Reach is the physics of being this god. It caps how high the presence may
 * rise, how fast it crosses the island — and, separately, the widest Focus
 * the wheel may choose. Flying up to cross a mountain no longer widens a
 * cast: scale is chosen, never inferred from altitude. The cap on that
 * choice is focusFor(maxAltitude) — the exact focus the old orbit ceiling
 * permitted — so progression stands where it always did. A spirit tending a
 * hillside still cannot mean a valley; now it also cannot outfly one.
 */
/**
 * THE HOSTED WORLD, WHEN THERE IS ONE — `?server=…&token=…`, and null for
 * every ordinary boot.
 *
 * Null is the default and stays the default. Everything below reads it the
 * same way: when it is null nothing changes at all, and `test/` never sees it
 * set. `SERVER.md` is the document; `src/ui/attach.ts` is the module.
 */
let attached: AttachedWorld | null = null;

/** Real ms between presence updates to the server. Five a second is plenty. */
const LOOK_EVERY_MS = 200;
let lastLook = -1e9;

/**
 * WHICH GOD THESE HANDS ARE. `player` alone, until a server says otherwise.
 *
 * It used to be `sim.pantheon.player` written out at every call site through
 * `god()`, which was right while there could only ever be one god at these
 * hands. On a hosted world the server assigns the deity — a friend is
 * `friend-3f2a…`, not `player` — and every read of the god's power, level,
 * reach and spent stones has to follow. One variable, and the fallback keeps
 * a local world behaving exactly as it did.
 */
let myDeityId = 'player';

// the player is one deity in the world's pantheon, not a special case: the
// simulation owns the roll, so a save restores who was acting as well as what
// they did, and a rival god will act through the very same entrypoint
const god = () => sim.pantheon.get(myDeityId) ?? sim.pantheon.player;
/**
 * What the player is when they arrive: what their spending has earned. Until
 * 2026-08-18 this was a fixed 0.35 — a local god with a 1.5 km ceiling from
 * the first second. Griffin's direction that day: a level-one god stands low
 * (`LADDER`'s spirit rung, 180 m) and earns the sky by putting impulse into
 * the world (`GodState.spend`, `levelOf`, `reachOf`). A save carries `spent`
 * and `reach`, and neither is lowered by arriving.
 */
const OPENING_REACH = 0;
/** The level last announced, so a cast that turns one is told once. */
let levelSeen = 1;
let focusRadius = focusFor(340).radius;  // opens at grove, like the old camera
let focusCap = focusFor(god().maxAltitude).radius;
/** Reach moved — re-hang the sky, the speed and the widest focus off it. */
const syncReach = () => {
  /**
   * HOW HIGH THIS GOD MAY RISE — the reach ladder, and what the Web adds to it.
   *
   * `presence.comfortableAltitude` is a **cap**, sold by *The High Air* (Reach
   * / mastery), and it is the first `ceiling()` any code in this build has ever
   * read: three of the Web's four effect kinds had zero call sites outside
   * `godhood/`, so the whole non-percentage half of progression was
   * unobservable whatever the player spent.
   *
   * `Math.max`, never assignment: a cap **below** the god's earned ceiling must
   * never lower it. The ladder is one axis and the Web is another on top of it.
   */
  const alt = Math.max(
    // …and `presence.maxAltitude` is the multiplier on the ladder itself,
    // sold by the Epic *The High God*. Two variables and two shapes on
    // purpose: a MULTIPLIER stretches what the reach ladder already gave you,
    // and a CAP is a floor under it that does not care what you have earned.
    // A god that has both takes whichever is kinder, which is what `Math.max`
    // has always been for here.
    god().maxAltitude * godhoodWeb.modifier('presence.maxAltitude'),
    godhoodWeb.ceiling('presence.comfortableAltitude') ?? 0);
  presence.maxClearance = alt;
  focusCap = focusFor(alt).radius;
  focusRadius = Math.min(focusRadius, focusCap);
  // `presence.terrainConstraint` — Unbound Presence's rule — turned off the
  // flight's ground lookahead. An orbit eye has no lookahead to turn off: the
  // target skims the land and the eye is lifted clear of it, and the rule is
  // `partial` again in `bindings.ts` until an orbit-era meaning is decided.
  // …and how fast, in the two shapes the Web sells it: flat speed, and what
  // the sky adds to it.
  presence.speed = godhoodWeb.modifier('presence.speed');
  presence.speedFromAltitude = godhoodWeb.modifier('presence.speed.fromAltitude');
};
/**
 * Step into whatever world we are now in, as the player.
 *
 * Reach belongs to the *player*, not to the world — but it rides in the
 * snapshot, because a save has to restore who was acting as well as what they
 * did. Prehistory is not a save: it is a world with no god in it, written by
 * a tool that never set a reach. Adopting it therefore handed the player the
 * world-builder's default of zero, and the shipped island opened on a spirit
 * with a **260 m ceiling and a 134 m cast** instead of the 859 m and the
 * valley a local god is supposed to have. Which is a quiet way of undoing the
 * entire far view: everything worth seeing from the air was above the ceiling.
 */
const beginAsPlayer = () => {
  god().reach = Math.max(OPENING_REACH, god().reach, reachOf(god().level));
  levelSeen = god().level;
  syncReach();
};
beginAsPlayer();
/**
 * When the wheel was last turned, in real ms.
 *
 * The Focus readout is transient now. It used to be a permanent row in the top
 * right, which is a number on screen at all times for a control the player
 * touches in bursts — and the ring the reticle draws in the world is the honest
 * version of the same fact (law 12). So the words appear while the scale is
 * being steered, or while a verb is armed, and otherwise the screen is world.
 */
let focusTouched = -1e9;
/**
 * THE WHEEL IS THE DISTANCE, AND THE DISTANCE IS THE SCALE OF ATTENTION.
 *
 * `focusRadius` is read off the eye's distance every frame through `focusFor`
 * — the band table the old orbit camera paid at each distance and the one the
 * wheel walked as a bare number while the mouse was captured. Close, a cast is
 * channelled into the thing under the cursor; zoom out and the same click
 * means the party, then the band, then the country. Reach caps the distance
 * (`maxClearance`), so a local god's widest attention is what its old ceiling
 * permitted, exactly as before.
 */
presence.onZoom = () => { focusTouched = performance.now(); };
const syncFocus = () => {
  focusRadius = Math.min(focusFor(presence.r).radius, focusCap);
};
// dev: '[' and ']' walk the arc until devotion exists to earn it
addEventListener('keydown', (e) => {
  if (e.key !== '[' && e.key !== ']') return;
  god().grow(e.key === ']' ? 0.08 : -0.08);
  syncReach();
  notice(`${god().stageInfo.title} · ${god().stageInfo.scope} · ` +
         `${nice(god().maxAltitude)} of sky`);
});
// start somewhere habitable rather than in the middle of the sea
const start = { x: 0, z: 0 };
{
  let best = { x: 0, z: 0, s: -1 };
  for (let y = 20; y < region.n - 20; y += 3) for (let x = 20; x < region.n - 20; x += 3) {
    const wx = (x - region.n / 2) * region.cell, wz = (y - region.n / 2) * region.cell;
    const site = region.siteAt(wx, wz);
    if (!site.land) continue;
    const s = site.rain * 0.6 + clamp(Math.log(1 + site.flow) / 9, 0, 1) * 0.4;
    if (s > best.s) best = { x: wx, z: wz, s };
  }
  start.x = best.x;
  start.z = best.z;
}

// stock the island with wildlife and found the first band near the start —
// the §57 scenario: one community of 25, several days of food, no structures.
// The band picks its own home from the habitat, and the presence opens
// standing off it exactly where the old orbit camera opened — a few hundred
// metres out, high enough to see the camp whole, gaze on the people. The
// island is 50 km and the story is 2 m tall; the whole island is a place you
// choose to fly out to.
{
  const home = sim.populate(clock.t, start);
  const hy = groundAt(home.x, home.z);
  presence.placeEye({ x: home.x - 200, y: hy + 139, z: home.z + 238 },
                    { x: home.x, y: hy + 2, z: home.z });
  syncFocus();
}

/** Put the eye over a band, standing off it the way the game opens. */
const openOver = (x: number, z: number) => {
  const gy = Math.max(groundAt(x, z), 0);
  presence.placeEye({ x: x - 200, y: gy + 139, z: z + 238 }, { x, y: gy + 2, z });
  syncFocus();
};


// THE FOUR VERBS, ON THE NUMBER KEYS — `DECISIONS.md`.
//
// This listener has been registered and EMPTY since ROADMAP §25 retired the
// speed buttons, under a comment about time control that had outlived the
// feature it described. The digits have been free ever since and nothing said
// so, which is the whole reason they were available for this.
//
// The number keys are the primary control and the bar is the accessible path,
// not the intended one. Arming is all that happens here: what a cast DOES is
// the click's business, so this stays one line per verb and cannot drift away
// from what the palette's own buttons do.
/**
 * A KEY IS A CAST NOW (2026-08-18, Griffin's direction). Tap `1`–`3` and the
 * verb is cast at what is under the hand for ONE stone of impulse; press and
 * HOLD it and the cast charges, a stone every `CHARGE_MS`, up to everything
 * the god has left — and lands when the key comes up. Law 30 as input:
 * intensity comes through tap, hold and deep exertion on one verb, never
 * through a second button. `4` still arms Grasp, which is the hand and not a
 * cast (`DECISIONS.md`); the bar's buttons still arm for the mouse, and a
 * click then spends one stone. Escape lets a charge go unspent.
 */
/** Milliseconds of hold per stone gathered — a full charge is about two seconds. */
const CHARGE_MS = 500;
let charge: { verb: CastVerb; since: number; stones: number } | null = null;
addEventListener('keydown', (e) => {
  // Never steal a browser or OS chord. `alt` is no longer one of ours — it was
  // the strengthen modifier until that whole path was deleted — but it stays
  // in the guard because alt+digit IS an OS chord on several platforms, and
  // that was always the better reason.
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  // and never while the player is typing into something
  const el = document.activeElement;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;

  // A MORTAL'S 1–4 light a placeholder slot and do nothing else (brief §24):
  // no arm, no charge, no cast, no impulse. Decided on the mode and nothing
  // incidental to it, before the divine key table is so much as consulted.
  if (modes.mode === 'mortal') {
    const slot = mortalSlotForKey(e.key);
    if (!slot) return;
    e.preventDefault();
    if (!e.repeat) pressMortalSlot(slot);
    return;
  }

  const v = VERB_KEYS[e.key];
  if (!v) return;
  e.preventDefault();
  if (e.repeat) return;              // the hold is timed by the frame, not by key repeat
  if (hud.armed !== v) hud.arm(v);   // arm, never toggle off: the key is a trigger now
  // Arming is not an act. Nothing is written, nothing is spent, and the world
  // does not know — law 9 covers choosing to look and this is the same shape:
  // a god deciding what they might do is not a god having done it.
  if (v === 'grasp') return;
  if (sim.pantheon.player.power + 1e-9 < IMPULSE_STONE) {
    notice('too spent — not a stone of impulse left');
    return;
  }
  charge = { verb: v, since: performance.now(), stones: 1 };
});
addEventListener('keyup', (e) => {
  if (modes.mode !== 'deity') return;   // a mortal's key coming up lands nothing
  const v = VERB_KEYS[e.key];
  if (!v || !charge || charge.verb !== v) return;
  const c = charge; charge = null;
  hud.disarm();
  // it lands on what is under the hand — the pick the reticle is already
  // showing — and nowhere if the hand is over the sky
  const t = hovered;
  if (!t) { notice('nothing under the hand'); return; }
  cast(c.verb, new THREE.Vector3(t.x, t.y, t.z), t, c.stones * IMPULSE_STONE);
  notice(c.stones === 1 ? `${c.verb} — one stone` : `${c.verb} — ${c.stones} stones`);
});
/** How many stones a hold has gathered so far, capped by what the god has. */
const chargeStones = (now: number): number => {
  if (!charge) return 0;
  // how much of itself a god may put into one act — the Web's
  // `act.impulseShare`, all of it at baseline (Griffin's rule: a hold charges
  // to everything that is left), and never under one stone
  const share = godhoodWeb.ceiling('act.impulseShare') ?? 1;
  const most = Math.max(1, Math.floor(sim.pantheon.player.power * share / IMPULSE_STONE + 1e-9));
  charge.stones = Math.min(most, 1 + Math.floor((now - charge.since) / CHARGE_MS));
  return charge.stones;
};

/** `1`–`4` → the armed verb. The single source is `hud.ts`'s own VERBS row. */
const VERB_KEYS: Readonly<Record<string, Verb>> = {
  '1': 'strengthen', '2': 'strike', '3': 'omen', '4': 'grasp',
};

// persistence: K saves the world's sparse history, L restores it in place.
// The baseline is never saved — the seed rebuilds it (spec §49).
const SAVE_KEY = 'unseen-save';

/**
 * When this client was last open, in wall-clock milliseconds — kept BESIDE the
 * save and deliberately never inside it.
 *
 * "When was a browser last pointed at this world" is not world history. A
 * snapshot that carried it would stop being reproducible: two loads of the
 * same file would advance to two different worlds, which is precisely the
 * property `test/simulation-save-roundtrip.ts` exists to defend. So it lives
 * in its own key, outside the `terra-` version gate, in the same spirit as the
 * render settings and the Web allocation.
 *
 * The server will own the real version of this — it is the one thing here that
 * a client genuinely cannot be trusted with, since a hand-edited stamp is a
 * request to fast-forward the world. Which is also why the cap below exists.
 */
const CLOCK_KEY = 'unseen-save-wallclock';

const doSave = () => {
  const ok = saveLocal(SAVE_KEY, sim.toSnapshot(clock.t));
  if (ok) { try { localStorage.setItem(CLOCK_KEY, String(Date.now())); } catch { /* full/blocked */ } }
  notice(ok ? 'the world remembered' : 'save failed');
};

/** Real seconds since this world was last saved, or 0 if that is unknowable. */
const realSecondsAway = (): number => {
  try {
    const raw = localStorage.getItem(CLOCK_KEY);
    if (!raw) return 0;
    // A clock that went backwards — a timezone change, an NTP correction, an
    // edited stamp — reads as zero rather than rewinding anything. `absenceSpan`
    // guards this too; doing it here as well means the number that reaches the
    // notice text is honest, not only the number that reaches the simulation.
    return Math.max(0, (Date.now() - Number(raw)) / 1000);
  } catch { return 0; }
};
/**
 * Take a whole world in place of this one. Every load goes through here.
 *
 * `continuity` marks the one case where the world being adopted is THE SAME
 * WORLD, one instant later: an attached client refetching after somebody's
 * intent landed. Everything the session clears on adoption is cleared because
 * ids from a discarded world mean nothing in the new one — the god's own
 * deeds, the Chronicle cursor, the feed, the chosen subject. On a resync every
 * one of those ids is still exactly as valid as it was a moment ago, and
 * clearing them would mean the feed emptied, a few hundred old events replayed
 * as though new, and whoever you were watching let go of — **every time any
 * god anywhere cast anything**. That is not a world being replaced; it is the
 * same world arriving again, and it is worth the one flag to say so.
 */
const adoptWorld = (snap: SimulationSnapshot,
                    opts: { continuity?: boolean } = {}): void => {
  // Chunks carrying the OLD history must rebuild too, or ghosts linger — and
  // that has to mean EVERY history layer the chunk transform reads, not two of
  // them. `main.ts`'s own transform stacks six: env, mortal wear, burn scars,
  // the sere, moved earth and promoted things. This list named the first and
  // the third, so adopting a world left the other four rendering the world
  // that was replaced: a burn's regrowth stage, a hill somebody dug, and the
  // ground around a sacred tree all belonged to the previous save until an
  // unrelated invalidation happened to sweep them.
  //
  // …and the sixth is mortal WEAR, which this list used to leave out under an
  // argument that was false in two places at once. It said pressure "has no
  // chunk enumerator — it is derived from band positions" and is "covered by
  // the fact that adopting a world moves every band, which dirties their
  // chunks anyway". Pressure is a sparse per-cell store on the same 32 m
  // lattice as the scars and always was; nothing MOVES during an adoption; and
  // `BandSystem.restore` calls `this.dirty.clear()`, emptying the very set the
  // argument leaned on. So the previous world's beaten camp floors stayed on
  // the ground. `MortalPressure.chunks` is the enumerator that was missing.
  const historyChunks = (): string[] => [
    ...sim.overlay.scarChunks(), ...sim.env.chunks(),
    ...sim.sere.chunks(), ...sim.earth.chunks(), ...sim.things.chunks(),
    ...sim.bands.pressureChunks(),
  ];
  const stale = historyChunks();
  const restored = Simulation.fromSnapshot(region, WIND, snap);
  sim = restored.sim;
  /**
   * …AND EVERYTHING THIS CLIENT WAS HOLDING ABOUT THE WORLD THAT JUST LEFT.
   *
   * Four separate slots were surviving this line, and they had nothing in
   * common except that nobody could see them all at once from here: the
   * Chronicle's cursor into a discarded event ring, the feed drawn out of it,
   * the god's own record of its own acts, the two physical holds, and a
   * catch-up run that closes over the old `Simulation` by value. Each was a
   * plausible-looking lie rather than a crash — a stranger's lightning shown
   * as your miracle, a hand still gripping an object that was never made.
   *
   * They live in one table now and this one call empties all of them, so the
   * next slot somebody adds is covered by construction rather than by being
   * remembered. `test/adoption.ts` is the gate: it fails if the table falls
   * behind the session, and it fails if a slot survives this line.
   */
  // …unless this is the same world arriving again, in which case what the
  // session holds still refers to things that exist. Copied out BEFORE the
  // adoption empties the slots, and put back after.
  const carried = opts.continuity
    ? { acts: [...session.acts], feed: [...session.feed], cursor: session.cursor,
        hold: session.hold, dig: session.dig }
    : null;
  session.adopt(sim);
  if (carried) {
    for (const id of carried.acts) session.acts.add(id);
    session.feed.push(...carried.feed);
    session.cursor = carried.cursor;
    session.hold = carried.hold;
    session.dig = carried.dig;
  }
  // …and the HUD's own second copy of the same state: it keeps a cursor and an
  // id → phrase memory that `main.ts` cannot reach, and the card reads the
  // second one to say what a chosen mortal carries.
  if (!carried) hud.forgetEvents();
  // A run abandoned mid-flight leaves its panel on screen — `pumpAbsence` is
  // the only thing that ever removes the class — and the dev door's Chronicle
  // skip belongs to the run that just stopped existing.
  absenceEl.classList.remove('show');
  devSkipChronicle = false;
  clock.t = restored.worldTime;
  // THE WEB HAS TO BE RE-ATTACHED, and until now it never was.
  //
  // `useWeb` was called exactly once, on the Simulation built at boot. Every
  // path that adopts a world — `doLoad`, and the prehistory adoption the
  // shipped game ALWAYS takes — replaced that object with one built by
  // `fromSnapshot`, which constructs with no mods, so `modOf` fell back to
  // `() => 1` for everything. So the game everybody actually plays has been
  // running with the whole Godhood Web inert, while the bar's price preview
  // went on quoting `godhoodWeb.modifier('act.cost')` at it — the exact
  // quote-versus-bill drift `simulation.ts`'s own docstring warns about.
  useWeb(sim);
  // the restored world brings its own gods: this reach may not be the one
  // that was flying a moment ago
  syncReach();
  lastPathsVersion = -1;
  // A chosen thing pointed into the world that just went away, and the reading
  // cached against it is now about somewhere that no longer exists. Both are
  // observation state rather than history, so both simply reset — because the
  // alternative is a card confidently narrating the previous world's hillside,
  // with the eye still circling where it used to be.
  // A `Target` is a plain descriptor — kind, id, position, name — with no
  // live reference into the simulation, so on a resync the thing being watched
  // is still the thing being watched. Losing it would make watching anything
  // impossible on a shared world, which is most of what a shared world is for.
  if (!carried) letGo();
  placeCache = null;
  // both worlds' ground: what the old history touched, and what the new one does
  invalidateKeys([...stale, ...historyChunks()]);
  // …and the far field, which is the sixth history layer and is not a chunk.
  // It rebuilds off a version tag compared against `farSeen`, and two worlds
  // can perfectly well carry the same tag — a fresh one always does — so the
  // silhouette would keep the shape of the world that was replaced until
  // something happened to move a version. Forcing it costs one full pass,
  // once, on a frame the player is already waiting through.
  farSeen = -1;
  farBox = null;      // …and not the discarded world's changed box
  farNextAt = 0;
  refreshFarField();
};

/* ── crossing an absence ───────────────────────────────────────────────── */

/**
 * The world carried on while the tab was shut, and this is where it is paid
 * for. `sim/persistence/catchup.ts` owns the correctness — that slicing the
 * span any way at all lands on the same world, and that quitting mid-catch-up
 * and finishing after a reload lands on it too. What lives here is the half
 * that must: the wall clock, the frame budget, and what the player is told.
 *
 * **The cap is a design ruling and it is the client's, not the world's.**
 * `DEFAULT_CAP_WORLD_SECONDS` is one world-year, which bites at 1.90 real days
 * away; past that the world advances less than really elapsed and never
 * catches back up. Two things make that the right call *for now* and the wrong
 * one later:
 *
 *  · For a single world on one machine it is harmless. The world is younger
 *    than the wall clock thinks and is internally consistent, which is all
 *    that can be observed.
 *  · For the shared world law 27 describes it is wrong outright — two players
 *    away for different lengths of time would return to different centuries,
 *    and ONE SHARED CLOCK is the whole point. The server resolves this by
 *    advancing the world whether or not anyone is watching, which is the
 *    architecture `ROADMAP.md` §7 already anticipates.
 *
 * So the cap stays, honestly flagged to the player when it bites, and it is a
 * stopgap with an expiry rather than a tuning constant. The alternative —
 * uncapped — is measured and unaffordable: a month away is 16 world-years and
 * ~4.8 real minutes of computing at the door.
 */
/**
 * The absence being paid for — world-bound, and it was the sharpest of the
 * four: `runAbsence(sim, …)` closes over `sim` BY VALUE, so a load while the
 * bar is on screen leaves a run advancing the world that was just discarded,
 * spending real frames on world-seconds nobody will ever see. It lives on the
 * session now (`ui/session.ts`) and adoption drops it.
 */

const absenceEl = document.getElementById('absence')!;

/** Start paying for an absence, if there is one worth paying for. */
const beginAbsence = (away: number): void => {
  const est = absenceSpan(away);
  // Under a world-day is not an absence, it is a coffee. Advancing it is
  // correct but there is nothing to say about it, and a progress bar for
  // 0.05 seconds is worse than none.
  if (est.worldSeconds < DAY) return;
  rememberSubjectForReturn();
  session.crossing = { run: runAbsence(sim, clock.t, clock.t + est.worldSeconds),
                       est, leftAt: clock.t };
};

/**
 * Advance the absence inside this frame's remaining budget. Returns true while
 * there is more to do, in which case the caller must NOT also advance the
 * world on the clock — one hand at a time.
 */
const pumpAbsence = (frameStart: number): boolean => {
  const a = session.crossing;
  if (!a) return false;
  /**
   * The budget, and the slice that has to fit inside it.
   *
   * `pump` checks the budget BEFORE each slice, so a slice larger than the
   * budget means exactly one slice per frame and a frame that overruns by the
   * difference. Measured in the browser on the shipped century: **one
   * world-day costs 38.2 ms** (13.9 real seconds per world-year, close to the
   * headless 18). Against a 16.7 ms frame that is not a budget at all — the
   * default day-sized slice was overshooting by 30 ms every frame and
   * wrecking the frame rate it was there to protect.
   *
   * `catchup.ts` proves slice size is free — every slicing lands on the same
   * world — so the honest answer is to make the slice small enough to steer
   * with. An hour is ~1.6 ms of average work, fine-grained enough that the
   * budget is the thing actually deciding, with one dearer slice per world-day
   * when the daily systems fall due on a boundary.
   *
   * And the budget is 30 ms, not a frame's worth. Measured: 32 world-days of
   * absence is 1.2 real seconds of *simulation*, and at an 11 ms budget it
   * took roughly thirty — because almost the whole frame was going on drawing
   * a world nobody is flying through. During an absence there is no input to
   * be responsive to, so ~25 fps is plenty for watching seasons cross, and the
   * catch-up should have the rest.
   */
  const deadline = frameStart + 30;
  const more = a.run.pump(() => performance.now() < deadline, 3600);
  clock.t = a.run.at;
  absenceEl.classList.add('show');
  // Days under a year, years over it. "0.00 of 0.09 world-years" is what a
  // month of absence reads as otherwise, which is a progress bar that appears
  // not to move.
  const total = a.est.worldYears, done01 = a.run.progress01;
  const said = total >= 1
    ? `${(done01 * total).toFixed(1)} of ${total.toFixed(1)} world-years`
    : `${Math.round(done01 * total * 365)} of ${Math.round(total * 365)} world-days`;
  absenceEl.innerHTML =
    `<h2>while you were away</h2>` +
    `<div class="bar"><span style="width:${(done01 * 100).toFixed(1)}%"></span></div>` +
    `<div class="of">${said}</div>`;
  if (more) return true;

  // Done: the world is where it would have been. Everything its history
  // touched has to rebuild, and the far field with it.
  absenceEl.classList.remove('show');
  const dirty = sim.drainDirtyChunks();
  invalidateKeys(dirty);
  noteFarChange(dirty);
  refreshFarField();
  placeCache = null;
  // Park the Chronicle cursor past everything the absence emitted. Without
  // this a long advance turns the 2,000-record ring over dozens of times,
  // `noteEvents` loses its cursor, falls back to the start of the ring and
  // dumps a few hundred herd movements into the feed as if they had all just
  // happened. Skipping them is also the honest reading: you were not there.
  if (devSkipChronicle) {
    const all = sim.events.all();
    if (all.length) session.cursor = all[all.length - 1].id;
    devSkipChronicle = false;
  }
  const years = a.est.worldYears;
  const days = Math.round(years * 365);
  const span = years >= 1 ? `${years.toFixed(1)} years` : days === 1 ? 'a day' : `${days} days`;
  const changed = subjectChangedSince(a.leftAt);
  notice(a.est.capped
    // The honest sentence, not a reassuring one: the world moved further than
    // the client could follow, and the player should know that rather than
    // quietly believe the calendar.
    ? `${span} passed here — longer, out there, than this world could follow`
    : changed
      ? `${span} passed — ${changed}`
      : `${span} passed while you were away`, changed ? 9000 : 2600);
  session.crossing = null;
  return false;
};

/**
 * WHAT CHANGED ABOUT THE ONE YOU WERE WATCHING — `PLAYABLE.md` beat 9, *I
 * returned and found change*, for the subject the player actually cares
 * about. The Chronicle cursor is parked past an absence on purpose (you were
 * not there), so without this a player who left a person and came back a
 * season later had to read the whole card again and spot the difference.
 *
 * Client-side and read-only: the card's own lines before the crossing and
 * after it, and the ones that are new are said — *has grown bold*, *lost
 * Ila, who they worked beside*, *Roem died, and there was not enough*. Law 9
 * holds: the god's attention is a note in this client, written into nothing.
 */
let awayCard: { id: string; lines: string[] } | null = null;
const rememberSubjectForReturn = () => {
  awayCard = null;
  if (!chosen || chosen.kind !== 'person') return;
  const v = cardFor(chosen, presence);
  if (v) awayCard = { id: chosen.id, lines: [...v.now] };
};
const subjectChangedSince = (leftAt: number): string | null => {
  const parts: string[] = [];
  const was = awayCard;
  awayCard = null;
  if (was && chosen && chosen.kind === 'person' && chosen.id === was.id) {
    const v = cardFor(chosen, presence);
    const fresh = v ? v.now.filter((l) => !was.lines.includes(l))
      // what they are doing this minute and where they stand are not changes
      // worth a season's sentence; what they became, lost and heard are
      .filter((l) => /^(has |lost |child of .* who died|heard |.* died|not to be found|parent of|often out with)/.test(l))
      .slice(0, 3) : [];
    // "Ila: Ila died" is the card's own line twice; the name leads only when
    // the lines do not already carry it
    if (fresh.length) {
      parts.push(fresh[0].startsWith(chosen.name) ? fresh.join(' · ') : `${chosen.name}: ${fresh.join(' · ')}`);
    }
  }
  // …and what of the god's own deeds the world confirmed while it was
  // away — an omen somebody walked to, a fire that took, a tree that stopped
  // being ordinary. The god's own register (law 10: it knows its own
  // deeds), in `ui/lore.ts`'s words for what a strand left behind.
  const stayed = new Set<string>();
  for (const s of sim.chronicle.enduringOf(sim.pantheon.player.id)) {
    if (s.enduredAt === undefined || s.enduredAt < leftAt) continue;
    for (const l of s.left) stayed.add(LEFT[l] ?? l);
  }
  if (stayed.size) parts.push(`something of yours stayed: ${[...stayed].slice(0, 2).join('; ')}`);
  return parts.length ? parts.join(' — ') : null;
};

/**
 * THE DEVELOPER'S TIME DOOR — `__terra.advance(worldSeconds)`.
 *
 * `PLAYABLE.md` needs *leave something behind, return and find change* to be
 * testable, and at `CANON_SPEED = 192` a world-year is 1.90 real days. Every
 * horizon-C claim in this repository is otherwise unverifiable in the running
 * app, which is how a horizon-C claim comes to be believed without being seen.
 *
 * **This is a dev instrument and must not become player-facing UI**
 * (`DECISIONS.md`: the final time architecture is deliberately unresolved, and
 * a skip button would decide it by accident). It lives on `__terra`, which is
 * DEV-gated already.
 *
 * It is a wrapper and deliberately nothing more. `runAbsence` is already
 * proven slice-invariant by `test/persistence-absence.ts` — every slicing of a
 * span lands on the same world — and `pumpAbsence` already keeps `clock.t` in
 * lockstep with the run, drains and invalidates chunks, refreshes the far
 * field and drops the place cache. Re-implementing any of that would be a
 * second path through time, and the first one is the tested one.
 *
 * TWO TRAPS IT EXISTS TO AVOID, both of which the obvious console move hits:
 *
 *  · **`__terra.clock.t += N` DOES NOT ADVANCE THE WORLD.** The frame reads
 *    `tPrev = clock.t` *before* `clock.advance(dt)` and hands the pair to
 *    `sim.advance`, so moving the clock by hand moves the sun, the season and
 *    the year on the ribbon while the simulation skips the span entirely — and
 *    nothing can catch up afterwards, because `advance` holds no cursor and
 *    trusts `from` absolutely. The world is then quietly a different age from
 *    its own history. (`tools/consecration.ts` prints a paste-ready line that
 *    does exactly this, and can move the clock *backwards*.)
 *  · **The Chronicle floods.** The event ring is 2,000 records against roughly
 *    78,900 events a world-year, so a year of advance turns it over ~39 times.
 *    `noteEvents` then cannot find its cursor and falls back to `start = 0`,
 *    dumping the ring's worth of herd movement into the feed as though it had
 *    all just happened. The cursor is parked past the end of the ring on
 *    completion, so the player returns to a quiet Chronicle and sees only what
 *    happens next — which is also the honest reading: you were not there.
 */
const devAdvance = (worldSeconds: number): string => {
  if (session.crossing) return 'already advancing — wait for it to finish';
  const span = Math.max(0, worldSeconds);
  if (span < DAY) return 'less than a world-day; nothing to advance';
  rememberSubjectForReturn();
  session.crossing = {
    run: runAbsence(sim, clock.t, clock.t + span),
    // `absenceSpan`'s cap is deliberately bypassed: it exists to stop a player
    // who was away for a year from waiting through a year, and a developer
    // asking for a year means the year.
    est: {
      worldSeconds: span,
      worldYears: span / (365 * DAY),
      requestedWorldSeconds: span,
      capped: false,
      // `REAL_SECONDS_PER_WORLD_YEAR` is catchup.ts's own measured rate; the
      // progress panel only uses this to have something to say.
      estimateRealSeconds: (span / (365 * DAY)) * REAL_SECONDS_PER_WORLD_YEAR,
    },
    leftAt: clock.t,
  };
  devSkipChronicle = true;
  const y = span / (365 * DAY);
  return y >= 1 ? `advancing ${y.toFixed(2)} world-years…`
                : `advancing ${Math.round(span / DAY)} world-days…`;
};

/** Set by the dev door; consumed by `pumpAbsence` when the run completes. */
let devSkipChronicle = false;

const doLoad = () => {
  const snap = loadLocal(SAVE_KEY);
  if (!snap) { notice('no world to remember'); return; }
  try {
    adoptWorld(snap);
    // …and then the world carries on from where the wall clock says it should
    // be, not from where the file was written. This is the only place in the
    // build where real elapsed time reaches the simulation.
    beginAbsence(realSecondsAway());
    if (!session.crossing) notice('the world returned');
  } catch (err) {
    notice(`load refused: ${err instanceof Error ? err.message : err}`);
  }
};
/**
 * The general menu (Escape). Its actions are closures rather than values, so
 * it can be built here — before `hud`, `godhood` and `rset` exist — and still
 * reach them: everything it calls runs long after all three are up.
 */
const menu = createMenu({
  onSave: () => doSave(),
  onLoad: () => doLoad(),
  onSound: () => sound.toggle(),
  openGodhood: () => godhood.open(),
  openSettings: () => rpanel.open(),
});

addEventListener('keydown', (e) => {
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if (e.key === 'k') doSave();
  else if (e.key === 'l') doLoad();
  else if (e.key === 'Tab') {
    /*
     * **Tab is Become.** D §1.2 names the last phase of the loop, and it is one
     * of the two surfaces a player should be able to reach without thinking —
     * *show me what kind of god I am becoming*. It used to open the render
     * settings, which is a draw-distance panel holding one of the game's two
     * primary keys.
     *
     * The preventDefault is not optional: Tab would otherwise walk browser
     * focus through the bar's own buttons and leave one of them armed-looking.
     */
    e.preventDefault();
    // Become is a divine surface; a mortal body does not open it (brief §32)
    if (modes.mode === 'mortal') return;
    godhood.toggle();
  } else if (e.key === 'Escape') {
    /*
     * Escape's precedence chain, and it is the ONLY listener for the key in the
     * build. It has always meant *let go* — of the armed verb, of what the eye
     * is holding onto — and every panel closes on it too, so it opens the menu
     * only when there is nothing left to release.
     *
     * Every panel used to bind its own key: the Web took `B`, the render sheet
     * took Tab, and both raced whatever main did. One key, one owner, one
     * chain — which is also why the settings panel is asked here rather than by
     * reaching into its DOM.
     */
    if (charge) { charge = null; hud.disarm(); return; }   // a held cast, let go unspent
    if (rpanel.isOpen) { rpanel.close(); return; }
    if (godhood.isOpen) { godhood.close(); return; }
    if (menu.isOpen) { menu.close(); return; }
    // a hand closed on the ground is the most literal thing Escape can mean
    // by "let go", so it comes first — and it writes nothing, because a hold
    // in progress has never written anything (law 9)
    if (session.dig || session.hold) { abandonDig(); session.hold = null; return; }
    // the same in both bodies: a mortal chooses and observes as the deity
    // does, so Escape lets go of the choice on foot too. Escape never ascends;
    // the bar does.
    if (hud.armed || chosen) { hud.disarm(); letGo(); return; }
    menu.open();
  }
});

// the chrome around the world: the year, the bar, the Chronicle, the one card.
// Clicking a Chronicle line glides the presence to where it happened.
let flyTarget: { x: number; z: number; y?: number } | null = null;
const hud = createHud({
  onFlyTo: (x, z) => { flyTarget = { x, z }; },
  onAnchor: () => doAnchor(),
  onInhabit: () => enterMortal(),
  onAscend: () => ascend(),
  onMortalSlot: (slot) => pressMortalSlot(slot),
});

/* ── the two bodies: descending into a life, and ascending from it ──────── */

/**
 * The deity's field of view, taken the moment a mortal body is entered and
 * put back exactly on ascending. The shared camera is never otherwise
 * re-tuned per mode: the mortal gets `MORTAL_FOV` (60°) because 46° on foot
 * is a telephoto walk, and the deity gets its own number back, whatever it was.
 */
let deityFov = camera.fov;
/** Whose viewpoint the mortal body is — said on the bar's control, nowhere else. */
let mortalName = '';

/**
 * INHABIT. A chosen, living person → the mortal body is seated on their
 * present ground (`locatePerson`, the follow's own lookup), facing the way
 * the deity's eye was facing, and owns the input; the deity is left exactly
 * where it stands. Refuses in words and changes nothing otherwise.
 *
 * Everything the divine hand was in the middle of is let go of UNSPENT — a
 * held charge, a grasp, a dig, an armed verb, a Chronicle glide, the arrival
 * — because none of it can be finished from inside a mortal and a charge
 * that landed on ascending would be a cast nobody made. The eye stops
 * FOLLOWING but keeps its CHOICE: a drag that looks away does the same, and
 * it is what lets ascending find the eye where it was rather than glided off
 * after wherever the person walked in the meantime (brief §7, §35). The card
 * stays open — on foot it is the card of the life just entered, which is the
 * one thing a player descending into somebody wants to read.
 */
const enterMortal = () => {
  const r = modes.inhabit(chosen);
  if (!r.ok) {
    if (r.reason === 'no-person') notice('choose a mortal first');
    else if (r.reason === 'gone') notice('that life is not here to be lived');
    return;
  }
  charge = null;
  hud.disarm();
  abandonDig();
  session.hold = null;
  flyTarget = null;
  arrival = null;
  arrivalPending = null;
  if (attention) attention.following = false;
  mortalName = r.name;
  deityFov = camera.fov;
  camera.fov = MORTAL_FOV;
  camera.updateProjectionMatrix();
  notice(`you walk as ${r.name}`);
};

/**
 * ASCEND. The body is put down, the deity's FOV and input restored — and the
 * deity is where the player left it, attending to whatever was last chosen
 * (from either body; the follow stays off, so it does not glide). No event is
 * emitted: this is client perspective state and nothing in the world knows
 * it happened.
 */
const ascend = () => {
  if (modes.mode !== 'mortal') return;
  modes.ascend();
  camera.fov = deityFov;
  camera.updateProjectionMatrix();
};

/**
 * A mortal slot, from a key or the bar: it lights, or goes out, and says in
 * one line that there is nothing behind it yet (`ui/mortalVerbs.ts`). It
 * cannot reach `sim.act` — there is no path from here to it.
 */
const pressMortalSlot = (slot: 1 | 2 | 3 | 4) => {
  const lit = modes.pressSlot(slot);
  if (lit) notice(MORTAL_VERBS[lit - 1].placeholder);
};

// The render-settings panel (Tab). The multipliers the frame loop reads
// live sit on `rset`; everything with a one-shot side effect applies here,
// idempotently, so the stored profile at boot and a slider drag mid-flight
// run the identical path. All of it is render-only — no knob touches the
// simulation, so a save made under any profile is byte-identical.
const BASE_PIXEL_RATIO = Math.min(devicePixelRatio, 2);
const { values: rset, panel: rpanel } = createSettings((s) => {
  // the aura IS the detail ring; the settings multiplier rides on top of it
  chunks.detailScale = detailFor(aura.radius) * s.detailMul;
  sceneFog.near = 400 * s.fogMul;
  sceneFog.far = 12000 * s.fogMul;
  renderer.setPixelRatio(BASE_PIXEL_RATIO * s.resMul);
  const shadowsOn = s.shadowRes > 0;
  renderer.shadowMap.enabled = shadowsOn;
  sun.castShadow = shadowsOn;
  if (shadowsOn && sun.shadow.mapSize.x !== s.shadowRes) {
    sun.shadow.mapSize.set(s.shadowRes, s.shadowRes);
    // three only rebuilds the depth target if the old one is released
    sun.shadow.map?.dispose();
    sun.shadow.map = null;
  }
  nearDetail.setScale(s.reachMul, s.grassMul);
}, () => ({
  fps,
  calls: renderer.info.render.calls,
  tris: renderer.info.render.triangles,
  chunks: chunks.stats.live,
}));

// The Godhood Web (DESIGN.md §7–§10), on `B` — D §1.2 calls the last phase of
// the loop *Become*. Its ALLOCATION is deliberately not on the Simulation yet:
// nothing in the sim reads `web.modifier(...)`, and putting it in the snapshot
// before anything does would cost a SNAPSHOT_VERSION bump, every save, and a
// twenty-minute prehistory rebuild for a system with no effects. It lives in
// localStorage until the first system reads it, and that read is the bump it
// earns. `tools/godhood.ts` prints what it is waiting for.
//
// Its HISTORY, though, is the world's, and now there is one. `D §6.2`'s outer
// nodes are refused unless a deity has actually lived what they ask for, and
// until the Chronicle existed nothing in the build could answer that — the
// `EventBus` ring spans a measured 0.470 world-years. `chronicleOracle` reads
// the durable record instead, through a view that follows both the clock and
// `sim` being swapped out when a world is adopted (see beginAsPlayer).
/**
 * …and the world starts answering to it. Until this line the Web was a panel
 * that spent points and changed nothing: `modifier()` had no call sites at all,
 * so a god could earn levels and allocate them and play exactly as it had.
 *
 * Only the player's own build applies. A rival god acts through the identical
 * `sim.act(..., deityId)` and gets 1 for everything, which is right until
 * rivals have Webs of their own — and the seam already takes the deityId, so
 * that day is a change of this one function.
 */
const useWeb = (s: Simulation) => s.useGodhood(
  (deityId, variable) => deityId === s.pantheon.player.id ? godhoodWeb.modifier(variable) : 1,
  // …and the RULES, the boolean channel: what a verb may mean for this god.
  // The Beasts direction is mostly rules, and `Simulation.act` reads them at
  // the one place a Strengthen decides whether it lands on a herd's own
  // drive or only on the ground under it.
  (deityId, variable) => deityId === s.pantheon.player.id && godhoodWeb.hasRule(variable));
useWeb(sim);
godhoodWeb.setOracle(chronicleOracle(
  sim.pantheon.player.id,
  worldHistory(() => sim, () => clock.t),
));

/**
 * And the LEVEL is derived, not stored. `D §6`: a god levels when the world
 * meaningfully changes through it, so `godhood/level.ts` reads the Chronicle
 * and `GodhoodWeb.level` is whatever that says — recomputed here rather than
 * saved, which is what makes it a property of *this world's* history and not
 * of the browser it was played in.
 *
 * The allocation stays in localStorage; only the budget moves. A player who
 * carries a spent Web into a fresh world finds `pointsFree` negative and
 * simply cannot spend more until they have earned it back, which is the
 * honest behaviour: refunds still work, nothing is confiscated, and the
 * world has the last word on what this god is.
 *
 * Recomputed at boot and whenever the Web is looked at — `standingOf` walks
 * the god's strands, which the journal's own forgetting caps at a few hundred,
 * so it is far too cheap to be worth caching and much too dear for a frame.
 */
/**
 * THE MOMENT SOMETHING BECOMES POSSIBLE — said in the world, not only on
 * `Tab` (`PLAYABLE.md` P5: *after a consequence genuinely persists, Tab
 * recognises that something has become possible*). The panel names an
 * earned node when it is opened; this is what tells a player who has not
 * opened it that there is something to open. Once per qualification, never
 * again: the set of nodes already seen earned is kept for the session, and
 * a node the god has taken is not an announcement.
 *
 * Polled a few times a minute rather than on every cast, because an
 * endurance can arrive days after the act — an omen is answered when the
 * band walks to it, a burn finishes when it finishes — and the oracle's
 * answers are a walk over the god's own strands, which the journal's own
 * forgetting keeps to a few hundred.
 */
const becomeSeen = new Set<string>();
let becomeCheckedAt = -Infinity;
const BECOME_POLL_MS = 4000;
const noticeBecome = (nowMs: number) => {
  if (nowMs - becomeCheckedAt < BECOME_POLL_MS) return;
  becomeCheckedAt = nowMs;
  for (const node of NODES) {
    if (!node.qualifies || becomeSeen.has(node.id)) continue;
    if (godhoodWeb.ranksIn(node.id) > 0) { becomeSeen.add(node.id); continue; }
    if (!godhoodWeb.lived(node.id)) continue;
    becomeSeen.add(node.id);
    notice(`something has become possible — ${node.name.toLowerCase()}. Tab`, 6000);
    sound.become();
  }
};

const refreshLevel = () => {
  // History's standing (law 25) OR the stones spent (2026-08-18), whichever
  // has carried the god further: neither is a numerator for the other, and
  // a save that earned its level the old way keeps it.
  godhoodWeb.level = Math.max(
    standingOf(sim.chronicle, sim.pantheon.player.id, clock.t).level,
    levelOf(sim.pantheon.player.spent));
};
refreshLevel();
/**
 * …and a purchase takes effect NOW, not on the next reload.
 *
 * The panel's onChange was `forgetCastMods` alone, which was right while the
 * only thing the Web could reach was the price of a cast. It reaches the sky
 * and the ground's patience too now, and both are read once in `syncReach` —
 * so spending a point on *The High Air* would have opened a ceiling the
 * presence did not learn about until something else happened to re-sync. A
 * progression surface whose purchases do not land is worse than one that sells
 * nothing.
 */
const godhood = createGodhoodPanel(
  godhoodWeb, () => { forgetCastMods(); syncReach(); }, refreshLevel,
  // …and the reading the panel already caused to be computed. `refreshLevel`
  // has been calling `standingOf` on every open and keeping one of its six
  // fields; this hands the panel the whole thing so it can say WHY.
  () => standingOf(sim.chronicle, sim.pantheon.player.id, clock.t));

// the divine verbs. Strike is a double-click; Rain, Strengthen and Weaken ride
// modifier-clicks. Every verb enters the world as a condition — the fire,
// the colours and the fuel all read the same state the verbs wrote.
let noticeText = '';
let noticeUntil = 0;
const raycaster = new THREE.Raycaster();
const _fallsV2 = new THREE.Vector2();
const notice = (text: string, ms = 2600) => { noticeText = text; noticeUntil = performance.now() + ms; };
const aimAtClient = (cx: number, cy: number): void => {
  const r = stage.getBoundingClientRect();
  raycaster.setFromCamera(new THREE.Vector2(
    ((cx - r.left) / r.width) * 2 - 1,
    -((cy - r.top) / r.height) * 2 + 1,
  ), camera);
};
/**
 * Everything in the world is selectable (v0.3.2 — "one tree can become
 * sacred"; Expansion II §3).
 *
 * One raycast resolves the pointer to a *thing*: the instanced meshes are
 * already there and already carry the only identity a deterministic world
 * needs — for a plant or an outcrop, its own position. Simulation entities
 * are resolved by name and by nearest, which is cheap and never wrong at the
 * ranges you can actually click at.
 *
 * This runs every frame and writes nothing. OBSERVATION MUST NOT CREATE
 * HISTORY: looking is free, and `test/attention.ts` proves it by hovering a
 * live world and comparing snapshots.
 */
const _im = new THREE.Matrix4();
const _ip = new THREE.Vector3(), _iq = new THREE.Quaternion(), _is = new THREE.Vector3();
/** The base and size of one instance of an InstancedMesh, in world metres. */
const instanceAt = (m: THREE.InstancedMesh, i: number) => {
  m.getMatrixAt(i, _im);
  _im.decompose(_ip, _iq, _is);
  return { x: _ip.x, y: _ip.y, z: _ip.z, w: Math.max(_is.x, _is.z), h: _is.y };
};
/** Nearest of a list to a point, or undefined past `within`. */
const nearest = <T extends { x: number; z: number }>(
  list: readonly T[], x: number, z: number, within: number,
): T | undefined => {
  let best: T | undefined;
  let bestD = within;
  for (const it of list) {
    const d = Math.hypot(it.x - x, it.z - z);
    if (d < bestD) { bestD = d; best = it; }
  }
  return best;
};

/**
 * How much easier a thing is to keep than to acquire. With the mouse captured
 * the pick rides the gaze itself, and a gaze breathes — without hysteresis the
 * named thing flickered against its own background at every part-pixel drift.
 * A held target's score is scaled by this, which in distance terms lets it
 * survive out to ~1.8× the reach that first won it.
 */
const HOVER_HOLD = 0.55;

/**
 * What a ray-hit plant scores, in the same `distance / tolerance` currency as
 * everything else.
 *
 * It used to be **0**, on the reasoning that "the ray went through it, so
 * there is nothing nearer to argue with" — offered as `offer(t, 0, 1)`. That
 * is not a strong preference, it is an *absolute* one: `bestScore` starts at
 * 1, every other candidate scores `d / reach` with `d >= 0`, and `0 < 0` is
 * false. So once the ray touched any plant, **nothing else could ever win, at
 * any distance or angle**, and the hover hysteresis could not rescue it
 * either since `0 * HOVER_HOLD` is still 0. A person under canopy, a hearth
 * in wood, a herd in open forest, an anchor or a boulder beneath a tree were
 * unpointable — law 12 broken silently, and looking exactly like a fiddly
 * pointer rather than a missing class of subject.
 *
 * A ray hit *is* strong evidence, so the plant keeps a real advantage: a
 * non-plant has to score under this to take the pick. `tools/pick.ts` prices
 * both sides of that on the shipped island, and they do not overlap —
 *
 *   restores   a subject aimed at squarely scores p99 **0.0001** once the
 *              ground point is resolved properly, so 0.35 clears it by
 *              ~3,500x (and by ~1,900x through the hysteresis, at 0.19)
 *   costs      aiming squarely at a tree crown, a non-plant is near enough to
 *              take it in **0.0%** of aims at 0.35, and at 0.50; the steal
 *              curve does not leave zero until 0.75 (0.5%) and 1.00 (1.4%)
 *
 * In metres it is the radius around the aim point that out-argues an
 * occluding plant: 0.70 m at 200 m altitude, 1.40 m at 400 m. Inside that the
 * crosshair is visibly on the thing rather than on the canopy, and where it
 * is genuinely ambiguous the tie should go to the subject with an identity
 * over one of a million procedural plants.
 */
const PLANT_HANDICAP = 0.35;

/**
 * Does the world hold this particular thing apart from the others?
 *
 * `world/things.ts` has kept `standing`, `since` and `acts` per place since
 * promotion was built, keyed on exactly the `placeKey` the pick already mints —
 * and the pick never once asked. So a tree three casts and a century deep
 * hovered as *"a broadleaf tree — 11 m"*, identical to its untouched
 * neighbour, and the only signal a player ever got was a chunk recolour they
 * had to already be staring at. The repetition mechanic the whole store exists
 * to express was unlearnable from inside the game.
 *
 * The note carries no hand and cannot: `ThingMark` holds no `godId` (law 10),
 * and this is a pure read — `test/promotion.ts` already proves law 9 for
 * `things.at`.
 */
const apart = (x: number, z: number, fallback?: string): { note?: string } => {
  const m = sim.things.at(x, z, clock.t);
  if (!m || Math.abs(m.standing) < PROMOTE) return fallback ? { note: fallback } : {};
  return { note: m.standing > 0
    ? 'and nothing about this one is ordinary'
    : 'and something went wrong with this one' };
};

const pickAtClient = (cx: number, cy: number, sticky?: Target | null): Target | null => {
  aimAtClient(cx, cy);
  refreshPickable();
  const ground = groundAlong(raycaster.ray.origin, raycaster.ray.direction);
  // a ray cast from a stage with no size — the pane hidden, a resize to
  // nothing — is NaN from the first line and lands nowhere; every reader
  // below would otherwise name the biome of nowhere and throw
  if (!ground || !Number.isFinite(ground.x) || !Number.isFinite(ground.z)) return null;
  const eye = raycaster.ray.origin;
  const far = ground.distanceTo(eye);
  /**
   * How near a thing has to be to the ground point to count as the thing you
   * meant. It grows with distance because a metre at four kilometres is a
   * third of a pixel: the grab is in screen terms, always, or everything
   * past the next hill becomes unpointable.
   */
  const tol = (size: number) => Math.max(size, far * 0.010);
  /**
   * How many pixels tall a thing of `metres` is at `dist`, against this
   * camera. `tools/settlement.ts`' rule — under about three pixels is not a
   * signal — is what decides whether a thing is worth being able to MEAN, and
   * both the litter branch and the animal branch resolve it through here so
   * the two cannot drift apart.
   *
   * It matters beyond tidiness, because a channelled cast collapses onto its
   * target's own footprint: if every point on a hillside resolved to a pebble
   * or a distant rabbit, a god could no longer cast wide at all.
   */
  const PICK_PIXELS = 3;
  const pxOf = (metres: number, dist: number) =>
    (metres * (stage.clientHeight / 2))
      / Math.max(1, dist * Math.tan(((camera.fov * Math.PI) / 180) / 2));
  let best: Target | null = null;
  let bestScore = 1;                       // 1 = exactly at the edge of reach
  const offer = (t: Target, d: number, reach: number) => {
    let score = d / reach;
    if (sticky && sticky.kind === t.kind && sticky.id === t.id) score *= HOVER_HOLD;
    if (score < bestScore) { bestScore = score; best = t; }
  };
  /**
   * Distance from the aim point to the EDGE of a thing, not to its centre —
   * zero once the aim is inside the footprint. **Every candidate is measured
   * this way now.** Litter, rocks, relics and animals always were; people,
   * roofs, stones and herds were measured centre-to-point, and the two
   * currencies did not compare: a twig scored 0 the moment the crosshair sat
   * anywhere on it, while a person aimed at squarely still scored 0.8 m over
   * a 2 m reach — so the twig under their feet took every tie and precise
   * selection of anything standing in litter was fiddly (HANDOFF's known-weak
   * list, and the note at the animal branch below). It is the same failure
   * shape as `PLANT_HANDICAP`'s: not a preference but an absolute one, and the
   * fix is likewise not a veto on litter but ONE measure for everything.
   * Ties at 0 fall to whichever was offered first, and the order below puts
   * the things with an identity ahead of the ones with only a position.
   */
  const edge = (d: number, size: number) => Math.max(0, d - size * 0.5);

  // ---- the one thing worth a ray: plants, whose crowns are what you see ----
  {
    const hit = raycaster.intersectObjects(pickable, true)
      .find((h) => h.object.visible && h.distance <= far + 2);
    if (hit && hit.instanceId !== undefined) {
      const g = instanceAt(hit.object as THREE.InstancedMesh, hit.instanceId);
      const species = hit.object.name.slice(4);
      const said = namePlant(species, g.h);
      offer({ kind: 'plant', id: `plant:${placeKey(g.x, g.z)}`,
              x: g.x, y: g.y, z: g.z, size: Math.max(0.6, g.w * 0.9),
              about: { species, height: g.h },
              ...said, ...apart(g.x, g.z, said.note) }, PLANT_HANDICAP, 1);
      // A CROWN UNDER THE CURSOR HIDES WHAT STANDS BEHIND IT (2026-08-18,
      // Griffin: "when I am looking at a tree I shouldn't be able to select
      // objects behind it"). Everything below is found by where it stands
      // near the GROUND point of the ray, and that point is behind this crown
      // whenever the ray met the crown first — so a stone under the far side
      // of a tree, or a person the canopy hides, could take the pick from the
      // tree that is actually under the pointer. If the crown was struck
      // short of the ground by more than a body's height, the ground point
      // is not visible and nothing standing on it is offered.
      if (hit.distance < far - 2.5) return best;
    }
  }

  // ---- and everything else, by where it stands ----
  {
    const who = mortals.nearestTo(ground.x, ground.z, tol(2));
    if (who) {
      const band = sim.bands.bands().find((b) => b.people.some((q) => q.id === who.id));
      const person = band?.people.find((q) => q.id === who.id);
      const people = band ? sim.cultures.get(band.cultureId)?.name : undefined;
      offer({ kind: 'person', id: who.id, x: who.x, y: who.y, z: who.z, size: 1.6,
              ...(person ? namePerson(person.name, people) : { name: 'somebody' }) },
            edge(Math.hypot(who.x - ground.x, who.z - ground.z), 1.6), tol(2));
    }
  }
  {
    const s = nearest(sim.bands.structures(), ground.x, ground.z, tol(6));
    if (s) {
      const size = s.type === 'hearth' ? 4 : 5;
      // a fire on a shared ground names the constellation it belongs to:
      // functional fires within hearth range of this one, itself included
      const fires = s.type === 'hearth'
        ? sim.bands.structures().filter((q) =>
            q.type === 'hearth' && q.condition >= 0.2 &&
            Math.hypot(q.x - s.x, q.z - s.z) <= 150).length
        : 1;
      offer({ kind: 'structure', id: s.id, x: s.x, y: groundAt(s.x, s.z), z: s.z, size,
              ...nameStructure(s.type, s.condition, litHearths.has(s.id), fires) },
            edge(Math.hypot(s.x - ground.x, s.z - ground.z), size), tol(6));
    }
  }
  {
    const a = nearest(sim.anchors.all(), ground.x, ground.z, tol(8));
    if (a) {
      offer({ kind: 'anchor', id: a.id, x: a.x, y: groundAt(a.x, a.z), z: a.z, size: 6,
              ...nameAnchor(a.stage) },
            edge(Math.hypot(a.x - ground.x, a.z - ground.z), 6), tol(8));
    }
  }
  {
    const h = nearest(wildlifeViews, ground.x, ground.z, tol(30));
    if (h) {
      offer({ kind: 'herd', id: `herd:${h.species}:${h.seed}`,
              x: h.x, y: groundAt(h.x, h.z), z: h.z, size: 24,
              about: { species: h.species }, ...nameHerd(h.species) },
            edge(Math.hypot(h.x - ground.x, h.z - ground.z), 24), tol(30));
    }
  }
  /*
   * ---- and ONE animal out of the herd, when there is one to mean ----
   *
   * A person has always been pointable one at a time and an animal was not:
   * the finest thing a god could mean was *those* deer, never *that* deer,
   * though `render/wildlife.ts` has drawn up to 64 separate bodies per herd
   * since it was written. Law 12 wants the individual pointable; law 3 wants
   * detail to follow relevance, so the herd stays the subject at range.
   *
   * Identity costs nothing, exactly as a tree's does. `bodyAt` places body
   * `i` as a pure function of (herd seed, index, clock), so `(seed, i)` IS
   * the animal's name — nothing stored, nothing allocated for being pointed
   * at, the same on every machine (laws 2 and 9). And it is the RENDERER's
   * own function, not a copy: the pointer cannot name an animal standing
   * anywhere but where the player can see it.
   *
   * **The gate is the ≥3 px rule litter already uses, and it needs no new
   * constant** — `BODY_LENGTH` and the pixel maths the pick already does.
   * `tools/animals.ts` measured both halves of it. Body size binds and
   * crowding never does: a grazer's body crosses 3 px at 678 m while its
   * median gap to its nearest neighbour is still 3 px at 2,587 m, so the rule
   * can never offer an animal the player cannot aim at separately. Per
   * species that is grazer 678 m, predator 636 m, browser 551 m, scavenger
   * 212 m, small game 170 m — a rabbit has to be genuinely close.
   *
   * Cost is 0.0071 ms with the per-herd cheap reject, against the march's
   * 0.035 ms, so it is free at frame centre every frame.
   */
  {
    let bestD = Infinity;
    // The individual's own build travels with the hit. `bodyAt` already
    // computed all of it to DRAW the body, so carrying it here costs nothing —
    // and without it the pointer says "one of the herd" while the renderer is
    // showing an antlered stag standing next to a pale hind. Law 12: if a
    // thing meaningfully exists, you should be able to point at it and be told
    // what it is.
    let bestBody: {
      v: WildlifeView; i: number; x: number; y: number; z: number;
      seen: SeenAnimal;
    } | null = null;
    for (const v of wildlifeViews) {
      // cheap reject before touching a herd's bodies. `standRadius`, not
      // `herdSpread`: the stand is where the animals were dealt and the drift
      // carries them up to 1.6·√2 further, so rejecting on the spread alone
      // makes the animals that have wandered furthest — the ones standing
      // alone, and so the easiest to mean — silently unpointable.
      if (Math.hypot(v.x - ground.x, v.z - ground.z) - standRadius(v) > tol(30)) continue;
      // is one body of this kind worth meaning from here? The herd's distance
      // serves for the whole stand — its own spread is far under the range at
      // which this decides anything.
      const dist = Math.hypot(v.x - eye.x, v.z - eye.z, groundAt(v.x, v.z) - eye.y);
      if (pxOf(BODY_LENGTH[v.species], dist) < PICK_PIXELS) continue;
      const reps = bodiesInView(v);
      // To the animal's BODY, not to the point its feet are on — the measure
      // `edge` gives everything now. It was load-bearing here before it was
      // general: the ground is thick with litter and a litter item scores 0
      // once the aim is inside its own footprint, so an animal measured
      // centre-to-point scored ~0.4 against a twig's 0 and lost the tie — and
      // so did a person, a roof, a stone and a herd, until `edge` was applied
      // to them too. Measured live over a 64-head herd at 150 m: aiming
      // squarely at every drawn body names the animal 63 times of 63, and the
      // exact one 62 times — the miss is a second animal standing nearer the
      // aim than its neighbour, which is not a mistake.
      const half = BODY_LENGTH[v.species] * 0.5;
      for (let i = 0; i < reps; i++) {
        const b = bodyAt(v, i, drawnAtSeconds, groundAt);
        const d = Math.max(0, Math.hypot(b.x - ground.x, b.z - ground.z) - half);
        if (d < bestD) {
          bestD = d;
          bestBody = { v, i, x: b.x, y: b.y, z: b.z,
                       // `b` is already in hand; this is a copy, not a lookup
                       seen: { kind: b.kind.label, calf: b.calf,
                               horned: b.horn > 0, morph: b.morph } };
        }
      }
    }
    if (bestBody) {
      const { v, i } = bestBody;
      const size = BODY_LENGTH[v.species];
      offer({ kind: 'animal', id: `animal:${v.species}:${v.seed}:${i}`,
              x: bestBody.x, y: bestBody.y, z: bestBody.z, size,
              about: { species: v.species },
              ...nameAnimal(v.species, bodiesInView(v) <= 1, bestBody.seen) },
            bestD, tol(size));
    }
  }
  {
    const r = rocks.nearestTo(ground.x, ground.z, tol(2));
    if (r) {
      const said = nameRock(true, r.size * 2);
      offer({ kind: 'rock', id: `rock:${placeKey(r.x, r.z)}`,
              x: r.x, y: r.y, z: r.z, size: Math.max(1.5, r.size),
              ...said, ...apart(r.x, r.z, said.note) },
            Math.max(0, Math.hypot(r.x - ground.x, r.z - ground.z) - r.size), tol(2));
    }
  }
  {
    // The rare things, and they are offered BEFORE litter on purpose.
    //
    // Both ride invertible lattices, so neither costs a search — but they mean
    // opposite things. Litter is dense enough that under a tolerance of tens of
    // metres *some* pebble is always nearest, so if a relic were offered after
    // it, a once-in-a-valley find would lose every tie to a twig lying closer.
    // A relic is also pointable much further out, for the same reason a fallen
    // log is: it is bigger, and being the only one for miles is its content.
    //
    // `relicNear` returns null until the relics themselves land; this call site
    // exists so that landing them is never an edit to main.ts.
    const relicReach = Math.min(tol(1.2), 60);
    const rel = far > POINTABLE_FAR ? null
      : relicNear(ground.x, ground.z, relicReach, (x, z) => region.siteAt(x, z));
    // …and not one a god already carried off — but unlike litter, a relic that
    // has been moved is offered AT ITS NEW GROUND rather than dropped from the
    // world. A twig lifted away is gone and nobody could pick it out again; a
    // standing stone is a landmark on whatever ground it ends up on, and a god
    // that could move one and then never point at it again would have been
    // handed an editor rather than a hand.
    const carriedRel = rel ? sim.carried.at(rel.key) : null;
    const relic = carriedRel ? { ...rel!, x: carriedRel.x, z: carriedRel.z } : rel;
    if (relic && pxOf(relic.size, far) >= PICK_PIXELS
        && Math.hypot(relic.x - ground.x, relic.z - ground.z) - relic.size * 0.5 < relicReach) {
      offer({ kind: 'relic', id: `relic:${relic.key}`,
              x: relic.x, y: groundAt(relic.x, relic.z), z: relic.z,
              size: Math.max(0.5, relic.size), ...nameRelic(relic) },
            Math.max(0, Math.hypot(relic.x - ground.x, relic.z - ground.z) - relic.size * 0.5),
            relicReach);
    }
    // …and a relic standing somewhere the lattice never put one. `relicNear`
    // only ever answers from the lattice, so this is the one route by which a
    // moved stone can be found at all.
    if (!relic && far <= POINTABLE_FAR) {
      const put = sim.carried.near(ground.x, ground.z, relicReach)
        .find((c) => RELIC_KIND.has(c.kind));
      if (put && pxOf(put.size, far) >= PICK_PIXELS) {
        const asRelic = { key: put.key, kind: put.kind as RelicKind,
                          x: put.x, z: put.z, size: put.size };
        offer({ kind: 'relic', id: `relic:${put.key}`,
                x: put.x, y: groundAt(put.x, put.z), z: put.z,
                size: Math.max(0.5, put.size), ...nameRelic(asRelic) },
              Math.max(0, Math.hypot(put.x - ground.x, put.z - ground.z) - put.size * 0.5),
              relicReach);
      }
    }
  }
  {
    // Ground litter (Design III §28). No raycast and no search through what is
    // on screen: the lattice is invertible, so this is `floor(x / 2)` and a
    // handful of hashes — cheaper than picking a tree. It answers *what is
    // here*, so a pebble the render budget is currently omitting still has a
    // name, exactly as a promoted plant culled by wear keeps its mark.
    // …but only inside the range it is actually drawn at. `tol` grows with
    // distance so that distant things stay pointable, and litter is dense
    // enough that under a tolerance of tens of metres *some* pebble is always
    // nearest — so without this gate a pebble outranks a settlement from the
    // air. Existence is camera-free; pointability is not, and only the second
    // is allowed to care where the eye is.
    const reach = Math.min(tol(1.2), 2.5);
    const found = far > POINTABLE_FAR ? null
      : litterNear(ground.x, ground.z, reach, (x, z) => region.siteAt(x, z),
                   (x, z) => surfaceHeight(region, x, z, 1),
                   // Wear is sparse HISTORY, not a render budget, and that is
                   // the whole reason this argument exists. The budget case is
                   // deliberate and documented — a pebble the renderer is
                   // omitting still has a name, exactly as a promoted plant
                   // culled by wear keeps its mark. But a beaten camp floor
                   // really has no sedge on it: over packed ground the world
                   // keeps 44% of items and the pointer was naming 100%, so it
                   // was offering things the ground no longer holds. The two
                   // readers now call one rule (`wornKeep` in world/litter.ts).
                   (x, z) => wornAt(wearReader, x, z, clock.t));
    // …and not if a god already carried it off. The lattice grows a stone at
    // that cell for ever; the carried store is the record that it is no longer
    // lying there, and the pointer has to agree with the world.
    // …AND NOT IF IT IS NOT DRAWN (2026-08-18). The paragraph above says the
    // budget case is deliberate — "a pebble the renderer is omitting still
    // has a name". Griffin overruled it: a clump of sedge at 95 m that the
    // disc's budget had dropped could be selected and not seen, and a thing
    // you cannot see is not a thing you can mean. `nearDetail.isDrawn` is the
    // renderer's own record of what it drew this rebuild — disc, band and
    // carried — so the pointer and the picture cannot disagree.
    const lit = found && (sim.carried.at(found.key) || !nearDetail.isDrawn(found.x, found.z))
      ? null : found;
    // …and only while you can actually see *which one*. `tools/settlement.ts`'
    // rule — under about three pixels is not a signal — decides it, measured
    // against this camera rather than assumed: a 0.5 m pebble is 1.3 px at
    // 400 m, so pointing at it there is aiming at a rumour. It matters beyond
    // tidiness, because a channelled cast collapses onto its target's own
    // footprint: if every point on a hillside resolved to a pebble, a god
    // could no longer cast wide at all. A fallen log stays pointable much
    // further out than a pebble, which is correct — it is much bigger.
    if (lit && pxOf(lit.size, far) >= PICK_PIXELS) {
      offer({ kind: 'litter', id: `litter:${lit.key}`,
              x: lit.x, y: groundAt(lit.x, lit.z), z: lit.z,
              size: Math.max(0.5, lit.size), about: { species: lit.kind },
              ...nameLitter(lit.kind, lit.size) },
            Math.max(0, Math.hypot(lit.x - ground.x, lit.z - ground.z) - lit.size * 0.5),
            reach);
    }
  }
  {
    let hit: { id: string; strength: number; d: number; x: number; z: number } | null = null;
    for (const v of pathViews) for (const q of v.points) {
      const d = Math.hypot(q.x - ground.x, q.z - ground.z);
      if (!hit || d < hit.d) hit = { id: v.id, strength: v.strength, d, x: q.x, z: q.z };
    }
    if (hit && hit.d < tol(5)) {
      offer({ kind: 'trail', id: hit.id, x: hit.x, y: groundAt(hit.x, hit.z), z: hit.z,
              size: 6, about: { strength: hit.strength },
              ...nameTrail(hit.strength) }, hit.d, tol(5));
    }
  }

  if (best) return best;

  // Nothing in particular is standing here — but the GROUND may still be
  // somewhere rather than anywhere (Design III §12–§15). A landmark is offered
  // last, and only against bare ground, for the reason the litter layer had to
  // learn: a thing with a 700 m footprint outranks everything if it competes
  // on distance, and a god would point at a hillside and be told about the
  // mountain it is on. Under a tree, you get the tree; on open ground at the
  // top of the Watch, you get the Watch.
  {
    const m = nearestLandmark(landmarks, ground.x, ground.z, tol);
    if (m) {
      return { kind: 'landmark', id: m.id,
               x: m.x, y: groundAt(m.x, m.z), z: m.z, size: m.size,
               ...describeLandmark(m) };
    }
  }

  // and otherwise the world itself, which is a perfectly good thing to mean
  const site = region.siteAt(ground.x, ground.z);
  return { kind: 'ground', id: `ground:${placeKey(ground.x, ground.z)}`,
           x: ground.x, y: ground.y, z: ground.z, size: 0, ...nameGround(site.biome) };
};

const pickAt = (e: MouseEvent, sticky?: Target | null): Target | null =>
  pickAtClient(e.clientX, e.clientY, sticky);

// every verb enters through the simulation itself: sim.act emits the divine
// event (which bands may witness), applies the physical change, and queues
// the dirty chunks — main.ts only points, and shows the spectacle. The scale
// is the wheel's chosen Focus, capped by reach — the simulation never learns
// where the presence was, or what it chose; it receives two numbers.
//
// A far strike is a storm, so its bolts arrive over the next second rather
// than all in one frame — the simulation resolved them at the same instant.
const boltQueue: Array<{ at: number; x: number; z: number; soft: number }> = [];
const STORM_BOLTS = 5;      // how many of the storm's bolts get their own flash
const STORM_GAP = 150;      // ms between them

const nice = (m: number) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);

/**
 * The ids of acts these hands spent. A god knows what it did because it did
 * it — not because it read the world's ledger — so this is what lets the
 * Chronicle show your own miracle from anywhere without ever consulting the
 * attribution the world keeps. Nothing else here reads `godId` at all.
 *
 * World-bound: ids mint as `type@time#seq`, so a set carried across an
 * adoption is how a god comes to be credited with a stranger's lightning.
 * `session.adopt` empties it.
 */
const myActs = session.acts;

/**
 * What the player's Godhood has done to the price of a cast.
 *
 * `costOf` is deliberately the ONE place a cast's price is computed — the
 * simulation charges through it and the bar's pips quote through it — so the
 * moment the simulation started applying a Godhood discount, these two preview
 * call sites would have begun quoting a price nobody was charged. Same reader,
 * same numbers, one function: the pip and the bill move together or the pip is
 * a lie.
 */
let castModCache: { all: number; reach: number; focus: number; beast: number } | null = null;
const castMods = () => (castModCache ??= {
  all: godhoodWeb.modifier('act.cost'),
  reach: godhoodWeb.modifier('impulse.cost.distance'),
  // …and what precision costs. Every key here has a twin at `act`'s own
  // `costOf` call: this is the QUOTE and that is the BILL, and a modifier in
  // one of them is a lie on the bar.
  focus: godhoodWeb.modifier('act.cost.tightFocus'),
  // …and what acting on animals costs — Beasts' *Light Hand*. Resolved into
  // `costOf`'s `target` by `castModsFor`, off the kind of the thing aimed at.
  beast: godhoodWeb.modifier('impulse.cost.beastTarget'),
});
/** The quote's mods for a cast aimed at a thing of this kind (or at ground). */
const castModsFor = (kind?: TargetKind) => {
  const m = castMods();
  return { all: m.all, reach: m.reach, focus: m.focus,
           target: kind === 'herd' || kind === 'animal' ? m.beast : 1 };
};
/**
 * …and it is CACHED, which is not premature.
 *
 * `modifier()` walks `activeEffects()`, and that allocates a fresh array over
 * every spent node, epic and crossroad on every single call. The first version
 * of this called it twice per verb inside `verbCostBands`, which the frame loop
 * runs every frame — **600 allocating walks a second** for a number that only
 * changes when the player spends a point.
 *
 * The invalidation is the panel's own `onChange`, which was being passed
 * `undefined`: it is the one place a spend, a refund, an Epic or a crossroad is
 * committed, so it is exactly the signal, and using it means the cache cannot
 * go stale behind a purchase.
 */
const forgetCastMods = () => { castModCache = null; };

/**
 * The focus a cast will actually have, given what the pointer is on.
 *
 * Choosing a thing *is* the statement that you mean this and not the
 * hillside, so a channelled verb collapses to the thing's own footprint and
 * takes the concentration that comes with it — at five metres that is the
 * ceiling, 3.4× the reference. A god that wants the valley selects nothing
 * and points at the ground, which is the whole of the interface.
 */
/**
 * …AND SCROLLING OUT FROM A CHOSEN SUBJECT IS HOW YOU LET GO OF IT.
 *
 * That sentence used to read *a god that wants the valley selects nothing and
 * points at the ground*, and the code meant it literally: over any thing at
 * all, `focusRadius` was discarded outright. So the wheel moved the readout
 * and the cast did not follow, and a player who selected somebody and scrolled
 * out was steering a number that had already been thrown away.
 *
 * The rule now is one line longer and is the whole of semantic scroll:
 *
 *  · **At or under the thing's own footprint, choosing it IS the statement**
 *    — the verb collapses onto it, takes the ceiling concentration, and the
 *    named subject bears the full weight. Unchanged; this is close range.
 *  · **Above it, the subject stays the thread and the cast widens onto what
 *    the subject is part of.** Scroll out from Ena and you are no longer
 *    pressing on Ena; you are pressing on the party she is out with, then on
 *    her band, then on the country they live in. She is still the reason you
 *    are there and still the card's subject — but the world decides which
 *    individuals carry the pressure, which is `PRECISION × SCALE × INTENSITY`
 *    written as an argument rather than as a tuning constant. More scale, less
 *    authorship.
 *
 * `channelRadius` caps at 40 m, which sits inside `local`, so the crossing
 * always lands between the intimate register and the encounter one — exactly
 * where the meaning changes.
 */
const focusFor_ = (target: Target | null): Focus => {
  const wheel = Math.min(focusRadius, focusCap);
  if (!isThing(target)) return focusForRadius(wheel);
  const own = channelRadius(target.size);
  return focusForRadius(Math.max(own, wheel > own ? wheel : own));
};

/** Is the cast still channelled into the thing itself, or has the wheel pulled
 *  out past it? The one predicate the whole register split turns on. */
const channelled = (target: Target | null): boolean =>
  isThing(target) && Math.min(focusRadius, focusCap) <= channelRadius(target.size);

/**
 * `verbForModifiers` STOOD HERE, and it is gone — with `heldMods` and its four
 * listeners.
 *
 * It mapped shift → rain, alt → strengthen, ctrl → weaken, and it was a real,
 * fully castable arming path with FX, sound and a live reticle. Which made it
 * the last place in the build where **the pre-`DECISIONS.md` verb set could
 * still be cast**: the ruling took Weaken off the numbered bar and made Rain a
 * Signature rather than a starter, and a modifier chord that still fired both
 * meant the bar was a presentation layer over a verb set nobody had agreed to.
 *
 * Three things it was also quietly costing, all of which go with it:
 *
 *  · **`hud.armed` never knew about it.** The card and the reticle were taught
 *    to read `hud.armed ?? verbForModifiers(heldMods)`, but the palette itself
 *    was not — so a modifier-armed cast lit no button and showed no cost band
 *    while the world drew its footprint. Two surfaces disagreeing about what
 *    the god was holding.
 *  · **Shift is also the flight surge** (`ui/presence.ts`), so shift-clicking
 *    while flying fast called down rain. A modifier that means two things at
 *    once is not a binding, it is a coin flip.
 *  · **Alt was why `alt+1` did nothing.** The number-key listener still bails
 *    on `altKey` because alt used to be the strengthen modifier — an old
 *    habit's collision guard, outliving the habit.
 *
 * Neither verb is deleted from the simulation. `sim.act('weaken', …)` and
 * `sim.act('rain', …)` are untouched, both are still depth destinations, and
 * law 22 still forbids anything calling either of them moral. This is an
 * exposure decision, exactly as `DECISIONS.md` says.
 */

const cast = async (verb: CastVerb, p: THREE.Vector3, target?: Target | null,
                    /** stones of the god put into it — one for a tap or a click, more for a hold */
                    spend: number = IMPULSE_STONE) => {
  const thing = isThing(target ?? null) ? (target as Target) : null;
  const focus = focusFor_(thing);
  /**
   * THE SAME KEY, AT TWO SCALES, MEANING TWO DIFFERENT THINGS.
   *
   * Under the subject's own footprint the cast is channelled and they bear
   * the full weight — this is close range, and it is unchanged. Scrolled out
   * past it, the subject is still the thread and still where the cast is
   * CENTRED, but the pressure spreads over whoever is out on the same work,
   * with the square falloff and no privileged individual. Nothing about the
   * simulation is new here: `press` has always taken an optional `targetId`
   * and always done the right thing without one. What was missing was any way
   * for the player to say which they meant.
   */
  const aimed = channelled(thing);
  // a channelled verb lands on the thing itself, not on wherever the ray
  // happened to cross its crown; a widened one is still centred on it, because
  // the subject is what the god came here for
  if (thing) p = new THREE.Vector3(thing.x, thing.y, thing.z);
  // `from` is where the god is STANDING, and leaving it off is why casting
  // across the island has been free. `costOf`'s reach term (Design III §88 —
  // extreme distance is an advanced act) reads `from`, and with it undefined
  // `reachFactor` saw distance 0 for every cast the live game has ever made:
  // the 17.6× spread `tools/economy.ts` measures existed only in the probe.
  // `doAnchor` and `doGraspDrop` were passing it; the five verbs were not.
  // A herd's census cell rides with the target: the simulation cannot recover
  // it from a render seed and must not from (x, z) — the anchor leans out of
  // its cell by design — so the pointer, which dealt the herd, says which.
  const herd = thing ? herdOfTarget(thing) : undefined;
  const aim = thing
    ? { kind: thing.kind, id: thing.id, aimed, ...(herd ? { herd } : {}) }
    : undefined;
  const from = { x: presence.x, z: presence.z };
  /**
   * ONE LINE, TWO WORLDS — and everything below it is written once.
   *
   * Unattached, this is the call it has always been and nothing about the
   * timing changes: an `async` function runs synchronously up to its first
   * `await`, and on this branch there is not one.
   *
   * Attached, the world is not here. The cast becomes an INTENT, the server
   * decides, and what comes back is the world's own `DivineResult` — the same
   * object `sim.act` would have returned, because it IS the one `sim.act`
   * returned, on the machine that owns the world. That is what `IntentResult`
   * on the ack exists for, and it is why the two hundred lines under this one
   * — the storm, the level, the notices, the sounds — did not have to learn
   * that a server exists.
   *
   * Null means the ack never came or the world refused; `attach.ts` has
   * already told the player which.
   */
  const r = attached
    ? await attached.act({ verb, x: p.x, z: p.z, focus, from, spend,
                           ...(aim ? { target: aim } : {}) })
    : sim.act(verb, p.x, p.z, clock.t, focus, undefined, aim, from, spend);
  if (!r) return;
  if (r.eventId) myActs.add(r.eventId);
  // §131: a god too spent to act is told so and nothing happens — and now
  // that a cast is a stone, the telling is in stones
  if (!r.ok && r.reason === 'too-weak') { notice('too spent — not a stone of impulse left'); return; }
  // how big the cast LOOKS: a charged cast lands with a wider pulse and a
  // wider answer than a tap — √ of the stones, as the storm and the
  // persistence scale in the sim, so what is seen is what was spent
  const stones = Math.max(1, Math.round(spend / IMPULSE_STONE));
  const spread = Math.sqrt(stones);
  // the stones went into the arc: a level may have turned, and the sky with it
  {
    const lvl = levelOf(god().spent);
    if (lvl !== levelSeen) {
      levelSeen = lvl;
      syncReach();
      refreshLevel();
      notice(`level ${lvl} · ${god().stageInfo.title} · ${nice(god().maxAltitude)} of sky`);
    }
  }
  reticle.pulse(verb, p, focus.radius * spread);
  // Omen is heard now. It was excluded here and returned before reaching
  // `verbFx.spawn` below, so the verb with the richest behavioural response in
  // the build had no immediate answer of any kind — which made it read as the
  // one verb that did nothing. It is still a sign and not a fall: see the
  // `omen` cases in `sound.ts` and `verbFx.ts` for what that costs them.
  sound.cast(verb, focus.softness);
  if (verb === 'rain') rainHeard = performance.now() + 19000;   // the wet minute

  // Omen puts nothing into the land, so there is nothing to show falling on
  // it. The reticle's pulse is the whole spectacle, and it is the player's
  // alone: what the mortals got was a direction and a feeling, and how badly
  // they read it is the width of the cast.
  if (verb === 'omen') {
    // The sign itself, which rises and does not resolve. It draws nothing into
    // the land — `simulation.ts` guarantees an Omen dirties not one chunk —
    // and the effect is written to be unmistakable for weather, because a
    // spectacle that looked like rain would teach the player that Omen touched
    // the ground when its whole nature is that it did not.
    verbFx.spawn('omen', p.x, p.y, p.z, focus.radius * spread);

    // Mirror the witness rule exactly (`BandSystem.witness`): the reach is the
    // cast's own radius plus OMEN_RANGE, and every person is graded from THEIR
    // PARTY's ground rather than the band's centroid. This line used to invent
    // `focus.radius + 1200` against a 6000 m range and measure to the centroid
    // — under-reporting the audience by 4.8 km, and missing bands whose
    // foragers were out (a person sits a p90 402 m from the centroid and up to
    // 1.5 km). The one faculty whose whole output is "who could have seen it"
    // was the one telling the player the wrong number.
    const reach = focus.radius + OMEN_RANGE;
    const seen = sim.bands.bands().filter((b) => {
      if (b.population <= 0) return false;
      if (Math.hypot(b.x - p.x, b.z - p.z) <= reach) return true;
      return b.parties.some((q) => Math.hypot(q.x - p.x, q.z - p.z) <= reach);
    }).length;
    notice(seen === 0
      ? `a sign over ${nice(focus.radius)} — nobody there to see it`
      : `a sign over ${nice(focus.radius)} — ${
          seen === 1 ? 'one band' : `${seen} bands`} could have seen it`);
    return;
  }

  if (verb === 'strike') {
    const bolts = r.bolts ?? [];
    const lit = bolts.filter((b) => b.lit).length;
    // the aimed bolt falls now; the rest of the storm walks the footprint
    verbFx.spawn('strike', p.x, p.y, p.z);
    for (let i = 1; i < Math.min(bolts.length, STORM_BOLTS); i++) {
      boltQueue.push({
        at: performance.now() + i * STORM_GAP,
        x: bolts[i].x, z: bolts[i].z, soft: focus.softness,
      });
    }
    notice(bolts.length === 1
      ? (lit ? `strike — fire kindled${thing ? ` at ${thing.name}` : ''}`
             : `the strike finds nothing to burn${thing ? ` at ${thing.name}` : ''}`)
      : `${bolts.length} bolts across ${nice(focus.radius)} — ${
          lit === 0 ? 'none caught' : lit === 1 ? 'one caught' : `${lit} caught`}`);
    return;
  }

  // the spectacle is sized to the gesture: a Presence rain wets one tree's
  // worth of ground, a Watershed rain draws its curtain across the valley
  verbFx.spawn(verb, p.x, p.y, p.z, focus.radius * spread);
  // A CAST ON A PERSON SAYS SO, and says what it found.
  //
  // Without this the game's central interaction — find somebody, learn what
  // they are, lean on it — reported itself as *the land strengthens on Uvek*,
  // which is a sentence about grass. The player had no way to learn that the
  // verb reaches people at all, and no way to learn the rule that governs it.
  //
  // `touched: 0` is the important line, not the failure case. A cast on
  // somebody whose day is asking nothing of them moves nothing, and saying so
  // plainly is how the player discovers that **you can only strengthen what is
  // already being expressed** — the whole design, taught by the world in one
  // sentence instead of by a tutorial. Law 14 holds: it says where the causal
  // pressure landed and nothing about what will come of it.
  /**
   * A CAST THAT REACHED THE WILD SAYS SO, and says what it found — the same
   * discipline as the person case below. `beast.process` is what the herd
   * was doing that the hand leaned on; `null` is the honest refusal, and it
   * is said out loud because *nothing in them is inclined to move* is how a
   * player learns that Strengthen on a herd means the herd's own drive, and
   * only where there is one (law 21, taught by the world in one sentence).
   */
  if (r.beast && (verb === 'strengthen' || verb === 'weaken')) {
    const b = r.beast;
    const gave = verb === 'weaken' ? 'gives way' : 'takes to it';
    if (thing && aimed) {
      const who = thing.name;
      notice(b.process === 'movement'
          ? (verb === 'weaken' ? `${who} — their drift off this ground gives way` : `${who} — their drift off this ground ${gave}`)
        : b.process === 'increase' ? `${who} — their increase ${gave}`
        : b.process === 'hunt' ? `${who} — their hunting ${gave}`
        : `nothing in ${who} to lean on — not inclined to move, not gathering, not hunting`);
    } else {
      notice(b.herds === 0
        ? `over ${nice(focus.radius)} — no herd out here is inclined to move`
        : b.herds === 1 ? `over ${nice(focus.radius)} — one herd's drift ${gave}`
          : `over ${nice(focus.radius)} — ${b.herds} herds' drift ${gave}`);
    }
    return;
  }
  if (r.touched !== undefined && thing) {
    const who = thing.name;
    // A WIDENED CAST DOES NOT SAY THE CLOSE ONE'S SENTENCE. `named` is false
    // by construction out here, and *nothing in Ena to take hold of today*
    // would be a lie about a cast that never asked anything of Ena in
    // particular. It reports what it actually did: how many people out on
    // that work it moved, and nobody's name.
    if (!aimed) {
      notice(r.touched === 0
        ? `over ${nice(focus.radius)} — nobody out here is doing anything you could lean on`
        : r.touched === 1 ? `over ${nice(focus.radius)} — it finds one pair of hands`
          : `over ${nice(focus.radius)} — ${r.touched} of them take to it`);
      return;
    }
    // `named` and not `touched`: the sentence is about the person the player
    // aimed at, and only they can make it true. Anybody else the footprint
    // caught is the second clause at most.
    const others = (r.touched ?? 0) - (r.named ? 1 : 0);
    notice(!r.named
      ? `nothing in ${who} to take hold of today`
      : verb === 'weaken'
        ? (others > 0 ? `something gives way in ${who}, and in those beside them`
                      : `something in ${who} gives way`)
        : (others > 0 ? `${who} takes to it, and those beside them`
                      : `${who} takes to it`));
    return;
  }

  // and it is *said* by what it was aimed at, because that is the difference
  // the player is meant to feel: a valley is a place, a tree is a decision
  const over = thing ? ` on ${thing.name}` : ` over ${nice(focus.radius)}`;
  notice(verb === 'rain' ? `rain called down${over}`
    : verb === 'strengthen' ? `the land strengthens${over}` : `the land weakens${over}`);
};
/**
 * The Anchor (v0.3.2 §8). Deliberately not on the verb palette: every other
 * act is a gesture from wherever you happen to be, and this one is
 * attendance. You have to fly down and stand on the spot. It costs most of a
 * month of the god's power, it grants nothing, and nobody will come unless
 * they happen to walk past it.
 */
const doAnchor = async () => {
  const ground = Math.max(groundAt(presence.x, presence.z), 0);
  // Attached, the server judges `too-far` and `too-high` against the presence
  // IT last accepted from this session — which the frame loop sends every time
  // the eye moves — rather than against a position in this payload. There is no
  // field for one, deliberately (`host/intent.ts`).
  const r = attached
    ? await attached.anchor({ x: presence.x, z: presence.z })
    : sim.anchorAt(presence.x, presence.z, clock.t, {
        x: presence.x, y: presence.y, z: presence.z, ground,
      });
  if (!r) return;
  if (r.ok) {
    verbFx.spawn('strengthen', presence.x, ground, presence.z, 60);
    sound.cast('strengthen', 0);
    notice('you are bound to this place');
    return;
  }
  notice(r.reason === 'too-high'
    ? `come down to the ground — you are ${nice(presence.y - ground)} above it`
    : r.reason === 'too-weak'
      ? `not enough of you left — ${Math.round(sim.pantheon.player.power * 100)}%` +
        ` of ${Math.round(ANCHOR_COST * 100)}% needed`
      : 'you must be standing where you mean it');
};
// shift+V is the aura (below), so plain V must not also fire — otherwise one
// keystroke would place a stone and light it in the same breath
addEventListener('keydown', (e) => {
  if (modes.mode !== 'deity') return;   // a mortal has no stone to put down
  if (e.key.toLowerCase() === 'v' && !e.shiftKey) doAnchor();
});

/**
 * Grasp — hold **G** (Design IV §21–§27, §35).
 *
 * A faculty of Presence rather than a verb, so it has no Focus, no palette
 * slot and no cast: you take hold of the thing under the pointer, move the
 * pointer, and let go. The world carries on from wherever it lands, which is
 * the whole argument — *physical manipulation creates causes; the simulation
 * creates consequences.*
 *
 * Held rather than tapped on purpose. A tap would read as a command; a hold
 * reads as carrying something, and it is the one gesture in the game where
 * the god is doing a physical thing rather than meaning one.
 */
/**
 * WHAT THE HAND CAN ACTUALLY CLOSE ON — and this set is the fix for a
 * three-layer disagreement, not a restriction.
 *
 * `isThing` admits ten kinds and the routing used it: plant, rock, litter,
 * relic, person, herd, animal, structure, trail, anchor all went to the object
 * grasp. `doGraspTake` admitted one. And a layer below that, `sim.grasp`
 * re-resolves its subject from the litter lattice by COORDINATE
 * (`litterAt(fromX, fromZ)`) and ignores the target entirely, so the
 * simulation's Grasp is litter-only *by construction* — the main.ts gate was
 * not the narrow part, it was the visible part.
 *
 * The cost of the mismatch was not the refusal. It was that the nine refused
 * kinds did not fall through: holding `G` over a boulder was a dead gesture
 * that also could not take hold of the ground the boulder was standing in,
 * which is the thing the same key does one metre to the left. So the rule the
 * build already documents — *a thing under the aim is lifted, bare ground is
 * taken hold of as ground* — was false for nine tenths of the things in it.
 *
 * Routing asks THIS now. Everything else is ground, which is honest: a god
 * cannot pick up a redwood, and the ground it is rooted in is right there.
 *
 * **To widen it, `sim.grasp` has to be handed an identity rather than a
 * coordinate pair.** `world/relics.ts` already prices all seven relic kinds
 * (`relicMass`, `relicAttachment`, complete and covered) and has no `src/`
 * reader for either, so relics are the obvious next member — but they need
 * `CarriedStore` to hold a relic, which is a snapshot change. `WIRING.md`
 * carries that row; this set is where it lands when it is done.
 */
const LIFTABLE = new Set<TargetKind>(['litter', 'relic']);

/** World-bound (`ui/session.ts`): a hand cannot keep its grip across a
 *  change of world, and the object it closed on belonged to the old one. */
const heldNow = (): Target | null => session.hold as Target | null;
const doGraspTake = () => {
  if (session.hold) return;               // keydown repeats while a key is down
  const t = hovered;
  if (!t || !LIFTABLE.has(t.kind)) {
    // Routing should mean this is unreachable; it is kept because a wrong
    // refusal that says nothing is how the old mismatch survived. And it says
    // what WOULD work, the Anchor's way — never a bare error.
    notice(!t ? 'nothing there to take hold of'
      : `${t.name} is more than a hand can lift — the ground it stands in is not`);
    return;
  }
  session.hold = t;
  sound.cast('grasp', 0);
  notice(`you take hold of ${t.name}`);
};
const doGraspDrop = async () => {
  const from = heldNow();
  if (!from) return;
  session.hold = null;
  heldThing.hold(null);
  // wherever the pointer is now — the ground under it will do
  const drop = hovered ?? from;
  const r = attached
    ? await attached.grasp({ fromX: from.x, fromZ: from.z, toX: drop.x, toZ: drop.z })
    : sim.grasp(from.x, from.z, drop.x, drop.z, clock.t, undefined,
                { x: presence.x, z: presence.z });
  if (!r) return;
  if (r.ok) {
    // …and not `strengthen`, which it borrowed: setting a stone down sounded
    // like a blessing, which is the one thing it is not
    sound.cast('release', 0);
    notice(r.damage && r.damage > 0.4
      ? 'you set it down, and it is the worse for it'
      : 'you set it down');
    return;
  }
  notice(r.reason === 'too-weak'
    ? `too heavy — ${Math.round((r.mass ?? 0))} kg, and ${Math.round(sim.pantheon.player.power * 100)}% of you left`
    : r.reason === 'too-far' ? 'too far from you — come closer to it'
      : 'it is not there any more');
};
/**
 * …and the same hand, closed on the ground itself — `ROADMAP.md` §20's other
 * half, which was built and then had no way in.
 *
 * `sim.moveEarth` shipped priced, snapshotted, and covered by 26 assertions,
 * with **zero call sites in `src/`**: the effect ladder every earth design has
 * been reasoning about was built out of landforms no player could make. This
 * is that binding, and it is deliberately not a new button (law 20). Hold G
 * over a *thing* and you lift the thing; hold G over the ground and you take
 * hold of the ground. One key, one gesture, two scales — which is what
 * "Grasp at the scale where the object is the land" always meant.
 *
 * Everything about the shape of it is measured in `tools/handful.ts`:
 *
 *  · **The wheel is how wide.** Focus already means the scale of an act, and
 *    the purse caps it: the widest scoop a god can take to the bottom is
 *    ∛(power/k) — 46.4 m whole, 12.6 m on a day's regeneration. The hand
 *    shrinks as the god spends itself, which is the price rule restated in
 *    the only units the player can see.
 *  · **The hold is how deep**, to the ground's own repose angle (r/3, 37.6°)
 *    — which turns out to be the shape §20's whole cost ladder was already
 *    describing, at four scales. Law 30: intensity comes through input.
 *  · **A flick makes nothing.** Under a third of a full hold the pit is
 *    gentler than the island's own median slope, and *a landform gentler than
 *    the ground it sits on is not a landform*. It refuses and charges nothing
 *    — fire's BURN_MARK_CELLS, applied to a gesture.
 *  · **You must be standing there.** GRASP_REACH, the same 60 m the object
 *    grasp uses, in three dimensions rather than two. Earthworks are an act of
 *    attendance, exactly as the Anchor is.
 *  · **The throw is where it goes.** Beyond the pit's own rim, or the pile
 *    fills the hole it came from and the god has paid for flat ground.
 */
/** A full hold, in seconds — the one number here no measurement can supply. */
const DIG_HOLD = 1.6;
interface Dig { x: number; z: number; y: number; r: number; at: number }
/** World-bound, exactly as the object hold is: a scoop begun in one world is
 *  not a scoop of the next one's ground (`ui/session.ts`). */
const digNow = (): Dig | null => session.dig as Dig | null;

/**
 * How far the god may work from where it stands — for the pit and for the
 * throw alike. The wheel's own radius, floored at the object grasp's arm, so
 * the rule is one sentence: *any ground your attention covers*. Scroll wide to
 * work from altitude, scroll tight to work precisely at your feet.
 */
const digArm = () => Math.max(GRASP_REACH, Math.min(focusRadius, focusCap));

/**
 * How wide the earth lands — measured, and NOT the wheel's radius.
 *
 * The first version made the drop the focus radius, on the reasoning that the
 * wheel means the scale of an act. On screen that was a mistake and the frame
 * said so immediately: a full hand is 50,000 m³, and spread over the 134 m
 * focus the game opens at it stands **1.9 m** — a flank of 1.8°, against an
 * island whose median slope is 14.3°. §20's own words, arriving as a bug: *a
 * landform gentler than the ground it sits on is not a landform, it is a
 * wish.*
 *
 * So the pile is the size of the hole it came from, and the whole legal range
 * of the gesture reads: at `dropR = pit r` the mound stands exactly as tall as
 * the pit was deep, so its flank is the pit's flank — 37.6° at a full hold and
 * 14.3° at the shortest hold `scoopReads` allows. The threshold that decides
 * whether a hold dug anything and the rule that decides how the earth lands
 * turn out to be the same rule, which is how you know it is the right one.
 *
 * Between grab and release the wheel still does something, and it is the
 * interesting direction: scrolling TIGHTER concentrates the earth into a
 * taller mound, down to the narrowest circle it will stand in
 * (`spreadForVolume`). Scrolling wider does nothing — spreading a hand of
 * earth thin over a meadow is a real act and it is not this one.
 */
const digDrop = (scoop: { r: number; volume: number }) =>
  Math.max(spreadForVolume(scoop.volume),
           Math.min(focusRadius, focusCap, scoop.r));

/** What the hold has dug by now — the same function the act will be priced by. */
/**
 * What the hold has dug by now — the same pure function the act will be priced
 * by, so the ring the player is watching and the bill they are about to pay
 * cannot disagree.
 *
 * It takes NO timestamp. The first version passed one, and the two call sites
 * passed different clocks: the release read `performance.now()` while the
 * per-frame preview passed the rAF timestamp. In a browser those are the same
 * clock and it worked; under a pumped rAF they diverged and the preview showed
 * an empty pit above a hold that was nearly full. One measurement, one source.
 *
 * And it must be REAL seconds, never `clock.t`: world time runs at 192× and is
 * a property of the world, while how long a key has been held is a fact about
 * a hand. Law 7 binds `src/sim/`; this is the renderer, and the wall clock is
 * the only honest answer here.
 */
const digScoop = (d: Dig) =>
  scoopFor(d.r, sim.pantheon.player.power,
           Math.min(1, (performance.now() - d.at) / (DIG_HOLD * 1000)),
           godhoodWeb.modifier('grasp.maxMass'));

const doDigTake = () => {
  if (session.dig || session.hold) return;
  const t = hovered;
  if (!t) return;
  // The pit is frozen HERE, at the instant the ground was taken hold of: a
  // scoop that followed the cursor would be an editor's brush, and the gesture
  // is meant to be one hand closing on one place.
  const g = groundAt(t.x, t.z);
  if (region.heightAt(t.x, t.z) <= 0) { notice('the sea is not yours to move'); return; }
  const away = Math.hypot(presence.x - t.x, presence.y - g, presence.z - t.z);
  if (away > digArm()) {
    // said the Anchor's way — a distance and what would fix it, never an error
    notice(`that ground is ${nice(away)} from you — come nearer, or widen your focus`);
    return;
  }
  // D §9's THE DIVINE HAND, arriving exactly where §29 said it would. The hand
  // is ∛(power × hand / k), so `grasp.maxMass`'s ×4 on mass is ∛4 = 1.59 on the
  // radius — which takes a whole god's hand from 46.4 m to 73.7 m and brings
  // §20's "impossible, for now" 60 × 20 m ridge from 2.16 gods down to 0.54.
  // The unlock needed no rule written for it; it falls out of the same cube.
  const hand = godhoodWeb.modifier('grasp.maxMass');
  const r = Math.min(focusRadius, focusCap,
                     handRadius(sim.pantheon.player.power, hand));
  if (r < 1) { notice('there is not enough of you left to lift a handful'); return; }
  session.dig = { x: t.x, z: t.z, y: g, r, at: performance.now() } satisfies Dig;
  sound.cast('weaken', 0);
};

/** Let go of the ground without moving any: costs nothing, writes nothing. */
const abandonDig = () => {
  if (!session.dig) return;
  session.dig = null;
  notice('you let the ground be');
};

const doDigDrop = () => {
  const d = digNow();
  if (!d) return;
  session.dig = null;
  const scoop = digScoop(d);
  if (!scoopReads(scoop)) {
    // Deliberately not an error: it is the world's own threshold, said in the
    // world's terms. Nothing was written and nothing was spent.
    notice('barely a scuff — hold it longer to take a real handful');
    return;
  }
  const aim = hovered;
  if (!aim) { notice('nowhere to put it'); return; }
  const dropR = digDrop(scoop);
  const r = sim.moveEarth(
    { x: d.x, z: d.z, r: scoop.r, depth: scoop.depth },
    { x: aim.x, z: aim.z, r: dropR },
    clock.t, undefined, { x: presence.x, y: presence.y, z: presence.z }, digArm());
  if (!r.ok) {
    notice(r.reason === 'too-near' ? 'it would fall straight back in — throw it clear'
      : r.reason === 'not-land' ? 'the sea will not hold it'
      : r.reason === 'the-sea-would-come-in' ? 'dig that deep here and the sea comes in'
      : r.reason === 'too-far' ? 'too far from you — come nearer, or widen your focus'
      : r.reason === 'too-weak'
        ? `not enough of you left — ${Math.round(sim.pantheon.player.power * 100)}% of` +
          ` ${Math.round((r.cost ?? 0) * 100)}% needed`
        : 'the ground will not take it there');
    return;
  }
  if (r.eventId) myActs.add(r.eventId);
  // The spectacle is the earth arriving, not the hole leaving: the pit is
  // behind you by now and the mound is what you were looking at.
  verbFx.spawn('weaken', aim.x, groundAt(aim.x, aim.z), aim.z, dropR);
  reticle.pulse('weaken', new THREE.Vector3(aim.x, groundAt(aim.x, aim.z), aim.z), dropR);
  sound.cast('strike', 0.6);
  notice(`the ground rises — ${nice(r.height ?? 0)} of it, where you put it`);
};

addEventListener('keydown', (e) => {
  if (modes.mode !== 'deity') return;   // the hand is the god's, not a mortal's
  if (e.key.toLowerCase() !== 'g' || e.repeat) return;
  // one key, and which gesture it is was decided the moment it went down:
  // a hold that starts on a log and ends over open ground must still be the
  // log's hold, or the two paths cross halfway through
  if (hovered && LIFTABLE.has(hovered.kind)) doGraspTake(); else doDigTake();
});
addEventListener('keyup', (e) => {
  if (modes.mode !== 'deity') return;
  if (e.key.toLowerCase() !== 'g') return;
  if (session.hold) doGraspDrop(); else doDigDrop();
});
// A window that loses focus never sees the keyup. For the object grasp that
// stranded a flag; for a dig it would be a hold that goes on growing while
// nobody is looking and commits an enormous scoop on the next keypress.
addEventListener('blur', abandonDig);
addEventListener('visibilitychange', () => { if (document.hidden) abandonDig(); });

// the armed verb's footprint follows the cursor; the raycast runs at most
// once per frame, in the loop, off the latest remembered pointer position
let rainHeard = 0;
let pointerEvent: MouseEvent | null = null;
stage.addEventListener('pointermove', (e) => { pointerEvent = e; });
stage.addEventListener('dblclick', (e) => {
  if (modes.mode !== 'deity') return;   // a mortal's click takes the mouse, never casts
  if (hud.armed) return;   // an armed palette owns the mouse
  if (presence.wasLookingAround()) return;
  const t = pickAt(e);
  if (t) cast('strike', new THREE.Vector3(t.x, t.y, t.z), t);
});
/**
 * A bare click chooses, and choosing is the whole of attention — the one
 * interaction both bodies share. The card opens; in the deity's body the eye
 * also goes and stands where this kind of thing is worth watching from and
 * moves with it if it moves (`follow`); on foot nothing moves but the card —
 * a mortal looks at a tree from where they stand. There is no second step and
 * no Follow key.
 *
 * **Bare ground and landmarks choose too**, which `isThing` used to refuse.
 * A burnt hillside is a subject — it is one of the few in the game with a
 * century of history to read — and a god that could point at it, be told a
 * fire ran through here, and then not be able to *choose* it was being
 * offered the interesting half and denied the commitment.
 *
 * …but a click AWAY from a chosen thing lets go of it first (2026-08-18,
 * Griffin): with something chosen, a click on nothing or on bare ground
 * closes the card rather than swapping the subject for the hillside under
 * the hand. Ground is still a subject — the next click chooses it.
 */
const observe = (t: Target | null, follow: boolean) => {
  if (chosen && (!t || t.kind === 'ground')) { letGo(); return; }
  choose(t);
  if (!chosen) return;
  if (attention && !follow) attention.following = false;
  // The whisper is the *oldest* thing the world can say about it, because
  // that is the half a player has no other way of finding out — and it is
  // the answer to "why should I care about this one rather than that one",
  // which the game has never before been able to give.
  const told = gist(readAt(sim, chosen.x, chosen.z, clock.t,
                          isThing(chosen) ? { subject: chosen.id } : {}));
  notice(told ? `${describeTarget(chosen)} — ${told}` : describeTarget(chosen));
};
stage.addEventListener('click', (e) => {
  // IN A MORTAL BODY A CLICK CHOOSES AND OBSERVES, AND THAT IS ALL (Griffin,
  // 2026-08-19: "we can observe a tree from mortal form, we just can't use
  // god acts on it"). No verb is ever armed on foot, so there is nothing to
  // cast; the eye does not go to the thing, because the eye is a body
  // standing on the ground. The deity's click grammar below stays the deity's.
  if (modes.mode === 'mortal') {
    if (mortal.wasLookingAround()) return;   // a look round is not a choice
    observe(pickAt(e), false);
    return;
  }
  // turning the eye is not intent: a drag that ends over the land must not
  // spend the armed verb on wherever the release happened to fall
  if (presence.wasLookingAround()) return;
  const verb = hud.armed;
  const t = pickAt(e);

  // GRASP IS NOT A CAST, so it leaves before `cast` ever sees it — which is
  // what `CastVerb` and `Verb` being two types is for: `sim.act('grasp', …)`
  // cannot typecheck, and this branch is the only way grasp is reachable from
  // the palette.
  //
  // One key, two scales, exactly as hold-`G` already decides: a *thing* under
  // the aim is lifted, and bare ground is taken hold of as ground. That is law
  // 20's argument made literal and it is the whole of `DECISIONS.md`'s ruling
  // that Grasp absorbs Earth and Shape — the player never chooses between
  // three verbs, the world decides which scale they meant.
  if (verb === 'grasp') {
    if (session.hold) doGraspDrop();
    else if (session.dig) doDigDrop();
    else if (t && LIFTABLE.has(t.kind)) doGraspTake();
    else doDigTake();
    return;
  }

  if (!verb) { observe(t, true); return; }
  if (t) cast(verb, new THREE.Vector3(t.x, t.y, t.z), t);
});

/**
 * What the player comes to know, as opposed to what happened.
 *
 * The Chronicle was a global feed: every event anywhere, the moment it
 * emitted. With rival gods in the world that is a cheat — you could *read*
 * that another god had acted on the far side of the island, and "can I tell
 * someone else has been here?" would be answered by a list instead of by
 * going and looking. So an event is noticed if these hands spent it, or if
 * it happened in the country the presence was actually over at the time.
 * The judgement is made once, when the event is new: news does not become
 * un-noticed because the god flew away afterwards, and it never becomes
 * noticed later because the god happened to drift back.
 *
 * Awareness widens with height, because a god high over the island is
 * attending to more of it — six kilometres at valley scale, the whole
 * region from the ceiling of an old god.
 */
/**
 * Both world-bound, and both were surviving an adoption: the feed carried the
 * discarded world's news, and the cursor pointed into its event ring — where
 * `findIndex` cannot find it, so `start` falls back to 0 and the whole of the
 * NEW world's ring arrives at once, as though it had all just happened.
 */
const chronicle = session.feed;
const CHRONICLE_KEEP = 400;
const noteEvents = (px: number, pz: number, alt: number) => {
  const all = sim.events.all();
  if (all.length === 0) return;
  // Your own stones, when the Godhood says they report — see the exemption
  // below. Resolved once per call rather than per event: a god has a handful
  // of anchors and this walk can be hundreds of records after an absence.
  const watchedStones = godhoodWeb.hasRule('anchor.perception')
    ? sim.anchors.all().filter((a) => a.godId === sim.pantheon.player.id)
    : [];
  /**
   * WHAT MATTERS TO THE WILD REACHES YOU WHEREVER YOU ARE — *Wild Awareness*
   * (Beasts, transcendence), the same shape as the anchor exemption below and
   * for the same reason: an awareness the Web sold is a rule, and the one
   * place a rule about *knowing* can honestly land is this gate. A great
   * migration, a kind gone from a valley, a kind come back to one — the three
   * wildlife events the HUD will speak at all (salience ≥ 0.7; the rest is the
   * herd log, and `hud.ts` says nothing of it) — pass the distance filter for
   * a god who holds the rule. Nothing about the event changes, and law 10 is
   * untouched: these are natural events and carry no hand to leak.
   */
  const wildAware = godhoodWeb.hasRule('beast.aware.wild');
  const wildNews = (e: PerceivedEvent | { type: string; salience: number }): boolean =>
    wildAware && e.salience >= 0.7
    && (e.type === 'wildlife_migrated' || e.type === 'wildlife_vanished' || e.type === 'wildlife_returned');
  let start = 0;
  if (session.cursor) {
    const i = all.findIndex((e) => e.id === session.cursor);
    start = i >= 0 ? i + 1 : 0;
  }
  const range = awarenessRange(alt);
  for (let i = start; i < all.length; i++) {
    const e = all[i];
    /**
     * SOMETHING YOU LEFT BURNING HAS GONE OUT.
     *
     * `divine_aura_spent` is emitted when an aura can no longer be afforded
     * and gutters, and it had **no consumer that could ever fire**. It carries
     * salience 0.3 against `SALIENT`'s 0.35, so no mortal remembers it — which
     * is right, an aura is an unexplained condition and a condition quietly
     * ceasing is the least witnessable thing in the game. But it also meant
     * the one party who genuinely needs to know was not told either.
     *
     * So it is said to the god directly, before the distance filter, and only
     * for the god's OWN auras. That is not a law-10 breach: nothing in the
     * world learns anything, and this is the god's own ledger of its own hand
     * — the same standing as `myActs`. The HUD's `n/m` count already
     * decrements silently; this is the sentence that says why.
     */
    if (e.type === 'divine_aura_spent' && e.cause.kind === 'divine'
        && e.cause.godId === sim.pantheon.player.id) {
      notice('something you left burning has gone out — there was not enough of you to hold it');
    }
    /**
     * YOU KNOW WHEN SOMETHING HAPPENS AT YOUR STONE, WHEREVER YOU ARE.
     *
     * `anchor.perception` is the Web's own sentence for it, sold by *Anchor*
     * rank II, and `bindings.ts` has called it `absent` — *"a stone holds
     * resonance and reports nothing"* — since it was written. It is a **rule**,
     * so until now it was doubly unreachable: no reader, and no way to read a
     * rule at all.
     *
     * The whole implementation is this one exemption. An anchor is attendance
     * — a god flew down and stood there — and what it buys is that the place
     * goes on reporting after the god has gone. So an event inside the aura
     * radius of one of your own stones passes the distance filter the same way
     * your own acts do. Nothing else changes: the event is unmodified, the
     * Chronicle says exactly what it would have said standing there, and no
     * attribution is added or removed (law 10).
     */
    const nearMine = watchedStones.length > 0 &&
      watchedStones.some((a) => Math.hypot(a.x - e.x, a.z - e.z) <= AURA_RADIUS + e.radius);
    if (!nearMine && !myActs.has(e.id) && !wildNews(e)
        && Math.hypot(e.x - px, e.z - pz) > range + e.radius) continue;
    const seen = perceive(e);
    chronicle.push(seen);
    // …and if it was somebody else's hand, it is also something to LOOK at.
    // Called here rather than over the feed afterwards because the feed is
    // spliced when it passes CHRONICLE_KEEP, so any index into it is a bug
    // waiting for a busy day — and this way there is no cursor to keep, and
    // nothing new that a world adoption would have to clear.
    showOthersAct(seen);
  }
  if (chronicle.length > CHRONICLE_KEEP) chronicle.splice(0, chronicle.length - CHRONICLE_KEEP);
  session.cursor = all[all.length - 1].id;
};

/**
 * ANOTHER GOD'S ACT, SEEN — the half of `PLAYABLE.md` P6 that needs no server.
 *
 * Every visual answer a verb gives has been spawned by `cast()`, which is the
 * function the LOCAL player's click calls. So a rival acting in the same world
 * moved the ground, burned the trees, filed the event and appeared in the
 * Chronicle — and put not one thing on screen. The world would change under
 * you with no sign of anything happening, which is not mystery, it is a
 * missing frame.
 *
 * It reads the perceived feed instead, so what a rival's Strike looks like is
 * decided by exactly the information a witness has: **the event, with its
 * cause already reduced to `unexplained`** (`events.ts` `perceive`). There is
 * nothing here that could leak a hand even if it wanted to — the godId does
 * not survive the trip.
 *
 *  · `myActs` is skipped, because `cast()` has already drawn those and drawing
 *    them twice would double every flash the player makes.
 *  · Only what is close enough to be *seen*, not merely known: the Chronicle's
 *    awareness range is a god's knowledge, and a fire eight kilometres off
 *    should not flash in your face.
 *
 * Law 10 is intact in the strongest form: a rival's bolt and the sky's own
 * bolt are the same picture, because `render/fireFx` and `verbFx` are what
 * both already go through.
 */
const OTHERS_REACH = 4000;
const showOthersAct = (e: PerceivedEvent): void => {
  if (myActs.has(e.id) || e.cause !== 'unexplained') return;
  if (Math.hypot(e.x - presence.x, e.z - presence.z) > OTHERS_REACH + e.radius) return;
  const y = groundAt(e.x, e.z);
  switch (e.type) {
    case 'divine_strike': verbFx.spawn('strike', e.x, y, e.z); break;
    case 'divine_rain': verbFx.spawn('rain', e.x, y, e.z, e.radius); break;
    case 'divine_strengthen': verbFx.spawn('strengthen', e.x, y, e.z, e.radius); break;
    case 'divine_weaken': verbFx.spawn('weaken', e.x, y, e.z, e.radius); break;
    case 'divine_omen': verbFx.spawn('omen', e.x, y, e.z, e.radius); break;
    default: break;
  }
};

const invalidateKeys = (keys: string[]) => {
  for (const k of keys) {
    const [cx, cz] = k.split(',').map(Number);
    chunks.invalidate(cx, cz);
  }
};

function resize() {
  const r = stage.getBoundingClientRect();
  renderer.setSize(r.width, r.height, false);
  camera.aspect = r.width / Math.max(1, r.height);
  camera.updateProjectionMatrix();
}
addEventListener('resize', resize);
resize();

let last = performance.now();
let frames = 0, fpsAcc = 0, fps = 60;
// scratch bitmaps for the per-cell holes, refilled every frame: the horizon
// yields under any mounted chunk, the far canopy only under the ones that grow
// their own trees — so the bare outer band gets planted rather than skipped
const holeOccupancy = new Uint8Array(horizon.holeCells * horizon.holeCells);
const canopyOccupancy = new Uint8Array(farCanopy.holeCells * farCanopy.holeCells);
// the sere's island-wide ceiling field, for the far canopy. Refreshed only
// when the recovering set changes (version tag + store identity, because
// adopting a world swaps the store under the same version number).
// The far canopy's history field — RGBA, one texel per region cell: the
// sere's recovery ceiling, the land's remembered biomass, and how far a god
// has moved the ground. `scratch` is the sere's own single-channel pass.
const canopyField = new Uint8Array(region.n * region.n * 4);
const canopyScratch = new Uint8Array(region.n * region.n);
let lastSereStore: SereStore | null = null;
let lastSereVersion = -1;
// view caches for the habitation renderers
let lastPathsVersion = -1;
let pathViews: PathView[] = [];
const pathPoints = new Map<string, PathView['points']>();
/*
 * `PATH_STEP` used to live here at 20 m. `render/route.ts` owns the sampling
 * now (`ROUTE.step`, measured against the ground's own finest drawable
 * wavelength rather than chosen), and 20 m turned out to be a quarter of the
 * rate that wave needs. Deleted rather than left unused: `noUnusedLocals` would
 * fail the build, and a stale constant beside a live one is how two numbers
 * come to disagree.
 */
// camps: which structures stand together as one place, and how much ground
// they have cleared. Recomputed only when a structure is raised or a hearth
// goes cold — clustering two hundred structures is not a per-frame job.
let campSignature = '';
let campViews: CampView[] = [];
// the eye, for the one renderer that needs to know where it is
const eye = { x: 0, y: 0, z: 0, quaternion: camera.quaternion };
const wildlifeViews: WildlifeView[] = [];
const LAND_SPECIES: WildlifeView['species'][] =
  ['grazer', 'browser', 'small_game', 'predator', 'scavenger'];
/** Social cells swept for herds each way from the presence — 4 is ~4.6 km. */
const WILD_CELLS = 4;
/** Herds drawn at once, nearest first. Was 40, and the gate meant it never bound. */
const WILD_VIEWS = 96;
/**
 * How far a body of a given length is worth drawing, as a multiple of its own
 * size — `nearDetail`'s angular convention, and the same ~3 px bar
 * `tools/settlement.ts` holds every other mark to. A 1.6 m grazer carries to
 * ~540 m and a 0.4 m rabbit to ~135, which is the honest difference between
 * them rather than one distance for everything that breathes.
 */
const WILD_REACH = 340;
/** Which hearths are somebody's, this frame — the pick reads it to name them. */
let litHearths = new Set<string>();
/**
 * What the pointer is over, and what the god has chosen. Both are pure
 * observation: no verb, no write, no history. A chosen thing survives the
 * pointer wandering off it, which is what makes it a *choice* rather than a
 * hover, and it is what a later "make this place sacred" will act on.
 */
let hovered: Target | null = null;
let chosen: Target | null = null;

/* ── selection is attention, and attention is follow ───────────────────── */

/**
 * The eye's attachment to what the god chose.
 *
 * There is no Follow key. `F` walked the roster and `shift-F` walked the bands
 * — press a key, arrive at a stranger — which is an omniscient entity browser
 * however good the camera on the end of it is. **You cannot care about
 * something you have never found**, so the only way to a subject is now the
 * spatial one: fly, notice, point, choose. What used to be Follow is this: the
 * camera behaviour that selection has always deserved, with the teleporter
 * taken off the front of it.
 *
 * `ui/observe.ts` owns the arithmetic — how close you stand to a thing of this
 * kind, and where the eye drifts while it watches. This owns the state: which
 * side the eye is on, how long it has been watching, whether the player has
 * taken the controls back, and whether the subject is still there at all.
 */
interface Attention {
  /** Real seconds since the choice. */
  since: number;
  framing: Framing;
  /**
   * False once the player has carried the eye's target away — a pan key, a
   * pan drag — and the eye has let go; the card stays. Orbiting and zooming
   * about the subject keep it: `DeityPresence.moving` is the whole rule.
   */
  following: boolean;
  /**
   * True once the eye has settled at the framing distance for this kind of
   * subject, or the hand took the wheel first. Until then the approach eases
   * the distance and the elevation; after it, the distance is the player's.
   */
  framed: boolean;
  /** Which way a travelling subject is going, smoothed. */
  heading: { x: number; z: number } | null;
  /** Where it was last frame, so `heading` can be measured rather than asked. */
  wasAt: { x: number; z: number } | null;
  /**
   * Set once the subject is no longer there to watch. The card says so and the
   * eye lets go; **nothing is silently substituted**, which is the whole
   * difference between this and a roster that always has a next entry.
   */
  lost: string | null;
}
let attention: Attention | null = null;

/**
 * Choose something, or choose nothing.
 *
 * The one interaction in the game that changes nothing at all — no event, no
 * stamp, not one dirty chunk. OBSERVATION MUST NOT CREATE HISTORY, and law 15
 * extends it to exactly this: watching, following and caring are free. Only
 * consequence is stored.
 */
const choose = (t: Target | null) => {
  chosen = t;
  if (!t) { attention = null; return; }
  const framing = framingFor(t.kind, t.size);
  attention = {
    since: 0,
    framing,
    following: true,
    framed: false,
    heading: null,
    wasAt: null,
    lost: null,
  };
};

/** Let go of what the eye is holding. Escape's chain, and adopting a world. */
const letGo = () => {
  chosen = null;
  attention = null;
  flyTarget = null;
  arrival = null;
  arrivalPending = null;
};

/**
 * The subject is gone, and the world says so once.
 *
 * **Nothing is ever silently substituted.** A roster always has a next entry
 * and that is precisely what made the old Follow key wrong: press F over a
 * corpse and you are watching a stranger, and the moment that mattered — the
 * one this whole system exists for — passes without being marked. A life
 * ending, a herd walking out of sight and a band dividing are content.
 */
const lose = (why: string) => {
  if (!attention || attention.lost) return;
  attention.lost = why;
  attention.following = false;
  notice(why);
};

/**
 * Where the chosen thing is this frame, and whether it is still there.
 *
 * Everything the pointer can resolve carries an identity that survives being
 * looked away from — a simulation id, or, for baseline content, its own
 * position (law 1: a tree's position *is* its name). So the subject is
 * re-found rather than remembered, and a thing that has stopped existing is
 * discovered to have stopped rather than assumed to be fine.
 *
 * Read-only from end to end. Law 9, and law 15's other half: attention creates
 * no history.
 */
const trackChosen = () => {
  const c = chosen, a = attention;
  if (!c || !a || a.lost) return;
  switch (c.kind) {
    case 'person': {
      // their own scattered spot when the renderer is drawing them, the party's
      // ground when it is not — watching must survive the eye pulling out past
      // the range figures are drawn at. `locatePerson` is that rule, and INHABIT
      // seats the mortal body through the very same call.
      const at = locatePerson(c.id);
      if (!at) { lose('this life has ended'); return; }
      c.x = at.x; c.y = at.y; c.z = at.z;
      break;
    }
    case 'herd': {
      const v = wildlifeViews.find((w) => `herd:${w.species}:${w.seed}` === c.id);
      // …and only while the eye was actually trying to keep up. A god who flew
      // off on purpose has not lost the herd, it has left it, and being told
      // otherwise would be the chrome narrating the player's own decision.
      if (!v) { if (a.following) lose('the herd has moved beyond your sight'); return; }
      c.x = v.x; c.z = v.z; c.y = groundAt(v.x, v.z);
      break;
    }
    case 'animal': {
      const herd = c.id.slice('animal:'.length, c.id.lastIndexOf(':'));
      const v = wildlifeViews.find((w) => `${w.species}:${w.seed}` === herd);
      if (!v) { if (a.following) lose('it has gone where you cannot see it'); return; }
      const i = Number(c.id.slice(c.id.lastIndexOf(':') + 1));
      // the herd thinned as the eye pulled out, and this body is no longer one
      // of the ones being drawn — it is not dead, it is indistinguishable
      if (!Number.isFinite(i) || i >= bodiesInView(v)) {
        if (a.following) lose('you have lost it among the others');
        return;
      }
      const b = bodyAt(v, i, drawnAtSeconds, groundAt);
      c.x = b.x; c.y = b.y; c.z = b.z;
      break;
    }
    case 'structure':
      if (!sim.bands.structures().some((s) => s.id === c.id)) lose('nothing stands here any more');
      break;
    case 'anchor':
      if (!sim.anchors.all().some((s) => s.id === c.id)) lose('the stone is gone');
      break;
    case 'relic': {
      /**
       * A relic a god has CARRIED does not stop existing — it stands somewhere
       * else, and the eye should follow it there. That is the difference
       * between a relic and litter: a twig lifted out of the world is gone
       * from where it was and nobody could pick it out again, while a standing
       * stone is a landmark whatever ground it ends on.
       */
      const moved = sim.carried.at(c.id.slice('relic:'.length));
      if (moved) { c.x = moved.x; c.z = moved.z; c.y = groundAt(moved.x, moved.z); }
      break;
    }
    case 'litter':
      // the lattice grows a stone at that cell for ever; the carried store is
      // the record that it is no longer lying there
      if (sim.carried.at(c.id.slice('litter:'.length))) lose('it is not lying there any more');
      break;
    case 'trail':
      if (!sim.bands.paths().some((q) => q.id === c.id)) lose('the way has gone back to grass');
      break;
  }

  // Which way it is going, MEASURED rather than asked for: a party has a target
  // and a herd has a migration pressure, but an animal's drift is the
  // renderer's own and there is nothing to ask. One rule for all three, and it
  // is heavily smoothed — a heading that tracked every frame's jitter would
  // swing the eye around a grazing deer.
  if (travels(c.kind)) {
    if (a.wasAt) {
      const dx = c.x - a.wasAt.x, dz = c.z - a.wasAt.z;
      const d = Math.hypot(dx, dz);
      if (d > 0.03) {
        const k = 0.05;
        a.heading = a.heading
          ? { x: a.heading.x + (dx / d - a.heading.x) * k,
              z: a.heading.z + (dz / d - a.heading.z) * k }
          : { x: dx / d, z: dz / d };
      }
    }
    a.wasAt = { x: c.x, z: c.z };
  }
};

/* ── the arrival ───────────────────────────────────────────────────────── */

/**
 * The eye wakes and looks around (`ui/arrival.ts`; Design III §162–§164).
 *
 * `arrivalPending` is set the moment a world is opened over — the prehistory
 * landing, or the one-band island when there is none — and the tour is planned
 * on the FOLLOWING frame rather than then and there, because the herds the
 * arrival may point at are `wildlifeViews`, which the frame rebuilds around
 * wherever the presence now stands; planning against the previous standpoint's
 * herds would offer a herd the eye cannot see.
 *
 * Nothing here writes: `gatherHooks` reads the same reader-only source
 * `readAt` does, the tour is a pose and a sentence a frame, and
 * `test/arrival.ts` holds the whole of it to a byte-identical snapshot.
 */
let arrival: Arrival | null = null;
let arrivalPending: { x: number; z: number } | null = null;

/** Seconds for the eye to rise to its vantage. Slow: this is waking, not flying. */
const ARRIVAL_TAU = 5.5;

const planArrivalNow = () => {
  const home = arrivalPending!;
  arrivalPending = null;
  // the vantage's ground is read off the REGION field, because that is the
  // field `canSee` marches: the fine surface sits metres off it, and a
  // vantage fifteen metres under the sight-line's own ground drops a ruin
  // that is just clear of a ridge (seen live, 2026-08-15)
  const eye = vantageOver(home, Math.max(region.heightAt(home.x, home.z), 0), presence);
  const hooks = gatherHooks({
    sim, landmarks, herds: wildlifeViews, now: clock.t,
    events: sim.events.all(),
    // herds far from equilibrium right now — gathering onto ground that draws
    // them, thinning on a stripped range (ROADMAP §42's `stirring()`, a pure
    // read of today's ledger, strongest first)
    stirring: sim.wildlife.stirring(),
    // only a fall the ground actually carries white water on (§43): 101 of
    // the island's 161 fall landmarks; the rest are hillside reaches
    drawnFalls: falls.drawnIds,
    heightAt: (x, z) => region.heightAt(x, z),
  }, { home, eye });
  const glances = planArrival(hooks, presence, presence.yaw);
  arrival = beginArrival(glances, eye);
};

/**
 * One frame of looking around. **A hand on any control ends it at once** —
 * the flight keys, a drag, the captured mouse moving, the wheel, choosing a
 * thing, arming a verb, a Chronicle line — and ends it silently: the eye is
 * simply the player's from that frame, wherever it was pointing.
 */
const stepArrivalNow = (dt: number) => {
  const a = arrival;
  if (!a) return;
  const p = presence;
  const hand = p.moving || p.input.lift !== 0 ||
    p.dragging || p.steering || performance.now() < focusTouched + 200 ||
    chosen !== null || hud.armed !== null || flyTarget !== null || !!session.dig || !!session.hold;
  if (hand || a.done) { arrival = null; return; }

  const step = stepArrival(a, dt, groundAt);
  if (step.say) notice(step.say, step.hold * 1000);

  // rise to the vantage — never faster than this god can fly, never into the
  // hill, never above what reach permits: `stepAttention`'s own walls
  const v = a.vantage;
  const k = 1 - Math.exp(-dt / ARRIVAL_TAU);
  let nx = p.x + (v.x - p.x) * k, ny = p.y + (v.y - p.y) * k, nz = p.z + (v.z - p.z) * k;
  const clearance = Math.max(0, p.y - Math.max(groundAt(p.x, p.z), 0));
  const cap = Math.min(clamp(12 + clearance * 0.9, 12, god().maxAltitude * 1.2) * 3, 30000) * dt;
  const stepLen = Math.hypot(nx - p.x, ny - p.y, nz - p.z);
  if (stepLen > cap && stepLen > 0) {
    const s = cap / stepLen;
    nx = p.x + (nx - p.x) * s; ny = p.y + (ny - p.y) * s; nz = p.z + (nz - p.z) * s;
  }
  const g = Math.max(groundAt(nx, nz), 0);
  ny = clamp(ny, g + EYE_HEIGHT, g + god().maxAltitude);
  // the eye moves as a whole and keeps what it was looking at; then the gaze
  // eases toward the glance, which turns the orbit about the standing eye
  p.placeEye({ x: nx, y: ny, z: nz }, { x: p.tx, y: p.ty, z: p.tz });
  if (step.look) p.easeLookToward(step.look.x, step.look.y, step.look.z, dt, 1.1);
};

/**
 * Seconds for the eye to close most of the distance to where it wants to
 * stand. Slow enough that arriving is visibly travel; fast enough that
 * choosing something across the valley is not a chore.
 */
const OBSERVE_TAU = 1.5;

/**
 * Hold the eye's target on the chosen subject.
 *
 * Choosing something moves the eye: the orbit's target eases onto the subject
 * and the distance and elevation ease toward how far off, and how high, this
 * kind of thing is worth watching from (`ui/observe.ts`'s framing). The eye
 * keeps the side it is already on — attention moves toward a thing from where
 * it happened to be, never from a canonical angle, which is most of what makes
 * the approach read as travel rather than as a cut. If the subject moves, the
 * target moves with it, and the hand may still orbit and zoom about it.
 *
 * **PLAYER INTENT WINS, AT ONCE.** The moment the hand carries the target
 * itself — a pan key, a right-button drag — the eye lets go and does not come
 * back (`DeityPresence.moving`; there is no envelope to fly out of any more).
 * The subject stays chosen and its card stays open, because deciding to look
 * elsewhere is not deciding to stop caring; Escape lets go of both.
 *
 * **It is never a teleport.** The target's step is capped at the speed this god
 * can carry it, so distance costs time and the player keeps the sense of
 * having been over there and now moving toward this.
 */
const stepAttention = (dt: number) => {
  const c = chosen, a = attention;
  if (!c || !a) return;
  a.since += dt;
  if (!a.following) return;

  const p = presence;
  // MOVING LETS GO OF THE WHOLE THING now (2026-08-18, Griffin: "moving or
  // clicking away cancels the selection") — not only the follow. Walking off,
  // panning off, lifting off: the card closes and the god is on their own.
  if (p.moving || p.input.lift !== 0) { letGo(); return; }
  // …and a drag while following is the player looking AWAY: the follow ends,
  // the choice stands (the card stays open until a move or a click elsewhere)
  if (p.steering) { a.following = false; return; }

  // the target eases onto the subject — never faster than this god carries
  // it, which is the pan speed at this distance, surging
  const cap = Math.min(clamp(12 + p.r * 0.55 * p.speedFromAltitude, 12, god().maxAltitude * 1.2)
                       * p.speed * 3, 30000) * dt;
  const k = 1 - Math.exp(-dt / OBSERVE_TAU);
  let nx = p.tx + (c.x - p.tx) * k, ny = p.ty + (c.y - p.ty) * k, nz = p.tz + (c.z - p.tz) * k;
  const step = Math.hypot(nx - p.tx, ny - p.ty, nz - p.tz);
  if (step > cap && step > 0) {
    const sc = cap / step;
    nx = p.tx + (nx - p.tx) * sc; ny = p.ty + (ny - p.ty) * sc; nz = p.tz + (nz - p.tz) * sc;
  }
  p.hold(nx, ny, nz);

  // …and the eye stands off at the framing, until it has arrived or the hand
  // has taken the wheel: after that the distance is the player's own
  if (!a.framed) {
    const f = a.framing;
    const arrived = p.frameToward(Math.hypot(f.standoff, f.height),
                                  Math.atan2(f.height, f.standoff), dt, OBSERVE_TAU);
    if (arrived || p.steering || performance.now() < focusTouched + 200) a.framed = true;
  }
};

/* ── what a chosen thing has lived through ─────────────────────────────── */

/**
 * The reading, rebuilt only when the choice or the world-day changes.
 *
 * A reading costs a measured 9.6 µs (`tools/lore.ts`), which is nothing on its
 * own and is still not worth spending sixty times a second on an answer that
 * cannot change inside a world-day: every store it reads — the sere, promotion,
 * the Chronicle, anchors, structures, paths — advances on the day grid.
 */
interface PlaceReading {
  /** The country it stands in, and how far out from the presence. */
  of: string;
  world: LoreLine[];
  yours: LoreLine[];
  /** Set when the reading is a diff: the world-year the god last acted here. */
  sinceYear?: number;
}
let placeCache: { key: string; day: number; place: PlaceReading } | null = null;

/**
 * How far back "since you were last here" reaches.
 *
 * It is deliberately keyed on the god's own DEEDS and never on its gaze. Law 9
 * and law 15 are explicit that looking, hovering, following and flying write
 * nothing, so there is no record of when the player last *saw* a place and
 * there must not be — but there is a perfect record of when they last put a
 * hand on it, and it is already in the Chronicle as `Strand.last`. That is the
 * better question anyway: the loop the game is built around is care →
 * intervene → leave → return, and the thing worth diffing against is the
 * intervention.
 */
const lastTouchedHere = (t: Target | null, x: number, z: number): number | undefined => {
  const me = sim.pantheon.player.id;
  let last: number | undefined;
  for (const s of sim.chronicle.all()) {
    if (s.godId !== me) continue;
    const mine = t && isThing(t)
      ? s.subject === t.id
      : Math.hypot(s.x - x, s.z - z) <= CHRONICLE_CELL;
    if (!mine) continue;
    last = last === undefined ? s.last : Math.max(last, s.last);
  }
  return last;
};

/**
 * What this thing, and the ground it stands on, have lived through.
 *
 * The card's third question — *what has happened?* — and the one the game had
 * no way of answering at all before `sim/lore.ts`: a tree three casts and a
 * century deep inspected identically to its untouched neighbour, and a burn
 * that took a thousand hectares sixteen years ago said nothing whatever.
 *
 * `world` and `yours` come back as two lists and are never merged. The first is
 * what anyone standing here could come to know; the second is the god's own
 * record of its own hand, and law 10 is the wall between them.
 */
function readingFor(t: Target | null, eye: { x: number; y: number; z: number }): PlaceReading {
  const px = eye.x, pz = eye.z;
  const x = t ? t.x : px, z = t ? t.z : pz;
  /**
   * How big a place this reading is about. `reachFor` owns the rule and
   * `tools/lore.ts` measured it — notably, borrowing the picker's own `far *
   * 0.010` was tried first and does nothing: at three kilometres it leaves the
   * panel silent over a camp 57.5% of the time.
   */
  const far = Math.hypot(x - px, z - pz, (t ? t.y : groundAt(px, pz)) - eye.y);
  const reach = reachFor(far);
  const day = Math.floor(clock.t / DAY);
  // Bare ground and landmarks are re-keyed by POSITION rather than by id: a
  // landmark's id is the whole mountain, and two hundred metres of its flank
  // are two different places with two different histories. Quantised to the
  // sere's own 32 m so a wandering gaze does not rebuild every frame.
  const key = t && isThing(t) ? `${t.kind}:${t.id}`
    // the reach is part of the key: the same ground read from 3 km and from
    // 30 m is two different questions and two different answers
    : `at:${Math.round(x / 32)},${Math.round(z / 32)}@${Math.round(reach / 25)}`;
  if (placeCache && placeCache.key === key && placeCache.day === day) return placeCache.place;

  const me = sim.pantheon.player.id;
  const subject = t && isThing(t) ? t.id : undefined;
  // A return reading is the diff; a first reading is the whole story. The
  // player gets the diff only where there is genuinely a `before` to diff
  // against — a hand of theirs, on this, at some point in the past.
  const touched = lastTouchedHere(t, x, z);
  const whole = readAt(sim, x, z, clock.t, { subject, godId: me, reach });
  /**
   * A return needs an ABSENCE. Without this the heading fired the instant a
   * cast landed — "since you were last here", seconds after you were here —
   * which is the opposite of the beat it exists for.
   *
   * `FORGET_AFTER` is the horizon rather than a number of its own: one world
   * year is what the Chronicle itself calls long enough to stop keeping
   * something that came to nothing, so it is the world's own answer to "how
   * long is a while". At the shipped 192× that is thirty-six real hours; at
   * `D §4`'s 10,000× it is fifty-two real minutes, and the sentence stays true
   * at both because it is written in world time.
   */
  const away = touched !== undefined && clock.t - touched >= FORGET_AFTER;
  const since = away && touched !== undefined
    ? readAt(sim, x, z, clock.t, { subject, godId: me, since: touched, reach })
    : null;
  // …and only where something actually happened while you were gone. A "since
  // you were last here" heading over an empty list is a worse answer than the
  // story, and a full one is just the story with the beginning cut off.
  const use = since && !isSilent(since) && since.world.length < whole.world.length
    ? since : whole;

  /*
   * …and then the same fact is said once.
   *
   * A reading is assembled from six stores that overlap on purpose: the
   * Chronicle folds a durable `mark` onto the neighbourhood at the same time as
   * the store that owns the thing keeps its own note about it. Both are true
   * and one of them is always better — the owning store's note carries the
   * *state*, the mark carries only that it happened. So a stone read:
   *
   *     y100  this ground was made to matter
   *     y100  this ground was made to matter and nobody has found it
   *
   * which is the card saying one thing twice and getting shorter the second
   * time. The trail case was already handled here, one pair at a time; this is
   * the same rule stated once for every pair of stores that can both speak.
   *
   * Dropped from the NOTES rather than from the rendered lines: `saying()`
   * filters out anything it has no words for, so the two arrays do not share an
   * index and filtering afterwards would cut the wrong sentence.
   */
  const SUPERSEDED: Partial<Record<string, Note['kind']>> = {
    anchor_placed: 'stone',
    path_formed: 'walked',
    structure_completed: 'settled',
    camp_abandoned: 'abandoned',
    fire_burned: 'burned',
    thing_promoted: 'apart',
  };
  const told = new Set(use.world.map((n) => n.kind));
  let world = use.world.filter((n) =>
    n.kind !== 'mark' || SUPERSEDED[n.type] === undefined || !told.has(SUPERSEDED[n.type]!));
  /*
   * And the card's own headline is already a sentence about the thing. Being
   * told "a road — walked for generations" and then, underneath, "a road runs
   * past here, walked for generations" is padding rather than history; the
   * stone was worse, because its identity, its state AND its history were three
   * ways of saying *this ground was made to matter*.
   *
   * Only where the subject IS the thing the note is about. The same `stone`
   * note read from the hillside beside it is exactly the sentence that hillside
   * needs, and the same `walked` note is how you find out a road goes past.
   */
  const SELF: Partial<Record<string, Note['kind']>> = { trail: 'walked', anchor: 'stone' };
  const self = t ? SELF[t.kind] : undefined;
  if (self) world = world.filter((n) => n.kind !== self);
  const shown = { ...use, world };
  const said = saying(shown);
  const site = region.siteAt(x, z);
  const out = Math.round(Math.hypot(x - px, z - pz));
  const place: PlaceReading = {
    // The country, and how far out — but never the country twice. Bare ground
    // and landmarks keep the world's own name as the card's heading, so
    // repeating the biome under it is chrome talking to itself ("redwood /
    // REDWOOD · 2803 M OUT"). The test is `isThing`, not `t`: a chosen patch of
    // ground is a Target too.
    // …and for a PERSON, the country alone: how far out they stand is a
    // number about them, which the card's wall withholds anyway — it was
    // withholding it every frame and saying so in the console.
    of: isThing(t)
      ? `${nameGround(site.biome).name}${out > 15 && t?.kind !== 'person' ? ` · ${out} m out` : ''}`
      : out > 15 ? `${out} m out` : 'the ground below you',
    world: said.world,
    yours: said.yours,
    ...(use === since && touched !== undefined
      ? { sinceYear: Math.floor(touched / (DAY * YEAR_DAYS)) } : {}),
  };
  placeCache = { key, day, place };
  return place;
}

/**
 * Which verbs the card prices, and in what order.
 *
 * All five, including the two that are not on the bar. The bar is the *control
 * set* and the card is *knowledge about a subject*: a god who has not been
 * given Rain as an Expression can still be told that this ground would take it
 * badly, and Strike is a double-click rather than a slot but it is still a
 * thing you might do to a tree.
 */
const CARD_VERBS: Verb[] = ['strengthen', 'weaken', 'rain', 'strike', 'omen'];

/**
 * Whose identity IS a place, and whose is not.
 *
 * `world/things.ts` keeps ONE signed standing per `placeKey`, so `things.at`
 * answers *what has happened to this ground*. For a tree or a stone that is
 * the same question as "what has happened to this thing" — position is its
 * name (law 1). For anything that WALKS it is a different question with a
 * plausible-looking answer, and reading it would say a deer was promoted
 * because it happened to be standing on a consecrated oak.
 *
 * People, herds and single animals therefore take no standing from the land.
 * The build has no law-32 ladder for a life, and inventing one out of the
 * ground underfoot would be worse than admitting that.
 */
const STANDS_IN_PLACE = new Set<TargetKind>([
  'plant', 'rock', 'relic', 'litter', 'landmark', 'ground', 'trail',
  'anchor', 'structure',
]);

/**
 * The whole card for the chosen thing: what it is, what is true of it now,
 * what a verb would find in it, what has happened to it, and why this god
 * might care.
 *
 * ONE SELECTED THING = ONE CARD. The subject-specific part is a handful of
 * phrases from `ui/subject.ts` and nothing else — no per-kind panel, no
 * per-kind layout — which is what makes a new thing the world can know a new
 * sentence rather than a new surface.
 *
 * **Priced at the SUBJECT, not at the pointer.** `aimPoint()` reads `hovered`,
 * which is right for the verb bar — a click casts where the mouse is — and
 * wrong here: a chosen thing keeps its card while the pointer wanders off it,
 * and a card that repriced itself as the mouse moved would be quoting a
 * different thing from the one it is naming.
 *
 * A pure read, so law 9 holds: `things.at`, `siteAt`, `env.at` and
 * `anchors.all` are reads — `test/promotion.ts` already proves the first
 * writes nothing — and the rest is arithmetic.
 */
/**
 * THE BEAST READ MODEL, from the card's side — three small resolvers, so the
 * card asks the wild through one seam and the Web through one table.
 *
 *  · `herdCell` — the census cell a drawn herd was dealt from. The view
 *    carries it (`WildlifeView.sx/sz`) because the anchor may lean out of its
 *    cell by design, and `toSocial(v.x, v.z)` would name the neighbour a
 *    sixth of the time at an edge — which is the trend line describing the
 *    wrong herd. Falls back to the anchor for a view without one.
 *  · `beastSight` — which readings this god has earned, read off the Web's
 *    rules once per card. `ruleDepth` for the nodes whose ranks deepen the
 *    reading (Known Range's reach is a `bonus`, Sign on the Ground's flight/
 *    spill and Predator and Prey's prey side are rank II).
 *  · `beastContext` — what the read model cannot know on its own: the clock,
 *    the survey reach the Web bought, the events ring for *lately*, and the
 *    people out hunting each cell, read off the bands (a party's activity and
 *    size are already public; nothing new is simulated to say it).
 */
const herdCell = (v: WildlifeView): { sx: number; sz: number } =>
  v.sx !== undefined && v.sz !== undefined ? { sx: v.sx, sz: v.sz } : toSocial(v.x, v.z);

/** The herd behind a herd or animal target — its kind and census cell — for `Simulation.act`. */
const herdOfTarget = (t: Target): { species: string; sx: number; sz: number } | undefined => {
  if (t.kind !== 'herd' && t.kind !== 'animal') return undefined;
  const key = t.kind === 'herd' ? t.id.slice('herd:'.length)
    : t.id.slice('animal:'.length, t.id.lastIndexOf(':'));
  const v = wildlifeViews.find((w) => `${w.species}:${w.seed}` === key);
  if (!v) return undefined;
  return { species: v.species, ...herdCell(v) };
};

const beastSight = (): BeastSight => ({
  drive: godhoodWeb.hasRule('beast.read.drive'),
  company: godhoodWeb.hasRule('beast.read.company'),
  range: godhoodWeb.hasRule('beast.read.range'),
  movement: godhoodWeb.ruleDepth('beast.trace.movement'),
  compare: godhoodWeb.hasRule('beast.compare'),
  season: godhoodWeb.hasRule('beast.read.season'),
  carrion: godhoodWeb.hasRule('beast.read.carrion'),
  prey: godhoodWeb.ruleDepth('beast.trace.prey'),
  mortal: godhoodWeb.ruleDepth('beast.read.mortal'),
  branch: godhoodWeb.hasRule('beast.read.branch'),
  role: godhoodWeb.ruleDepth('beast.trace.role'),
});

const beastContext = (): BeastContext => ({
  now: clock.t,
  // one ring by default; Known Range widens it a ring per rank — the first
  // content `bonus()` has ever carried (WIRING.md A1)
  reach: 1 + godhoodWeb.bonus('beast.read.reach'),
  events: sim.events.all(),
  huntersAt: (sx, sz) => {
    let n = 0;
    for (const b of sim.bands.bands()) {
      if (b.population <= 0) continue;
      for (const p of b.parties) {
        if (p.activity !== 'hunt') continue;
        const c = toSocial(p.x, p.z);
        if (c.sx === sx && c.sz === sz) n += p.size;
      }
    }
    return n;
  },
});

function cardFor(t: Target | null, eye: { x: number; y: number; z: number }): CardView | null {
  if (!t) return null;
  const read = readingFor(t, eye);
  const now: string[] = [];
  const significance: string[] = [];
  let carries: CardView['carries'];
  /** Only a person has one; every other kind leaves this undefined. */
  let takes: readonly string[] | undefined;
  const site = region.siteAt(t.x, t.z);
  const env = sim.env.at(t.x, t.z, clock.t);
  const stage = sim.sere.stageAt(t.x, t.z, clock.t);

  /* ---- what a verb would find here ---- */
  const cond = { rain: site.rain, biomass: env?.biomass ?? 0, moisture: env?.moisture ?? 0 };
  // the same carrying capacity `simulation.act` scales the real cast by
  const carrying = BIOMES[site.biome]?.density ?? 0.4;
  const focus = focusFor_(isThing(t) ? t : null);
  const away = Math.hypot(presence.x - t.x, presence.z - t.z);
  const verbs: CardVerb[] = CARD_VERBS.map((v) => {
    const cost = costBand(
      costOf(v, focus.radius, away, cond, Infinity, castModsFor(t.kind)).total);
    // `processViability` returns a literal 1 for omen, strike AND rain, and
    // that is not a gap in it — it is law 21. Strengthen and Weaken reinforce
    // or diminish a process that is *already happening*, so they are scaled by
    // whether it is; Rain introduces moisture, Strike introduces its own
    // energy, and Omen touches no process in the land at all. Reading a
    // "purchase" off them would print `strong` for three of the five verbs for
    // ever, which is a number that looks like information and is not.
    //
    // So those three say what is actually true of them instead, and the card
    // ends up teaching the real shape of the verb set: two verbs answer to the
    // land, three do not.
    //
    // …and the three say it in their OWN words. They shared one sentence, and
    // on a card two rows apart that reads as the panel repeating itself rather
    // than as two facts — the same redundancy the reading's own `SUPERSEDED`
    // table exists to kill. What they have in common is that the land does not
    // have to be willing; what they do with that is not the same thing at all.
    if (v === 'omen' || v === 'strike' || v === 'rain') {
      return { verb: v, cost, purchase: 'strong' as const,
               note: v === 'omen' ? 'it changes where minds look, never the land'
                 : v === 'rain' ? 'it brings its own water, wherever it falls'
                   : 'it brings its own fire — but something here has to take it' };
    }
    const via = processViability(v, cond, carrying);
    // `VIABILITY_FLOOR` is 0.25 — at the floor nothing this verb acts on is
    // happening here at all — and the three words above it split what is left.
    //
    // MEASURED — and the instrument is GONE. `tools/viability.ts` was written
    // for exactly this calibration and deleted on 2026-08-16 once it had
    // produced the cut set below. The numbers are real and were taken through
    // this expression; what cannot be done any more is REPRODUCE them, so if
    // these thresholds are ever re-tuned the probe has to be rewritten first
    // rather than the constants nudged. `CLAUDE.md`: measure before tuning,
    // and a measurement with no surviving instrument is a citation, not a
    // measurement.
    //
    // `tools/viability.ts` (2026-08-15): 20,000 land points, every
    // camp, trail vertex and landmark on the shipped island, plus 3,000 points
    // round the opening camp, through this exact expression. Strengthen and
    // Weaken are the same function of the ground below omen/strike/rain, so
    // one cut set serves both. The land is a lump at 0.31–0.47 (desert 0.32,
    // then chaparral/grassland/scrub — ~60% of land), a real trough at
    // 0.48–0.55 (3.8%), and a long tail (oak 0.58, conifer 0.68, riparian
    // 0.74, redwood 0.91). So: 0.35 is a knee — desert, alpine and beach sit at
    // 0.31–0.33 and 0.34–0.35 is nearly empty — and `none` becomes the word
    // for ground whose biome carries nothing as well as for ground a Weaken or
    // a burn has stripped (the old 0.30 was unreachable on the untouched
    // island, and a word that never appears carries no information). 0.50 is
    // the trough between the dry lump and oak. 0.75 is NOT a knee — 0.70–0.80
    // is flat — it is three-quarters of full effect, which is what `strong`
    // should mean; 0.80 would only move riparian and conifer's upper half.
    // Prints, over land: none 12% · little 53% · some 21% · strong 14%; near
    // the opening camp, which is wet redwood country, 0 / 0 / 18 / 82. Land
    // quartiles were rejected: they slice the chaparral lump so two adjacent
    // hillsides read different words.
    const purchase = via <= 0.35 ? 'none' as const
      : via < 0.50 ? 'little' as const
        : via < 0.75 ? 'some' as const : 'strong' as const;
    return { verb: v, cost, purchase };
  });

  /* ---- law 32's ladder, as far as the build can honestly place a thing ---- */
  // Consecrated and Mythic have no store yet, so an anchor's own stage stands
  // in where there is one. Nothing here invents a rung the simulation cannot
  // back.
  const mark = STANDS_IN_PLACE.has(t.kind) ? sim.things.at(t.x, t.z, clock.t) : null;
  let standing: CardStanding =
    mark && Math.abs(mark.standing) >= PROMOTE ? 'promoted' : 'ordinary';
  if (t.kind === 'anchor') {
    const a = sim.anchors.all().find((q) => q.id === t.id);
    if (a && (a.stage === 'shrine' || a.stage === 'temple')) standing = 'consecrated';
    else if (a && a.stage !== 'bare') standing = 'promoted';
  }

  switch (t.kind) {
    case 'person': {
      const band = sim.bands.bands().find((b) => b.people.some((q) => q.id === t.id));
      const person = band?.people.find((q) => q.id === t.id);
      if (!band || !person) {
        /**
         * THE CARD OF SOMEBODY WHO IS GONE. It used to keep the name and say
         * nothing — a header over an empty card, for the one subject the
         * player had chosen to watch. What the world knows is said instead:
         * the death itself while the ring still holds it (a world-day or so
         * of events), and after that the people who lost them — the `lost`
         * records `BandSystem.mourn` wrote, which outlive the event by a life
         * — phrased as what a neighbour could tell you. Somebody nobody lost
         * and no record holds is simply not to be found; the world does not
         * say died about a person it cannot prove dead.
         */
        const died = sim.events.all().find((e) => e.type === 'person_died' && e.data?.id === t.id);
        const said = died ? phrase(perceive(died)) : null;
        const mourners: string[] = [];
        for (const b of sim.bands.bands()) {
          if (b.population <= 0) continue;
          for (const q of b.people) {
            const l = q.lost?.find((x) => x.id === t.id);
            if (!l) continue;
            mourners.push(l.tie === 'parent' ? `${q.name} lost a parent`
              : l.tie === 'child' ? `${q.name} lost a child`
              : `${q.name} lost the one they worked beside`);
            if (mourners.length >= 3) break;
          }
          if (mourners.length >= 3) break;
        }
        if (said) now.push(said);
        else if (mourners.length) now.push(`${t.name} died`);
        else now.push('not to be found');
        now.push(...mourners);
        break;
      }
      const party = band.parties.find((q) => q.id === person.partyId);
      /** Which truth about this person the player is currently asking for. */
      const reg: Register = REGISTER_OF[focusFor_(null).band];

      /**
       * THE SUBJECT AT THIS SCALE — the whole of semantic scroll, on one card.
       *
       * The thread does not change: it is still this person, still their name
       * at the top, still the card the click opened. What changes is which
       * truth about them is currently in front of the player. Close, she is
       * herself. Pull out and she is one of a working party. Wider, she is a
       * member of a band with needs and a decision. Wider still, she is one of
       * a people who bury their dead a particular way, on ground that carries
       * a particular amount of game.
       *
       * Every line below is a read of something the simulation already
       * computed and nothing in the game has ever asked for: `reading()` is
       * `explain()` plus the four things a table of scores cannot say,
       * `practices` are five fields that have drifted for a century with no
       * reader at all, and `practiceDistance` is the metric that says a
       * neighbour has become a stranger.
       */
      if (reg !== 'intimate') {
        const r = sim.bands.reading(band.id, clock.t);
        if (reg === 'encounter') {
          /**
           * The ENCOUNTER is the group around this person, and it is NOT the
           * band. `personCard` already says what they themselves are doing —
           * repeating it here was the first thing the built card got wrong —
           * so what this register adds is the rest of the working group:
           * where the OTHER parties are and what they are at, which is the
           * thing you cannot see from inside one errand.
           */
          const elsewhere = band.parties.filter((q) => q.id !== party?.id);
          if (elsewhere.length) {
            const far = elsewhere.map((q) =>
              `${doingWord(q.activity)}`).filter((w, i, a) => a.indexOf(w) === i);
            now.push(`the rest of them are ${far.join(', ')}`);
          } else if (party) {
            now.push('the whole band is on this one errand');
          }
          if (party?.target &&
              Math.hypot(party.target.x - party.x, party.target.z - party.z) > 25) {
            now.push('this party is walking somewhere');
          }
        }
        if (r && reg !== 'encounter') {
          // The COMMUNITY is where the decision lives: a band decides, parties
          // carry it out. So this is where the table's own answer belongs.
          now.push(`this band is ${doingWord(r.chose)}`);
          now.push(...whyThey(r));
        }
        if (reg === 'landscape') {
          const culture = sim.cultures.get(band.cultureId);
          if (culture) {
            now.push(...peopleNow(culture.name, culture.practices, idSeed(culture.id)));
            // …and the nearest OTHER people, and how far apart they have grown.
            // Divergence has been simulated since §50 and never said out loud.
            const others = sim.bands.bands()
              .filter((b) => b.population > 0 && b.cultureId !== band.cultureId)
              .sort((a, b) => Math.hypot(a.x - t.x, a.z - t.z) - Math.hypot(b.x - t.x, b.z - t.z));
            const near = others.length ? sim.cultures.get(others[0].cultureId) : undefined;
            if (near) {
              const said = apartFrom(near.name, sim.cultures.practiceDistance(culture, near));
              if (said) now.push(said);
            }
          }
        }
      }
      // Names are resolved across EVERY living band, not just this one. A tie
      // that survives a fission is the most interesting kind — half a band
      // walks four kilometres off and somebody is still the person you used to
      // work beside — and a resolver scoped to one band would report exactly
      // those as "gone". Reader-only, and a card is drawn for one subject at a
      // time, so the scan is a few hundred people once per selection.
      const nameOf = (id: string): string | undefined => {
        for (const b of sim.bands.bands()) {
          const q = b.people.find((x) => x.id === id);
          if (q) return q.name;
        }
        return undefined;
      };
      // …and a band to its people, for a story heard from strangers
      // (`PersonMemory.from`): the card says WHOSE people told them
      const peopleOf = (bandId: string): string | undefined => {
        const b = sim.bands.bands().find((x) => x.id === bandId);
        return b ? sim.cultures.get(b.cultureId)?.name : undefined;
      };
      // …and a point to a landmark's name, for where they keep going: the
      // nearest notable thing within the social cell's own half-width
      const placeOf = (x: number, z: number): string | undefined =>
        nearestLandmark(landmarks, x, z, () => 520)?.name;
      const p: HudPerson = describe(person, band, party,
                                    sim.cultures.get(band.cultureId), nameOf, peopleOf, clock.t,
                                    placeOf);
      // A person's card is assembled by `lives.ts`, the only place that knows
      // how to say a life without measuring it (`test/follow.ts`).
      /**
       * A PERSON'S OWN DETAIL RECEDES AS THE ATTENTION WIDENS.
       *
       * `personCard` returns six-odd lines about one life — what they are
       * doing, where, how they are holding up, whose people, since when, and
       * who matters to them. At the intimate register that IS the card. At a
       * valley's width it is the wrong half: the player has asked about
       * something larger and the card would answer with eleven lines of
       * biography and three of process.
       *
       * The thread is never lost — their name and their seal hold the header
       * at every register, and the first line (what they are doing right now)
       * stays, because it is the hook that ties the wider truth back to the
       * person who led you to it. Everything below it is intimate-register
       * detail and is offered where intimacy is what was asked for.
       */
      const mine = personCard(p);
      now.push(...(reg === 'intimate' ? mine
        : reg === 'encounter' ? mine.slice(0, 2) : mine.slice(0, 1)));
      /**
       * WHAT STRENGTHEN WOULD TAKE HOLD OF, and it is not the same question at
       * every scale.
       *
       * Close, it is what this work asks of *these hands* — `asked(activity)`,
       * the same mapping `press` and `suitability` read, so what the card names
       * is exactly what a cast would move. That has been right since the verb
       * reached people and it is unchanged.
       *
       * Scrolled out, the expression is no longer hers. What is being expressed
       * is the *work*, and a cast out here presses on whoever is doing it. So
       * the card offers the process rather than the trait — because a wide
       * Strengthen is a different intervention and not the close one with a
       * bigger radius, and offering *their curiosity* while the cast lands on
       * eleven people would be the panel promising a precision the world is
       * not going to honour.
       */
      takes = reg === 'intimate' ? p.takes
        : reinforceable(reg, sim.bands.reading(band.id, clock.t),
                        // at the encounter register the process is the PARTY's
                        // errand, not the band's strategy — this person is in a
                        // camp party while the hunt is out, and a card offering
                        // the hunt would be pointing at people who are not here
                        reg === 'encounter' ? party?.activity : undefined);
      carries = {
        // their own witness leads; the band's memory is the fallback, and
        // saying which is which is the difference between a person who was
        // there and a person who was told
        heading: p.remembers.some((r) => r.how) ? 'what they carry'
                                                : 'what the band remembers',
        lines: p.remembers,
      };
      break;
    }
    case 'herd': {
      // the COUNT is read live rather than off the pick: a herd is a census
      // number that migration moves every day, and the card is open for far
      // longer than the instant the pointer resolved it
      const v = wildlifeViews.find((w) => `herd:${w.species}:${w.seed}` === t.id);
      if (v) {
        const reg: Register = REGISTER_OF[focusFor_(null).band];
        const cellOf = herdCell(v);
        const sight = beastSight();
        /**
         * THE LOCAL POPULATION — the larger subject a herd resolves into
         * past its own footprint, for a god who has bought *Population as
         * Subject*. Scroll out from a herd and the card stops describing
         * these animals and starts describing the grazers of this country:
         * how much ground their kind holds around here, whether it is
         * filling or emptying, where it is worn, hunted, shared, lost. The
         * thread is still this herd — its name at the top, its seal — and
         * the same wheel that widens a person into their band widens a herd
         * into its population. Nothing is stored and nothing is an entity:
         * `populationOf` is a reading of the ledger the way `stirring()` is.
         */
        if (reg !== 'intimate' && godhoodWeb.hasRule('beast.subject.population')) {
          const pop = populationOf(sim.wildlife, cellOf.sx, cellOf.sz, v.species, beastContext());
          now.push(...populationNow(pop));
          if (godhoodWeb.hasRule('beast.read.territory')) {
            // the population's ground as a thing with edges — the same
            // reading, its territorial half, said only when it says something
            const edges: string[] = [];
            if (pop.latelyLost > 0 && pop.returned === 0) edges.push('their ground has been shrinking');
            if (pop.returned > 0 && pop.latelyLost === 0) edges.push('their ground has been spreading');
            if (pop.sharedWithHunters > pop.cells.length / 2) edges.push('hunters stand on most of the ground they hold');
            now.push(...edges);
          }
          break;
        }
        now.push(...herdNow(v.species, v.count, { spread: herdSpread(v.count) }));
        /**
         * WHAT A BEAST GOD IS TOLD, and what everyone else is.
         *
         * Everyone: the herd's count and its trend — `cellView` and `rangeAt`,
         * wired on 2026-08-17 (`WIRING.md` B4), one `Map.get` each, and shown
         * past the intimate register because at that scale the card describes
         * a population rather than bodies. That is unchanged and is what God A
         * sees.
         *
         * A god who has bought into Beasts sees the SAME herd through the read
         * model — `sim/wildlife/reading.ts`, one seam, computed once — and only
         * the readings its rules have earned (`beastSight`). *Read the Wild*
         * replaces the trend line with what the herd is doing about its ground
         * (it says the wear too, so nothing is lost); every node past it adds a
         * question the card can now answer. Same world, more causal structure
         * legible; the Web changes what a selection MEANS.
         */
        if (sight.drive) {
          const r = readBeast(sim.wildlife, cellOf.sx, cellOf.sz, v.species, beastContext());
          now.push(...beastNow(r, sight, clock.t));
          takes = reinforceableBeast(r.drive, v.species, {
            movement: godhoodWeb.hasRule('beast.press.movement'),
            increase: godhoodWeb.hasRule('beast.press.increase'),
            hunt: godhoodWeb.hasRule('beast.press.hunt'),
          });
          // a Beast god who has not yet bought a press still leans on the
          // ground under the herd — say so, rather than "nothing to take"
          if (!takes.length && !godhoodWeb.hasRule('beast.press.movement')
              && !godhoodWeb.hasRule('beast.press.increase') && !godhoodWeb.hasRule('beast.press.hunt')) {
            takes = ['the ground under them'];
          }
        } else {
          const cell = sim.wildlife.cellView(cellOf.sx, cellOf.sz, v.species);
          if (cell && reg !== 'intimate') {
            now.push(...herdTrend(cell, v.species.replace(/_/g, ' ')));
          }
          // and what Strengthen would take hold of, for a god without Beasts:
          // the ground the herd stands on — which is exactly what the cast does
          takes = ['the ground under them'];
        }
      }
      break;
    }
    case 'animal': {
      const herdId = t.id.slice(0, t.id.lastIndexOf(':')).replace('animal:', '');
      const v = wildlifeViews.find((w) => `${w.species}:${w.seed}` === herdId);
      now.push(...animalNow(t.about?.species ?? 'grazer', !!v && bodiesInView(v) <= 1));
      // ONE animal is one of the herd, and the simulation knows nothing about
      // it that it does not know about the herd (`render/wildlife.ts` header:
      // the body is a stand-in). So a Beast god reading an animal reads its
      // herd's drive and company — honestly, and nothing finer.
      if (v) {
        const sight = beastSight();
        if (sight.drive) {
          const cellOf = herdCell(v);
          const r = readBeast(sim.wildlife, cellOf.sx, cellOf.sz, v.species, beastContext());
          now.push(...beastNow(r, { ...sight, range: false, compare: false, role: 0 }, clock.t));
        }
      }
      break;
    }
    case 'plant':
      // the plant's own name already carries its height; what the card adds is
      // the ground it is standing in, which is the one thing about a tree the
      // landscape cannot show you from the air — and, since the wake, whether
      // this ground grows an unusual variety of it (law 12: the world is the
      // interface, so a strain is pointed at, never paneled)
      now.push(...plantNow(t.about?.species ?? '', t.about?.height ?? 0,
                           { stage, moisture: env?.moisture }));
      now.push(...strainNow(strainAbout(region, sim.strains,
                                        t.about?.species ?? '', t.x, t.z, clock.t)));
      break;
    case 'structure': {
      const s = sim.bands.structures().find((q) => q.id === t.id);
      // whose roof it is: the nearest living band's culture, at the range a
      // camp actually spreads over. A ruin keeps whoever raised it, which is
      // why the practice comes off the structure and not off the band.
      const band = sim.bands.bands().find((b) =>
        b.population > 0 && Math.hypot(b.homeX - t.x, b.homeZ - t.z) < 220);
      if (s) {
        now.push(...structureNow(s.type, s.condition, litHearths.has(s.id),
          { people: band ? sim.cultures.get(band.cultureId)?.name : undefined }));
      }
      break;
    }
    case 'anchor': {
      const a = sim.anchors.all().find((q) => q.id === t.id);
      if (a) {
        if (a.godId === sim.pantheon.player.id) now.push(...anchorNow(a, clock.t));
        // A rival's stone reads as a place that started mattering and nothing
        // else — law 10 both ways: you cannot see whose hand it was either.
        else now.push('this ground was made to matter, and not by you');
      }
      break;
    }
    case 'trail': {
      // live, for the same reason a herd's count is: a path decays with disuse
      const v = pathViews.find((q) => q.id === t.id);
      now.push(...trailNow(v?.strength ?? t.about?.strength ?? 0));
      break;
    }
    case 'litter': now.push(...litterNow(t.about?.species ?? '')); break;
    default: {
      const sky = weatherAt(region.seed, clock.t, t.x, t.z);
      now.push(...groundNow(site, {
        stage, moisture: env?.moisture, biomass: env?.biomass,
        rainNow: sky.rain, overcast: sky.overcast,
        burning: sim.fire.burning.some((b) => Math.hypot(b.x - t.x, b.z - t.z) < 60),
      }));
    }
  }

  // …and why this god, specifically, might care. `world/things.ts` has kept
  // `standing`, `since` and `acts` per place since promotion was built, and
  // until the pick started asking, the repetition mechanic the whole store
  // exists to express was unlearnable from inside the game. The ladder above
  // says *that* it is apart; this says how, and for how long.
  significance.push(...significanceOf(mark, clock.t, PROMOTE));

  return {
    id: t.id,
    kind: t.kind,
    name: t.name,
    note: t.note,
    of: read.of,
    // What this subject IS at the scale being held. A person carries a thread
    // that means different things as it widens; so, for a god who has bought
    // *Population as Subject*, does a herd — the grazers of this country. A
    // stone at a valley's width is still a stone.
    ...(t.kind === 'person' && REGISTER_OF[focusFor_(null).band] !== 'intimate'
      ? { at: REGISTER_WORD[REGISTER_OF[focusFor_(null).band]] }
      : t.kind === 'herd' && REGISTER_OF[focusFor_(null).band] !== 'intimate'
          && godhoodWeb.hasRule('beast.subject.population')
        ? { at: populationWord((t.about?.species ?? 'grazer') as WildlifeSpeciesId) } : {}),
    standing,
    wrong: !!mark && mark.standing < 0,
    // ONE CARD says each thing ONCE. `attention.ts` names a thing and adds the
    // one clause worth adding, and some of those clauses are exactly a sentence
    // the state section would produce independently — a bare stone identified as
    // *nobody has found it yet* and then, two lines down, *nobody has found it
    // yet*. Filtered here rather than in `ui/subject.ts` because the phrases are
    // right and it is the pairing that is redundant, and this catches the next
    // pair too without anybody having to notice it.
    now: now.filter((s) => s !== t.note && s !== t.name),
    verbs,
    // The card renders the held verb and no other. There is exactly one
    // arming path now — the palette, driven by `1`–`4` or by its own buttons —
    // so the card, the reticle and the bar cannot disagree about what the god
    // is holding, which they could and did while a modifier chord could arm
    // a verb the palette had never heard of.
    ...(hud.armed ? { armed: hud.armed as string } : {}),
    why: hud.armed
      ? costReasons(costOf(hud.armed, focus.radius, away, cond, Infinity, castModsFor(t.kind)))
      : undefined,
    significance,
    ...(takes ? { takes } : {}),
    carries,
    world: read.world,
    yours: read.yours,
    ...(read.sinceYear !== undefined ? { sinceYear: read.sinceYear } : {}),
    ...(attention?.lost ? { lost: attention.lost } : {}),
  };
}

/**
 * WHAT EVERYTHING DOWNSTREAM READS OF WHERE THE PLAYER IS — the eye, its
 * gaze, and the point it attends to — whichever body the player is in.
 * `DeityPresence` has always had this shape; the mortal body is poured into
 * it each frame. Streaming, LOD, fog, the shadow box, the sea, the glow, the
 * sound and the crowd renderers all read `p` below and so follow the mortal
 * on foot without one of them knowing a mortal exists (brief §30, §31).
 */
interface ActiveEye {
  readonly x: number; readonly y: number; readonly z: number;
  readonly yaw: number; readonly pitch: number;
  /** The point attended to: the orbit target, or a few strides ahead on foot. */
  readonly tx: number; readonly ty: number; readonly tz: number;
}
const mortalEye = { x: 0, y: 0, z: 0, yaw: 0, pitch: 0, tx: 0, ty: 0, tz: 0 };
/** How far ahead a mortal's attention is taken to sit — the lantern's pool on foot. */
const MORTAL_ATTENDS = 8;
const activeEye = (): ActiveEye => {
  if (modes.mode !== 'mortal') return presence;
  const m = mortal, d = m.lookDir();
  mortalEye.x = m.x; mortalEye.y = m.y; mortalEye.z = m.z;
  mortalEye.yaw = m.yaw; mortalEye.pitch = m.pitch;
  mortalEye.tx = m.x + d.x * MORTAL_ATTENDS;
  mortalEye.ty = m.y + d.y * MORTAL_ATTENDS;
  mortalEye.tz = m.z + d.z * MORTAL_ATTENDS;
  return mortalEye;
};

function frame(now: number) {
  requestAnimationFrame(frame);
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;
  fpsAcc += dt; frames++;
  if (fpsAcc > 0.5) { fps = frames / fpsAcc; fpsAcc = 0; frames = 0; }

  // WHICH BODY STEPS. One of the two, on the mode and nothing else; the other
  // is dormant — it is not stepped, and its listeners are dropping everything
  // — so its state is exactly what it was when the player left it.
  const onFoot = modes.mode === 'mortal';
  if (onFoot) mortal.step(dt, groundAt);
  else presence.step(dt, groundAt);
  // A capture station re-parks after `step`, never instead of it: `step` owns
  // the ground-clearance floor, so parking first would let a station sink into
  // a hill. See ui/capture.ts for why this is a module and not a snippet.
  //
  // It parks EVERY frame for as long as the station is armed, and a bounded
  // hold was tried instead and is wrong: the arrival flight (`openOver`,
  // `arrivalPending`) is still steering the presence after load, so the moment
  // re-parking stops it flies the station away and the shot is of somewhere
  // else. The protection against trapping a human is not a frame budget, it is
  // the input listener where `capStation` is declared — which releases on the
  // first keydown, pointerdown or wheel, and costs the harness nothing because
  // taking a screenshot involves no input.
  if (capStation) {
    const st = STATIONS[capStation](groundAt);
    presence.placeEye(st.from, st.at);
    presence.vx = presence.vy = presence.vz = 0;
    // and nothing steers it afterwards: the opening flight and a Chronicle
    // glide both move the eye BELOW this line, which left the camera creeping
    // off the station by a metre over the first thirty frames (measured
    // 2026-08-19 — the same station shot twice was two different frames,
    // which is the one thing a station is for). A station owns the eye.
    arrival = null;
    flyTarget = null;
  }
  const p = activeEye();
  // Ground under the presence, and height over it. Everything that used to
  // read the orbit radius — LOD, fog, sky, the crowd renderers' distance
  // gates — now reads how high the deity actually stands over the land,
  // which is the honest version of the same number. On foot it is the floor,
  // two metres, and every distance gate opens as wide as it goes.
  const gUnder = Math.max(groundAt(p.x, p.z), 0);
  const alt = Math.max(p.y - gUnder, 2);
  // the aura rides with the eye, on the ground beneath it
  aura.update(p.x, p.y, p.z, gUnder, now / 1000, groundAt);
  /*
   * …and the detail ring follows it, every frame.
   *
   * The settings panel sets `detailScale` too, and that is where it was set
   * from at first — which meant changing the aura's radius did nothing until
   * something happened to re-run the settings. The aura IS the ring; a radius
   * that does not re-spend the budget is a number, not a mechanic. Cheap
   * enough to assign unconditionally: `ChunkManager` only re-evaluates LODs
   * when the value actually changes.
   */
  {
    const want = detailFor(aura.radius) * rset.detailMul;
    if (chunks.detailScale !== want) chunks.detailScale = want;
  }
  // the scale of divine attention is chosen by the wheel, capped by reach, and
  // independent of where the flight happens to be — but pointing at a *thing*
  // overrides it, so what the chrome and the reticle report is resolved once,
  // below, off whatever the pointer is on (see `aimFocus`).

  // Where the chosen thing is NOW, and whether it is still there at all — in
  // both bodies: choosing and observing are the one interaction the mortal
  // shares with the deity. The follow's broad band of gold light under a
  // chosen person is the deity's, though, sized to be found from grove
  // height; on foot the plain selection ring marks them instead (Griffin,
  // 2026-08-19: the observe selector "much smaller and not mystical at all").
  trackChosen();
  mortals.follow(!onFoot && chosen?.kind === 'person' ? chosen.id : null);

  if (!onFoot) {
    // I SEE SOMETHING → I CLICK IT → MY ATTENTION MOVES TOWARD IT → IF IT MOVES,
    // I MOVE WITH IT. The eye goes and stands where this kind of subject is worth
    // watching from, and keeps standing there while the player reads the card.
    stepAttention(dt);
    // the wheel is a dolly toward what is chosen, and nothing without one
    presence.zoomEnabled = chosen !== null;

    // …and before any of that, on the first frames of a world: the eye wakes
    // and looks around. (Planned at the END of the frame the world was opened
    // over on — see the wildlife pass — so that the herds it may point at are
    // the ones drawn from where it stands.)
    stepArrivalNow(dt);

    // A clicked Chronicle line glides the presence there: it approaches from the
    // side it is already on, stands off far enough to see the place rather than
    // hover on it, holds its height over the sliding ground, and turns its gaze —
    // unless the hand is on the mouse, which always wins the gaze.
    //
    // This is NOT selection. Nothing here chooses anything or opens a card: it is
    // "take me to where that happened", which the Chronicle can honestly offer
    // because the god was present for every line in it (`noteEvents`).
    if (flyTarget) {
      const ft = flyTarget;
      const ly = ft.y ?? Math.max(groundAt(ft.x, ft.z), 0) + 2;
      // the orbit's target glides onto the place and the eye comes with it, at
      // the distance and the angle it already holds — the hand always wins: a
      // pan lets go of the glide, exactly as it lets go of a chosen subject
      const d = presence;
      if (d.moving) flyTarget = null;
      else {
        const k = 1 - Math.pow(0.0015, dt);
        d.hold(d.tx + (ft.x - d.tx) * k, d.ty + (ly - d.ty) * k, d.tz + (ft.z - d.tz) * k);
        if (Math.hypot(ft.x - d.tx, ft.z - d.tz) < 12) flyTarget = null;
      }
    }
    // the scale of attention is the eye's distance, read once the eye has settled
    // for the frame — the pick, the reticle and the card all read it below
    syncFocus();
  } else {
    // on foot the scale of attention is the intimate one: a mortal stands
    // beside what it looks at, and the card reads a person as *them*, never
    // as their party or their people
    focusRadius = Math.min(focusFor(R_MIN).radius, focusCap);
  }

  camera.position.set(p.x, p.y, p.z);
  camera.rotation.set(p.pitch, p.yaw, 0);

  // the sun rides the world clock: below the horizon at night, so the fires
  // and the stars get the sky to themselves
  // An absence is being paid for: it owns the clock this frame, and the
  // world must not also be advanced on `dt` underneath it.
  // A capture station owns the clock the same way an absence does, and for the
  // same reason: `tPrev === clock.t` makes `sim.advance` below a no-op, so the
  // world is frozen at one absolute instant. Weather drifts by DAY, so two
  // shots at "noon" on different days differ in ways that read as a rendering
  // change — and a control that advanced the world is not a control.
  if (capStation) clock.t = capT;
  const crossing = pumpAbsence(now) || capStation !== null;
  const tPrev = clock.t;
  /**
   * WHOSE CLOCK THIS IS.
   *
   * Unattached: this machine's, advanced on `dt`, exactly as ever.
   *
   * Attached: the SERVER's, extrapolated from its last heartbeat. And then
   * `sim.advance(tPrev, clock.t)` two hundred lines below runs unchanged — the
   * client goes on simulating, over the same absolute grid, from the same
   * snapshot, at the same world time as the server. That is not an
   * approximation: it is the partition-invariance
   * `test/persistence-equivalence.ts` gates, used as a prediction. The two
   * worlds are the same world until somebody acts, and the block below is what
   * notices when somebody does.
   *
   * `Math.max` because a heartbeat can arrive a hair behind where this client
   * had already extrapolated to, and the sun may stall for a frame but must
   * never walk backwards.
   */
  if (!crossing) {
    if (attached) clock.t = Math.max(clock.t, attached.worldTimeNow());
    else clock.advance(dt);
  }
  if (attached) {
    // Where this god is looking, a few times a second. Law 9: it is not
    // history, it is never journaled, and the server holds it only so the
    // other gods can be shown it and so `anchor` and `grasp` can be judged
    // local. Throttled because attention moves every frame and a socket
    // message per frame is sixty a second for a fact nobody records.
    if (now - lastLook > LOOK_EVERY_MS) {
      lastLook = now;
      attached.look({ x: presence.x, y: presence.y, z: presence.z,
                      ground: groundAt(presence.x, presence.z) });
    }
    /**
     * …and if somebody's intent landed, this client's prediction has stopped
     * being the world. There is exactly one honest answer — take the world
     * again — and `attach.ts` owns the throttling, the single-flight and the
     * decision of whether it is worth doing at all. Null on every ordinary
     * frame, which is almost all of them.
     *
     * The adoption lands in a promise callback, so it happens BETWEEN frames
     * rather than inside one. That matters: `tPrev` is read at the top of the
     * frame and spent at the bottom, and a world swapped between those two
     * lines would advance the new world across the old one's span.
     */
    void attached.takeWorld()
      .then((snap) => { if (snap) adoptWorld(snap, { continuity: true }); });
  }
  const tod = clock.tod;
  const elv = 1.05 * Math.sin((tod - 0.25) * Math.PI * 2);
  const az = -2.15 + 4.3 * tod;
  const sunDir = new THREE.Vector3(
    Math.cos(elv) * Math.sin(az), Math.sin(elv), Math.cos(elv) * Math.cos(az));
  // The shadow box stands AHEAD of the eye, not on it (2026-08-18). An orbit
  // eye looks along the ground far more often than down at it, and a box
  // centred under the presence spent half its texels on land behind the
  // camera. Sliding the centre forward by 0.42 of the half-extent — scaled by
  // cos(pitch), so a straight-down look stays centred — puts the same map on
  // the ground that is actually in frame. And the centre is snapped to whole
  // shadow texels in the light's own plane, so a slow flight does not crawl
  // every shadow edge (the "shimmering" the close-range brief names): the
  // box moves in steps of one texel and the sampled map is stable between.
  const e = shadowExtent(alt);
  {
    const ahead = e * 0.42 * Math.max(0, Math.cos(p.pitch));
    const tx = p.x - Math.sin(p.yaw) * ahead, tz = p.z - Math.cos(p.yaw) * ahead;
    // the light's right and up, as three's lookAt will build them: right =
    // up × forward for a camera looking down −sunDir from +sunDir·dist
    const texel = (2 * e) / Math.max(1, sun.shadow.mapSize.x);
    _sr.set(0, 1, 0).cross(sunDir).normalize();
    _su.crossVectors(sunDir, _sr).normalize();
    _st.set(tx, gUnder, tz);
    const kr = Math.round(_st.dot(_sr) / texel) * texel - _st.dot(_sr);
    const ku = Math.round(_st.dot(_su) / texel) * texel - _st.dot(_su);
    _st.addScaledVector(_sr, kr).addScaledVector(_su, ku);
    sun.target.position.copy(_st);
  }
  sun.position.copy(sunDir).multiplyScalar(sunDistance).add(sun.target.position);
  sun.target.updateMatrixWorld();
  // the shadow volume tracks the presence, so its texels stay small enough
  // to resolve what is actually near — see shadowExtent
  {
    const cam = sun.shadow.camera;
    if (cam.right !== e) {
      cam.left = cam.bottom = -e;
      cam.right = cam.top = e;
      /*
       * …and the DEPTH range tracks it too, which is half of why objects can
       * cast at all now. Depth precision is spent across near→far, so a fixed
       * 2 km→60 km span threw almost all of it away on empty sky above and
       * empty rock below. Pulling the light in to a few box-widths keeps the
       * range tight where the casters actually are.
       *
       * The light still has to stand clear of the tallest thing it lights —
       * the island's 2,560 m peaks and the cloud deck above them — so the
       * distance is floored well above both rather than hugging the box.
       */
      const dist = clamp(e * 4, 6000, 34000);
      cam.near = dist * 0.25;
      cam.far = dist * 2.1;
      sunDistance = dist;
      cam.updateProjectionMatrix();
    }
    // The normal bias is a texel's worth of world metres, not a constant: it
    // is what keeps a receiver from sampling its own depth at grazing light,
    // and it has to grow with the texel or acne returns at altitude — and it
    // must SHRINK with it, or nothing shorter than the bias casts onto the
    // ground under it. At 0.55 m fixed, a stone, a log, a tuft cast nothing:
    // the ground under them was sampling the map from above their tops.
    const texel = (2 * e) / Math.max(1, sun.shadow.mapSize.x);
    sun.shadow.normalBias = clamp(texel * 0.85, 0.12, 4.0);
  }
  // moonlight: night was unnavigably black, and it is a third of every cycle.
  // A cool floor under the hemisphere keeps the land legible in blue-grey
  // after dark — so a fire, a hearth, a burning ridge is the brightest thing
  // in a world you can still read, rather than the only thing that exists.
  const day01 = smoothstep(-0.03, 0.12, sunDir.y);
  const night = 1 - day01;
  // the sky's mood over this ground, this second — the same pure field for
  // every eye at this world-time (world/weather.ts)
  const air = weatherAt(region.seed, clock.t, p.x, p.z);
  // The year, on the one number the simulation already burns by. A uniform, so
  // moving it rebuilds nothing: `render/season.ts` has the whole argument.
  dryness.value = seasonalDryness(clock.t);
  // The sun warms as it drops: noon is the white-gold it always was, the
  // first and last hours are amber. One number drives sun, fill AND the
  // hemisphere — the ambient is most of a low sun's light, and an amber sun
  // under a cool blue fill reads as a grey evening, which the first
  // screenshot of this proved. Because every material takes the same three
  // lights, golden hour is a state of the world rather than a filter over it.
  // A grey lid mutes it: no overcast evening glows amber.
  const lowSun = (1 - smoothstep(0.06, 0.45, sunDir.y)) * (1 - air.overcast * 0.7);
  // The sun's amber stops short of orange (2026-08-18): the close-range look
  // wants "warm-ish direct light, cool ambient shadow, very subtle", and the
  // old (1.0, 0.70, 0.42) at the horizon was a filter, not a light.
  sun.color.setRGB(1.0, lerp(0.941, LIGHT.sunLowG, lowSun), lerp(0.839, LIGHT.sunLowB, lowSun));
  sunFill.color.copy(sun.color);
  // The sky's light stays COOL as the sun drops (2026-08-18). It used to warm
  // toward the sun's own amber, on the reasoning that an amber sun under a blue
  // fill reads as a grey evening — but that was true only while the fill was
  // most of the light. With the sun carrying the modelling (below), a cool,
  // dim sky is exactly what puts blue into the long shadows and warm into the
  // lit slopes: the two-temperature light every reference frame is built on.
  // Warm bounce comes from the ground half of the hemisphere, not the sky.
  hemi.color.setRGB(
    lerp(lerp(LIGHT.skyR, LIGHT.skyLowR, lowSun), 0.30, night),
    lerp(lerp(LIGHT.skyG, LIGHT.skyLowG, lowSun), 0.38, night),
    lerp(lerp(LIGHT.skyB, LIGHT.skyLowB, lowSun), 0.58, night));
  hemi.groundColor.setRGB(LIGHT.groundR, LIGHT.groundG, LIGHT.groundB);
  // overcast eats the direct sun and returns a little of it as flat ambient
  // — a grey day is dimmer AND flatter, which is what makes it read as one
  // …and a clear sky's sun is a little fiercer than it was (2026-08-17): the
  // art direction wants the sun to MODEL the land, and at 1.15 a clear noon
  // was one stop short of it. Under cloud the extra goes with the rest.
  // The curve keeps more of the sun's energy while it is LOW: at 18° a sun
  // that reads 0.31 of its noon strength on flat ground is physics, and the
  // golden hour the art direction wants is not physics — it is a low amber
  // sun still strong enough to model the land. Noon is unchanged.
  // …and the whole curve is lifted (2026-08-18), MORE at a low sun than at
  // noon: ×1.45 at noon, rising to ×2.2 below a 15° sun. Three's lights are
  // physical now (no π), so 1.7 of sun on a 0.15-albedo hillside was a linear
  // 0.07 before exposure — the measured close-range frame at midday averaged
  // 0.36 in display luminance and the golden hour 0.19, on a station whose
  // reference frame is built on lit faces near 0.6 and shadow near 0.2. Judged
  // by eye at `?cap=camp` (noon, ×1.4 → 0.43; ×2.2 washes the clearing out) and
  // `?cap=close&tod=0.70` (×2.2 → 0.30, faces and long shadows at last).
  const sunLift = 1.45 + 0.75 * (1 - smoothstep(0.25, 0.75, sunDir.y));
  const sunTotal = (0.55 + 1.15 * Math.pow(clamp(sunDir.y * 2.2, 0, 1), 0.7)) * sunLift * day01
                 * (1 - air.overcast * 0.72) * LIGHT.sun;
  // …and the shadow-free half of it is now a sliver (2026-08-18). At 20% the
  // fill was a second sun from the same direction that lifted every tree
  // shadow a fifth of the way back to lit; a cloud's shadow stays soft because
  // the alphaTest silhouette and the PCF filter do that work, not the fill.
  sun.intensity = sunTotal * (1 - LIGHT.fill);
  sunFill.intensity = sunTotal * LIGHT.fill;
  sunFill.position.copy(sun.position);
  /*
   * The sky as a LIGHT, which is the other half of why nothing had shape.
   *
   * This read `(1 - overcast * 0.18)` — the ambient going DOWN as the sky
   * clouded over. That is backwards about the physics and backwards about the
   * look: an overcast sky is not less light, it is the light, spread out and
   * shadowless, which is exactly why an overcast day has no contrast. Clear
   * sky is the opposite — a small fierce sun and a dim blue dome — and this was
   * handing a clear midday an ambient of 1.00 against a sun of 0.52, so the
   * brightest, sharpest hour of the world's day was its flattest.
   *
   * Now it runs the right way: low and cool under a clear sky so the sun does
   * the modelling, rising toward the sun's own strength under cloud so an
   * overcast noon is bright and shadowless rather than dim. A storm core still
   * presses harder than its overcast and bruises the light.
   */
  // …and it FALLS as the sun drops (2026-08-18; it rose from 2026-08-17 to
  // this morning). Modelling is a ratio: what a slope facing the sun gets over
  // what a slope facing away gets, and the ambient is the floor under both. At
  // an 18° sun the old ×1.7 put the sky level with the sun on flat ground and
  // the evening read as one grey tone with a warm cast. A low, cool sky under
  // a sun that keeps its energy (the curve above) is what turns the same
  // hillside into lit faces and long blue shadows. The measured pair is in
  // the commit: near-band luminance sd 0.045 → the after's figure at
  // `?cap=close&tod=0.70`. Under cloud it still rises toward the sun's own
  // strength, because an overcast noon IS a bright shadowless sky.
  hemi.intensity = (0.18 + 0.27 * day01) * LIGHT.hemi * (1 - LIGHT.hemiLow * lowSun)
                 * (1 + air.overcast * 0.95) * (1 - air.storm * 0.30);
  // the presence's glow: from the player's own position — the eye — like a
  // lantern carried by the god. Sized to reach what is being looked at while
  // the eye is close (the pool's irradiance holds at the target out to
  // GLOW.reach metres of orbit), and no further: past that it dims as any
  // lantern does, and a god a country away carries no light down to it.
  {
    const orbitR = Math.max(4, Math.hypot(p.x - p.tx, p.y - p.ty, p.z - p.tz));
    presenceGlow.position.set(p.x, p.y, p.z);
    const reach = Math.min(orbitR, GLOW.reach);
    const wide = 1 - smoothstep(GLOW.fadeFrom, GLOW.fadeTo, orbitR);
    const e = lerp(GLOW.night, GLOW.day, day01) * wide * LIGHT.glow;
    // decay 1: irradiance at distance d is I/d, so I = E·r holds E at the target
    presenceGlow.intensity = e * reach;
    presenceGlow.distance = Math.max(orbitR, GLOW.reach) * 3.0;
  }

  // the whole living world advances on world time — fire, wildlife, bands,
  // and the slow bookkeeping that ages scars and dries wet ground — then
  // reports which chunks its history touched
  if (!crossing) sim.advance(tPrev, clock.t);
  const dirtyKeys = crossing ? [] : sim.drainDirtyChunks();
  invalidateKeys(dirtyKeys);
  noteFarChange(dirtyKeys);
  refreshFarField();

  // The sky's own bolts, drawn through the SAME strike effect a divine bolt
  // uses — law 10 as rendering: there is no visual difference to learn. The
  // schedule is pure, so this asks the identical minutes the simulation just
  // advanced through; at high time speeds only the last few minutes matter
  // to an eye, so the scan is capped rather than exhaustive.
  {
    const kFrom = Math.floor(tPrev / 60), kTo = Math.floor(clock.t / 60);
    for (let k = Math.max(kFrom + 1, kTo - 3); k <= kTo; k++) {
      for (const b of boltsAtMinute(region.seed, k * 60, region.extentMetres / 2)) {
        const d = Math.hypot(b.x - p.x, b.z - p.z);
        if (d > 7000) continue;
        verbFx.spawn('strike', b.x, surfaceHeight(region, b.x, b.z, 1), b.z);
        if (d < 3500) sound.cast('strike', clamp(d / 3500, 0, 1));
      }
    }
  }

  // the dome must stay inside the far plane or the GPU clips it to black
  sky.scale.setScalar(Math.min(350000, Math.max(4000, alt * 40)));
  sky.position.copy(camera.position);
  // the open sea travels with the presence, so its edge stays symmetric and distant
  sea.mesh.position.set(p.x, 0, p.z);
  // …and the camera, so the sea knows which part of the sky it is reflecting.
  // A flat sea reflects a view ray depressed by β into the sky at elevation β,
  // and the visible ocean spans about 7°–29°; without the camera `water.ts`
  // samples a fixed bearing instead. Measured by terra-3 across eight azimuths:
  // at midday that costs half a luminance level and the screenshot gate cannot
  // see it, but at a low sun the spread is 26.8 against 7.6, landing where
  // Fresnel is 44–74% — the difference between a silver path running toward the
  // sun and a sea that glows evenly everywhere. A sunrise feature, honestly
  // priced by the session that asked for it.
  sea.update(now / 1000, camera);
  rivers.update(now / 1000);
  // the viewport height and fov let the falls' sub-pixel fade honour the
  // resolution slider (§43); the defaults are right at resMul 1
  falls.update(now / 1000, camera.position, renderer.getDrawingBufferSize(_fallsV2).y, camera.fov);
  abyss.position.set(p.x, -14, p.z);
  paintSky(sky, sunDir, scene.fog as THREE.Fog, air.overcast);
  // fog scales with altitude but keeps a floor: standing on the ground the
  // near hills must stay crisp — the haze belongs to distance, not to murk.
  // Weather then takes its share: an overcast pulls the world in, rain pulls
  // harder, and morning fog swallows the middle distance whole.
  const f = scene.fog as THREE.Fog;
  f.near = (alt * 3.2 + 300) * (1 - air.fog * 0.8) * (1 - air.rain * 0.3);
  // The floor came down and the slope went up (2026-08-18): 8 km of visibility
  // from an eye twelve metres over a meadow left the ridge a kilometre off as
  // crisp and as dark as the tussock at its feet — no depth in the one frame
  // that is all about depth. 3.2 km at the ground puts a quarter of the sky
  // into that ridge; the steeper slope hands the altitude gate its old range
  // back by 300 m and clears the far view past it (3 km up: 69 km, was 50).
  f.far = Math.max(f.near + 500,
    (alt * 22 + 3200) * (1 - air.overcast * 0.26 - air.rain * 0.22 - air.fog * 0.52));
  // (the haze under a lid used to be greyed here; `paintSky` greys the dome's
  // own bands with the lid now, and the haze IS those bands — `render/aerial.ts`)

  // Metres of ground the streamer keeps: four kilometres near the ground,
  // tightening to two and a half as the eye pulls out.
  //
  // The old formula pinned the ring at its 24-chunk cap for everything between
  // one kilometre and forty — 2401 mounted chunks whose outer two thirds were
  // bare LOD 3, costing a draw call apiece to out-relieve the horizon mesh
  // slightly. Draw calls are what a wide view costs (close in, most of the ring
  // is out of frustum and free; at six kilometres all of it is on screen at
  // once), so that band was the most expensive nothing in the frame. The far
  // canopy covers the same ground for two calls, so the ring can let it go.
  const vr = lerp(4100, 2400, smoothstep(700, 11000, alt)) * rset.viewMul;
  // streaming centres on the presence itself: with no separate orbit target
  // the eye/focus split in ChunkManager.update collapses to one point
  chunks.update(p.x, p.z, vr, p.x, p.z);
  // the horizon's hole is cut per cell from what is actually mounted: the
  // coarse mesh yields exactly under real relief and carries everything else,
  // so streaming shows neither the void nor coarse ground poking through
  const holeHalf = (horizon.holeCells - 1) / 2;
  const holeX = (Math.floor(p.x / CHUNK_SIZE) - holeHalf) * CHUNK_SIZE;
  const holeZ = (Math.floor(p.z / CHUNK_SIZE) - holeHalf) * CHUNK_SIZE;
  // the canopy's hole is the narrower one: it yields only to chunks that grow
  // their own plants, and plants the coarse rim band itself
  chunks.occupancyInto(holeOccupancy, p.x, p.z, holeHalf, canopyOccupancy);
  horizon.setHole(holeX, holeZ, CHUNK_SIZE, holeOccupancy);
  farCanopy.setHole(holeX, holeZ, CHUNK_SIZE, canopyOccupancy);
  farCanopy.update(alt, rset.canopyMul);
  // burns hole the far canopy too: applySere's omission carried past the
  // streamed ring, refreshed only when the recovering set actually changes
  {
    // …and now the same for what the land remembers and what a god moved: one
    // texture, three channels, rebuilt only when one of the three stores says
    // it changed (ROADMAP §23)
    const v = sim.sere.version * 1e12 + farVersion(
      { earth: sim.earth, env: sim.env, overlay: sim.overlay });
    if (sim.sere !== lastSereStore || v !== lastSereVersion) {
      lastSereStore = sim.sere; lastSereVersion = v;
      buildCanopyField(
        { sere: sim.sere, env: sim.env, earth: sim.earth },
        clock.t, region.n, region.cell, canopyField, canopyScratch);
      farCanopy.setField(canopyField);
    }
  }
  veg.wind.value = now / 1000;
  clouds.update(dt, p.x, p.z,
    (x, z) => weatherAt(region.seed, clock.t, x, z));
  birds.update(now / 1000, p.x, p.z, gUnder, alt);
  rocks.update(alt);   // scree is near-range detail; the outcrops are landmarks
  // each kind carries as far as its biggest member can pay for — and anything
  // a god has carried off stands where they put it down instead of where the
  // lattice grew it. The version tag is what keeps that an O(moved) re-seat
  // rather than a per-frame pass over every relic on the island.
  relics.update(alt, movedRelics(), sim.carried.version);
  nearDetail.update(now / 1000, p.x, p.z, alt, groundAt,
    (x, z) => { const s = region.siteAt(x, z); return { land: s.land, biome: s.biome, rain: s.rain }; },
    // what a god carried off is not lying where it grew any more, and is
    // lying somewhere else instead. The generation is what tells the disc to
    // ask again without the player having to walk away and come back — and
    // it folds in the sim day, because the wear the layer now reads moves on
    // day scales: camps pack and old ground recovers once a day, not never.
    displacing(sim.carried),
    ((Math.floor(clock.t / 86400) * 4096) + sim.carried.version) >>> 0,
    // …LITTER only. `nearDetail`'s carried pass falls back to `K_STONE` for a
    // kind it does not know, so a standing stone a god had moved would be
    // drawn twice: once here as a big pebble, once by `render/relics.ts` as
    // itself. One layer draws one vocabulary.
    carriedLitter(p.x, p.z),
    // the ground people live on is swept: no ankle grass between shelters
    (x, z) => wornAt(wearReader, x, z, clock.t),
    // …and no grass stands in a drawn river
    rivers.inWater);
  rainFx.update(now / 1000, air.rain, p.x, p.y, p.z);
  // the shafts hanging over everywhere else, placed by the same field the
  // simulation reads and tinted with the sky's own haze
  curtainEye.set(p.x, p.y, p.z);
  curtains.update(now / 1000, curtainEye, (scene.fog as THREE.Fog).color,
    (x, z) => weatherAt(region.seed, clock.t, x, z),
    (x, z) => surfaceHeight(region, x, z, 1));

  // flames stand on the real ground, whatever LOD happens to be underfoot
  const burningCells = sim.fire.burning.map((b) => ({
    x: b.x, y: surfaceHeight(region, b.x, b.z, 1) + 0.5, z: b.z, intensity: b.intensity,
  }));
  fireFx.update(now / 1000, dt, burningCells, WIND);

  // the people, party by party. The band's centre of mass is an average of
  // groups that are usually hundreds of metres apart — often nobody stands
  // there at all — so the renderer is handed the parties themselves, and the
  // roster that says who walks with which. PersonState already carries every
  // field the renderer reads, so the roster passes through untouched.
  const glideDt = Math.max(0, clock.t - glidePrevT);
  glidePrevT = clock.t;
  const liveParties = new Set<string>();
  const bandViews: BandView[] = sim.bands.bands()
    .filter((b) => b.population > 0)
    .map((b) => ({
      id: b.id,
      people: b.people,
      parties: b.parties.map((p) => {
        // the glide: the displayed position runs a couple of world-minutes
        // behind the sim's hourly strides and moves every frame
        liveParties.add(p.id);
        let g = partyGlide.get(p.id);
        if (!g) {
          g = { x: p.x, z: p.z, hx: 0, hz: 1 };
          partyGlide.set(p.id, g);
        }
        glideStep(g, p.x, p.z, glideDt);
        const gap = Math.hypot(p.x - g.x, p.z - g.z);
        // heading falls back the old way when the glide stands still: the
        // walk when there is one, otherwise the way they came out from home
        const away = p.target
          ? { x: p.target.x - p.x, z: p.target.z - p.z }
          : { x: p.x - b.homeX, z: p.z - b.homeZ };
        const l = Math.hypot(away.x, away.z) || 1;
        const moving = gap > 25;
        return {
          id: p.id, activity: p.activity, size: p.size,
          x: g.x, z: g.z, y: surfaceHeight(region, g.x, g.z, 1),
          hx: moving ? g.hx : away.x / l,
          hz: moving ? g.hz : away.z / l,
          // a party whose destination is all but underfoot is working, not
          // walking: the last forty metres are the arrival radius — and a
          // glide still closing on the truth is a walk in plain sight
          walking: (!!p.target && l > 25) || moving,
        };
      }),
    }));
  for (const id of partyGlide.keys()) {
    if (!liveParties.has(id)) partyGlide.delete(id);
  }
  mortals.update(now / 1000, bandViews, alt, groundAt);
  verbFx.update(now / 1000, dt);

  // What attention is on — one raycast a frame, the same budget the armed
  // reticle always spent, now spent whether a verb is armed or not. It writes
  // nothing: the whole of selection is a read. With the mouse captured the
  // pick rides the centre of the gaze; the previous target is passed back in
  // as the sticky one, so what is named holds steady while the gaze breathes.
  // …in both bodies: a mortal points, hovers and chooses as the deity does
  // (Griffin, 2026-08-19); what it cannot do is arm or cast. A drag that is
  // turning either body's head holds the pick still.
  const lookingRound = onFoot ? mortal.dragging : presence.dragging;
  hovered = pointerEvent && !lookingRound ? pickAt(pointerEvent, hovered) : hovered;
  // the ring rides whatever `trackChosen` found at the top of the frame —
  // and on foot it is the PLAIN mark: small, still, unlit (Griffin, 2026-08-19)
  selectionFx.update(now / 1000, hovered, chosen, p, groundAt, onFoot);

  // the armed footprint under the cursor, drawn at the radius the wheel has
  // chosen — or, over a thing, at the radius channelling into it would use,
  // so the reticle shows the cast you are actually about to spend
  // …and a held modifier arms just as truly as the palette does, so the ring
  // follows whichever of the two is about to fire.
  const aiming = hud.armed;
  stage.classList.toggle('armed', aiming !== null);
  const aimHit = aiming && hovered
    ? new THREE.Vector3(hovered.x, hovered.y, hovered.z) : null;
  const aimFocus = focusFor_(hovered);
  // Grasp draws no aim ring, and that is not an omission. The ring means "this
  // is the ground a cast would take", and grasp has no such footprint: it is
  // local to the Presence (`GRASP_REACH`, 60 m) and reaches exactly one thing.
  // Drawing the focus disc for it would promise a radius the verb does not
  // have. It has its own reticle already, drawn by the dig/hold path.
  reticle.aim(aiming === 'grasp' ? null : aiming, aimHit,
              { radius: aimFocus.radius, softness: aimFocus.softness,
                camDistance: aimHit ? camera.position.distanceTo(aimHit) : alt,
                // the held key's stones, as a share of the five: the ring burns
                charge: charge ? charge.stones / 5 : 0 });

  // …and the hand in the ground, if there is one. Read-only from end to end:
  // the scoop is recomputed from the same pure function the release will be
  // priced by, and nothing is written until the key comes up (law 9).
  const dig = digNow();
  if (dig) {
    const scoop = digScoop(dig);
    const dropR = digDrop(scoop);
    reticle.dig({
      pit: { x: dig.x, y: dig.y, z: dig.z, r: scoop.r },
      drop: hovered
        ? { x: hovered.x, y: groundAt(hovered.x, hovered.z), z: hovered.z, r: dropR }
        : null,
      depth: scoop.r > 0 ? scoop.depth / (scoop.r * REPOSE) : 0,
      enough: scoopReads(scoop),
    });
  } else reticle.dig(null);
  reticle.update(now / 1000, dt);
  // …and the thing in the hand, over wherever the pointer is — it will be
  // set down there — or over where it was taken, while the pointer is off
  // the ground. Read-only: `session.hold` is the only truth about the hand.
  {
    const held = heldNow();
    if (held && held.kind === 'litter') {
      const over = hovered ?? held;
      heldThing.hold({ x: over.x, y: groundAt(over.x, over.z), z: over.z },
                     held.about?.species, held.size);
    } else heldThing.hold(null);
    heldThing.update(now / 1000);
  }

  // the storm arriving, one bolt at a time: the simulation lit them all at
  // the same instant, the sky takes a second to say so
  while (boltQueue.length > 0 && now >= boltQueue[0].at) {
    const b = boltQueue.shift()!;
    verbFx.spawn('strike', b.x, surfaceHeight(region, b.x, b.z, 1), b.z);
    sound.cast('strike', b.soft);
  }

  // the island's voice
  {
    // fire is heard by how close the nearest flame stands to the presence
    let nearest = Infinity;
    for (const b of sim.fire.burning) {
      const d = Math.hypot(b.x - p.x, b.z - p.z);
      if (d < nearest) nearest = d;
    }
    sound.update(dt, {
      fire: clamp(1 - nearest / 3800, 0, 1),
      // the called rain and the sky's own — whichever is falling harder
      rain: Math.max(performance.now() < rainHeard ? 1 : 0, air.rain),
      wind: clamp(0.35 + alt / 22000 + air.overcast * 0.2, 0, 1),
    });
  }

  // habitation: structures stand where they were built; paths rebuild their
  // ribbons only when a trail forms or dissolves; smoke marks living camps
  const activeHearths = sim.bands.activeHearths();
  litHearths = new Set(activeHearths);
  const structureViews: StructureView[] = sim.bands.structures().map((s) => ({
    id: s.id, type: s.type, x: s.x, z: s.z, y: surfaceHeight(region, s.x, s.z, 1),
    condition: s.condition, active: litHearths.has(s.id),
    // the practice that raised it, carried straight through: the renderer asks
    // the building, never the band, so a ruin keeps its people's roof
    built: s.built,
  }));
  eye.x = p.x; eye.y = p.y; eye.z = p.z;
  structuresFx.update(now / 1000, structureViews, alt, eye);

  // and the ground those structures stand on, cleared by living on it. Past
  // the streamed ring nothing else says a place is inhabited: wear is a chunk
  // transform and the horizon mesh has never heard of it. Dead camps come
  // through too, and are drawn as old ground rather than as bare earth.
  const signature = `${structureViews.length}|${activeHearths.join(',')}`;
  if (signature !== campSignature) {
    campSignature = signature;
    campViews = campsFrom(structureViews).map((c) => {
      const radius = clearingRadius(c.spread);
      // the ground's own gradient across the clearing, so the patch lies
      // along the slope instead of through it
      const d = radius * 0.7;
      const h = (x: number, z: number) => surfaceHeight(region, x, z, 1);
      return {
        id: c.id, x: c.x, z: c.z, y: h(c.x, c.z), radius, lit: c.lit,
        gx: (h(c.x + d, c.z) - h(c.x - d, c.z)) / (2 * d),
        gz: (h(c.x, c.z + d) - h(c.x, c.z - d)) / (2 * d),
      };
    });
  }
  habitationFx.update(campViews);

  // path points are sampled onto the terrain only when the set changes, but
  // strengths flow through every frame so a fading trail visibly fades
  if (sim.bands.pathsVersion() !== lastPathsVersion) {
    lastPathsVersion = sim.bands.pathsVersion();
    // ROUTE BY ID, NEVER THE WHOLE NETWORK.
    //
    // A version bump is one way forming or one fading, and this used to clear
    // the cache and relax all 41.3 km again: **311 ms in a single frame**,
    // measured (`tools/ways.ts`). At load that is invisible; mid-session a path
    // forming would freeze a third of a second of play, and paths form during
    // play. `PathRecord.points` is written once at formation and never touched
    // again — `sim/mortals/paths.ts` only mutates `strength` and `lastUsedAt`
    // afterwards — so a route cached by id cannot go stale, and the whole cost
    // becomes the one new way (~9 ms).
    const live = new Set(sim.bands.paths().map((p) => p.id));
    for (const id of [...pathPoints.keys()]) if (!live.has(id)) pathPoints.delete(id);
    // The SHAPE of a way lives in `render/route.ts`, not here. It is
    // render-only — the recorded points are untouched and a band still walks
    // what it always walked — so the line may be reshaped freely, and this
    // call site exists so that reshaping it never means editing main.ts.
    for (const path of sim.bands.paths()) {
      if (pathPoints.has(path.id)) continue;
      pathPoints.set(path.id, routeFor(
        path.points,
        (x, z) => surfaceHeight(region, x, z, 1),
        {
          // The two things a walker answers to that the ground alone cannot
          // say: where the wood is closed, and where the ground is wet. Both
          // are optional in `RouteFields` — a caller that has not wired a field
          // loses that term and gets no error — so without this the way still
          // curves and still follows the ground, it just walks through the
          // trees and the bog. Measured unwired (`tools/ways.ts --unwired`):
          // wet-ground share stays 9.5% instead of falling to 7.4%.
          fields: {
            canopy: (x, z) => standClosure(region.seed, x, z),
            flow: (x, z) => region.flowAt(x, z),
          },
        },
      ));
    }
  }
  pathViews = sim.bands.paths().map((p) => ({
    id: p.id, strength: p.strength, points: pathPoints.get(p.id) ?? [],
  }));
  pathsFx.update(pathViews, lastPathsVersion);

  // wildlife: the herds standing in the social cells around the presence.
  //
  // The census is the truth and the renderer's job is to agree with it (law 6).
  // What stood here was a flat `pop < 40` gate and `pop / 30` bodies, and
  // `tools/wildlife.ts` prices what that cost on the shipped island: a
  // predator's carrying capacity is 8 head a cell and a scavenger's is 20, both
  // *below* the gate, so 3,844 animals could never be drawn anywhere at any
  // range; browsers cleared it in 0 of 918 cells; and the densest grazer cell
  // on the island — 68 head — drew two. 68,298 land animals could put at most
  // 1.5% of themselves on a screen, against a 420-body budget that therefore
  // could never bind.
  //
  // A cell now draws the head it actually holds, thinned by DISTANCE rather
  // than by a population threshold: nothing is invisible for being rare, and
  // detail follows relevance (law 3). Two properties are load-bearing:
  //   · thinning takes a PREFIX of each herd's own stable ordering, and the
  //     scatter radius is read off the CENSUS count rather than the drawn
  //     count — so flying closer reveals more of the same herd standing where
  //     it already stood, and never re-deals it (law 8);
  //   · a species is dropped once one body falls under the pixel floor, on the
  //     ~340×-its-own-size angular convention `nearDetail` already uses. A
  //     0.4 m rabbit is not worth a triangle at 800 m; a 1.6 m grazer is.
  wildlifeViews.length = 0;
  const here = toSocial(p.x, p.z);
  const herdCandidates: { view: WildlifeView; d: number }[] = [];
  for (let dz = -WILD_CELLS; dz <= WILD_CELLS; dz++)
    for (let dx = -WILD_CELLS; dx <= WILD_CELLS; dx++) {
      const sx = here.sx + dx, sz = here.sz + dz;
      for (const species of LAND_SPECIES) {
        const pop = sim.wildlife.populationAt(sx, sz, species);
        if (pop < 1) continue;
        // The species has to reach the HIGH bits of the seed. It used to be
        // xored in as a bare index 0..4 — three bits that `>>> 3` then threw
        // away — so every species in a cell stood at one identical anchor:
        // grazers, browsers, rabbits and the predator hunting them in a single
        // heap, five herds deep. Multiplying by an odd word spreads it.
        const seed = (sx * 73856093) ^ (sz * 19349663) ^ (LAND_SPECIES.indexOf(species) * 83492791);
        let fx = 0.2 + 0.6 * ((seed >>> 3) % 97) / 97;
        let fz = 0.2 + 0.6 * ((seed >>> 9) % 89) / 89;
        // ...and then the herd MOVES, which it never did. `migration.ts` shifts
        // 15–30% of a herd into a neighbouring cell every day and the drawn
        // stand sat on that fixed hash for a century: the census migrated and
        // the eye never once saw it. Two pure terms fix it, both render-only
        // and both read off state that already exists.
        //
        // It leans toward the neighbours that hold MORE of its own species —
        // the direction the animals are actually going, since that is the same
        // headroom comparison `migrationPass` makes — so a herd is found on the
        // side of its ground that is filling, and crosses as the census does.
        //
        // Weighted across all eight rather than picked from the best one. The
        // winner-take-all version read better and was wrong: the moment two
        // neighbours crossed in population the whole offset flipped to the
        // other side of the cell, teleporting the herd up to 530 m between one
        // frame and the next. A weighted centroid moves as smoothly as the
        // weights do, and the weights only move on the day grid.
        let wx = 0, wz = 0, wsum = 0;
        for (let k = 0; k < 9; k++) {
          const ox = (k % 3) - 1, oz = ((k / 3) | 0) - 1;
          if (!ox && !oz) continue;
          const w = Math.max(0, sim.wildlife.populationAt(sx + ox, sz + oz, species) - pop);
          wx += ox * w; wz += oz * w; wsum += w;
        }
        if (wsum > 0) {
          const lean = Math.min(0.26, (wsum / Math.max(1, pop)) * 0.18);
          fx += (wx / wsum) * lean; fz += (wz / wsum) * lean;
        }
        // and it grazes: a slow circuit of its own ground on the world clock,
        // pure in t so every machine draws the herd in the same field. Six days
        // a lap — at 192× that is 45 real minutes, so a herd is somewhere else
        // when you come back and never visibly teleports while you watch.
        const ph = ((seed >>> 17) % 977) / 977 * Math.PI * 2;
        const lap = clock.t / (86400 * 6) * Math.PI * 2 + ph;
        fx += Math.cos(lap) * 0.1; fz += Math.sin(lap * 0.87) * 0.1;
        const ax = (sx + fx) * SOCIAL_CELL_SIZE;
        const az = (sz + fz) * SOCIAL_CELL_SIZE;
        // To the herd's nearest edge, not its anchor: a herd 200 m across that
        // the presence is standing inside is at distance 0, not at 100 m. And
        // in THREE dimensions — a horizontal reach is no reach at all for a
        // god, who spends the whole game looking straight down. Measured with
        // the flat version (`tools/fauna2.ts`): the body count was identical at
        // 80 m and at 3 km, and from 900 m up every single one of them was
        // under three pixels. `region.heightAt` rather than `surfaceHeight`
        // here because this is a distance test over hundreds of candidates a
        // frame and the drawn ground is only metres off the region's.
        const ay = region.heightAt(ax, az);
        const d = Math.max(0, Math.hypot(ax - p.x, ay - p.y, az - p.z) - herdSpread(pop));
        // A herd carries a little further than one animal does — but only a
        // little, and the first derivation of this was wrong. Tiling the bodies
        // gives L·√n, which sounds right and is not: a herd is not a solid
        // object, it is dots with gaps, and once a single body drops under
        // ~2.5 px the herd stops reading however many of them there are.
        // Measured over 400 standpoints at three altitudes, √n and n^¼ deliver
        // the IDENTICAL readable count (27 at 150 m, 17 at 400 m) — √n just
        // adds 392 sub-pixel bodies a frame against n^¼'s 46. So the exponent
        // is a quarter: enough that a big herd halves the odds of a standpoint
        // showing no animal at all (38% → 12% at 400 m), and no more.
        if (d > BODY_LENGTH[species] * Math.pow(Math.max(1, pop), 0.25) * WILD_REACH) continue;
        herdCandidates.push({
          view: { species, x: ax, z: az, y: surfaceHeight(region, ax, az, 1), count: pop, seed, sx, sz,
                  // dev-only: the phenotype lab dresses every herd; absent in
                  // ordinary play, and the field stays undefined (free)
                  ...(phenoLab?.beast ? { phenotype: phenoLab.beast } : null) },
          d,
        });
      }
    }
  // nearest first, so the view budget is spent on what the eye is closest to.
  // The old scan order filled from the far northern row inward and only did not
  // show because the gate left barely forty candidates to choose between.
  herdCandidates.sort((a, b) => a.d - b.d);
  for (let i = 0; i < herdCandidates.length && i < WILD_VIEWS; i++) {
    const view = herdCandidates[i].view;
    // the herd's strain, expressed (the wake): only for the views actually
    // drawn, and only when the lab has not overridden it — ordinary ground
    // resolves to undefined and costs a cache hit
    if (!view.phenotype) {
      const p = strainHerdPhenotype(sim.strainReads, view.species,
                                    view.sx!, view.sz!, clock.t);
      if (p) view.phenotype = p;
    }
    wildlifeViews.push(view);
  }
  // the pointer resolves individual animals through the same `bodyAt` at the
  // same instant — see `drawnAtSeconds`
  drawnAtSeconds = now / 1000;
  wildlifeFx.update(drawnAtSeconds, wildlifeViews, alt, groundAt);
  // the arrival is planned HERE, once the herds around the new standpoint are
  // in hand: `wildlifeViews` is rebuilt above from wherever the presence now
  // stands, and a plan made earlier in the frame would read the previous
  // standpoint's herds — on the boot frame, a band on the far side of the island
  if (arrivalPending) planArrivalNow();

  // the anchored places: the god's stone, and the landscape mortals raised
  const anchorViews: AnchorView[] = sim.anchors.all().map((a) => ({
    id: a.id, x: a.x, z: a.z, y: surfaceHeight(region, a.x, a.z, 1),
    stage: a.stage, resonance: a.resonance,
  }));
  anchorFx.update(now / 1000, anchorViews, alt);

  // the chrome reads the world; intent flows back through the callbacks.
  // The Chronicle takes the new events first: it is what turns an event id
  // into a sentence, and the followed life's panel is about to ask it for
  // some — otherwise the panel spends a frame claiming a band with a history
  // remembers nothing, which is exactly what it looks like after a load.
  // The god's awareness stays with the god's eye: a mortal body does not
  // carry divine perception about with it (brief §32), so on foot the
  // Chronicle notices what happens around where the eye was left standing.
  if (onFoot) {
    const dg = Math.max(groundAt(presence.x, presence.z), 0);
    noteEvents(presence.x, presence.z, Math.max(presence.y - dg, 2));
  } else noteEvents(p.x, p.z, alt);
  hud.pushEvents(chronicle);
  noticeBecome(now);
  hud.update({
    // which body, told — the bar dresses itself for it (`ui/hud.ts`)
    mode: modes.mode,
    inhabitable: chosen?.kind === 'person' && !attention?.lost,
    mortalName: onFoot ? mortalName : undefined,
    mortalArmed: modes.mortalArmed,
    // the world has an age, and that is the whole of what the persistent
    // chrome has to say about time (law 27: there is one clock, and it is not
    // the player's business sixty times a second)
    year: Math.floor(clock.t / (DAY * YEAR_DAYS)),
    // Hover is LIGHT: a name, and nothing more. Selection is the commitment,
    // and it is the only thing that opens a card.
    //
    // Bare ground says nothing at all. With the mouse captured the crosshair is
    // over *something* every single frame, and the biome under it is the least
    // surprising fact in the game — a permanent word floating under the gaze
    // that changes about once a minute. A landmark still names itself, because
    // being on the Watch is not the least surprising fact in the game.
    looking: hovered && hovered.kind !== 'ground' ? describeTarget(hovered) : null,
    // …and where on the screen the cursor is, so the name sits beside the
    // hand rather than at the centre of the frame — the mouse is a free
    // cursor again, and attention is wherever it points
    aim: pointerEvent ? { x: pointerEvent.clientX, y: pointerEvent.clientY } : undefined,
    // ONE SELECTED THING = ONE CARD. Everything about what the god chose — what
    // it is, what is true of it, what it has lived through, and what of that
    // was the god's own hand — assembled once, into one surface, on the left.
    // On foot too: the card renders at most the ARMED verb's reading and
    // nothing is ever armed on foot, so a mortal's card is the observation
    // without the act — identity, state, what it carries, its history.
    card: cardFor(chosen, p),
    // the focus row shows the cast a click would actually spend, which over a
    // thing is the thing's own footprint and not the wheel's. Chrome that
    // advertises a valley while the click delivers six metres is chrome that
    // lies about the only number the player is steering by.
    focus: { band: aimFocus.band, radius: aimFocus.radius },
    // stones a held key has gathered, so the bar can show what is about to be spent
    charge: chargeStones(now),
    focusFresh: !onFoot && performance.now() < focusTouched + 1800,
    // What the god is made of, and what each verb would take of it at the
    // focus and aim it is holding right now. Priced through the same
    // `costOf` the act itself pays, so the preview cannot drift from the
    // bill — and shown as a band rather than a number (Design III §113).
    power: sim.pantheon.player.power,
    // read from the world, never tallied by the chrome: a load, an absence or
    // a rival's world all report the truth
    anchors: sim.anchors.all().filter((a) => a.godId === sim.pantheon.player.id).length,
    // …and how many are burning. `AuraSystem.lit()` has had no `src/` caller
    // since it was written, so a god had no way to find out where it had left
    // part of itself working — the one thing an aura's silence hides.
    lit: sim.auras.lit().filter((a) => a.godId === sim.pantheon.player.id).length,
    costs: verbCostBands(),
    costWhy: armedCostWhy(),
    notice: performance.now() < noticeUntil ? noticeText : null,
  });

  // a nudge so the ground detail keeps pace with altitude
  void smoothstep(0, 1, 0);
  renderer.info.reset();
  renderer.render(scene, camera);
  // …and the frame finished: bloom on what is bright, shafts off a low sun,
  // the grade and the vignette. Reads the frame back and draws over it, so
  // nothing in the scene has to know (`render/post.ts`); the frame probe
  // below reads what the player sees, post and all.
  post.apply(renderer, camera, sunDir, 1 - smoothstep(-0.04, 0.08, sunDir.y));

  // The framebuffer reading, taken HERE and nowhere else: the drawing buffer
  // is not preserved, so a readback from anywhere but immediately after the
  // render returns black. Settles once the streamed ring has caught up, then
  // parks the numbers in the title, which is the one place a reloading preview
  // pane can still be asked for them.
  // Re-read every few frames rather than once. A hidden preview pane runs only
  // a handful of frames per screenshot, so any fixed "settle" count either
  // never fires or fires before the streamed ring has caught up; refreshing
  // means the newest screenshot always carries the newest reading, and two
  // screenshots agreeing IS the settle test.
  if (capProbe && ++capFrames >= 2) {
    let r: ReturnType<typeof frameReading> | null = null;
    let err = '';
    try {
      const gl = renderer.getContext();
      r = frameReading(gl, renderer.domElement.width, renderer.domElement.height);
    } catch (e) { err = String(e); }
    if (!r) { if (capPanel) capPanel.textContent = 'PROBE FAILED ' + err; return; }
    // Painted into the page, not logged. The preview pane reloads the document
    // before any console evaluation, so the console and the title are both
    // gone by the time they could be read — the screenshot is the only channel
    // out, which means the reading has to be something the camera can see.
    if (capPanel) {
      capPanel.textContent =
        `mean ${r.mean}   sd ${r.sd}   HP ${r.hp}\np01 ${r.p01}   p99 ${r.p99}   n ${r.n}`;
    }
  }
}
requestAnimationFrame(frame);

// keep the chunk grid honest if the window is resized while paused
void CHUNK_SIZE;

/**
 * The world precedes the player (Expansion II §3, §4).
 *
 * `tools/worldgen.ts` runs a century of prehistory through the real systems
 * and writes it to `public/prehistory.json`. If it is there, the game does
 * not begin with one band on an empty island — it begins on ground that has
 * already been lived on for a hundred years, by several peoples, with their
 * trails and their camps and their dead already in it.
 *
 * The fetch is deliberately not awaited at module scope: the frame loop and
 * the whole render path are synchronous, and blocking boot on a multi-megabyte
 * file would trade a populated world for a white screen. One band exists for
 * the second it takes to arrive, and then the island does.
 */
/**
 * ATTACH TO A HOSTED WORLD.
 *
 * The request comes from the threshold, or straight off a URL a friend was
 * handed (`?server=8787&token=our-secret`).
 *
 * Returns true when it took, and false when it did not — including a boot that
 * TRIED and could not reach the server, which falls through to the local world
 * and says so. A game that hung on a white screen because a friend had closed
 * their laptop would be a worse failure than playing alone.
 */
const attachTo = async (req: AttachRequest): Promise<boolean> => {
  try {
    const { world, snapshot } = await attach(req, {
      /*
       * NO `onEvents` HANDLER, AND THAT IS THE INTERESTING PART.
       *
       * A predicting client generates the world's events itself — same
       * snapshot, same absolute grid, same world time, so the same fire starts
       * at the same minute and `noteEvents` finds it in the local ring exactly
       * as it does offline. The socket's event stream would be a second copy of
       * something this client already has.
       *
       * What it cannot predict is an INTENT, and those arrive with the resync
       * the intent's own sequence bump triggers. So the stream is left doing
       * the one job only it can do — telling us the world moved — and the
       * drawing stays where it always was.
       */
      onNotice: (text) => notice(text),
      onLost: (why) => {
        attached = null;
        notice(`the world is out of reach — ${why}. you are looking at a copy.`, 8000);
      },
    });
    attached = world;
    // WHOSE HANDS THESE ARE, before anything reads the god. The server assigned
    // it; this client did not choose it and cannot.
    myDeityId = world.deityId;
    adoptWorld(snapshot);
    clock.t = world.worldTimeNow();
    beginAsPlayer();
    const seen = sim.events.all();
    if (seen.length) session.cursor = seen[seen.length - 1].id;
    // the band the game opens over is a rule, not the first in the list —
    // `ui/arrival.ts` openingBand, the First Playable lane's ruling
    const first = openingBand(sim.bands.bands());
    if (first) openOver(first.homeX, first.homeZ);
    notice(`${world.name ?? world.worldId} — this world was here before you ` +
           `opened it, and it has not been waiting`, WAKE_S * 1000 + 400);
    if (first) arrivalPending = { x: first.homeX, z: first.homeZ };
    return true;
  } catch (e) {
    notice(`could not reach ${req.origin} — playing your own world instead ` +
           `(${(e as Error).message})`, 8000);
    return false;
  }
};

/**
 * WHICH WORLD, DECIDED BEFORE THE FIRST FRAME THE PLAYER MEANS TO KEEP.
 *
 * The threshold (`ui/threshold.ts`) always runs, except where the URL has
 * already decided — a capture station, a probe, or a complete `?server=` link.
 * `thresholdSkipped` owns that list and `test/threshold.ts` guards it, because
 * the most expensive way for this to be wrong is silently: a sheet over a
 * `?cap=camp` frame would break every screenshot `CLAUDE.md` judges a visual
 * change by, and nothing would say so.
 */
const chooseWorld = async (wantOwnWorld: () => void): Promise<Entry> => {
  const direct = attachRequestFrom(window.location.search);
  if (thresholdSkipped(window.location.search)) {
    if (direct) return { kind: 'join', request: direct };
    wantOwnWorld();
    return { kind: 'local' };
  }
  return crossThreshold({ onWantOwnWorld: wantOwnWorld });
};

void (async () => {
  try {
    /*
     * The century starts downloading the moment the player LOOKS at their own
     * world — while the threshold is still on screen, so the reading of the
     * sheet is exactly the time the fetch needs and entering is instant.
     *
     * Not before that, and the difference matters on a hosted server: the file
     * is 3.8 MB, a player who came to join never opens it, and starting it
     * unconditionally would spend most of a minute of somebody's home upload
     * on a world nobody was going to enter.
     */
    let ownWorld: Promise<string | null> | null = null;
    const fetchOwnWorld = (): void => {
      ownWorld ??= fetch('prehistory.json')
        .then((r) => (r.ok ? r.text() : null))
        .catch(() => null);
    };

    const entry = await chooseWorld(fetchOwnWorld);
    if (entry.kind === 'join' && await attachTo(entry.request)) {
      applyCaptureRigs();
      return;
    }

    // A join that could not be reached falls through to here without ever
    // having asked for the century, so ask now.
    fetchOwnWorld();
    const text = await ownWorld;
    if (!text) throw new Error('no prehistory on this server');
    const snap = decodeSnapshot(text);
    adoptWorld(snap);
    applyCaptureRigs();
    // `?days=N` — the dev time door, reachable from a URL so a reloading
    // preview pane can use it. It is the same `devAdvance` the console door
    // calls, so it advances the WORLD and not merely the sun; the Chronicle
    // needs real elapsed history to have anything to fold.
    const days = capDays(window.location.search);
    if (days > 0 && import.meta.env.DEV) devAdvance(DAY * days);
    // the world arrives godless; the player arrives with it, and is who they
    // were before the fetch landed rather than whoever the world-builder was
    beginAsPlayer();
    /**
     * And the Chronicle starts *here*. The log ships with the world for the
     * simulation's own use, but replaying it would tell an arriving god about
     * a hundred years of events they were not present for — which is exactly
     * the thing OBSERVATION MUST NOT CREATE HISTORY is about, pointed the
     * other way. The player learns this island by looking at it.
     */
    const seen = sim.events.all();
    if (seen.length) session.cursor = seen[seen.length - 1].id;
    // the band the game opens over is a rule, not the first in the list —
    // `ui/arrival.ts` openingBand, the First Playable lane's ruling
    const first = openingBand(sim.bands.bands());
    if (first) openOver(first.homeX, first.homeZ);
    const peoples = new Set(sim.bands.bands()
      .filter((b) => b.population > 0).map((b) => b.cultureId)).size;
    const souls = Math.round(sim.bands.bands()
      .reduce((s, b) => s + Math.max(0, b.population), 0));
    // …and this line stands for as long as the eye rests on the camp before it
    // starts to look around (`ui/arrival.ts`), which is what happens next
    notice(`${souls} souls, ${peoples === 1 ? 'one people' : `${peoples} peoples`}` +
           ` — and none of them know you are here`, WAKE_S * 1000 + 400);
    if (first) arrivalPending = { x: first.homeX, z: first.homeZ };
  } catch {
    applyCaptureRigs();
    // no prehistory on this server: the world starts with one band, as before
    // — and the eye still wakes and looks around it
    const only = sim.bands.bands().find((b) => b.population > 0);
    if (only) arrivalPending = { x: only.homeX, z: only.homeZ };
  }
})();

/**
 * Aura — **shift+V**, on one of your own stones (law 31, `sim/aura.ts`).
 *
 * It sits next to the Anchor key because it is the same idea continued: `V`
 * binds you to a place, `shift+V` leaves part of you burning there. The verb
 * is whatever the palette has armed, which reuses the grammar the whole game
 * already has — choose a thing, arm an intent — rather than inventing a
 * second one for this.
 *
 * A stone you are not near and have not chosen is not eligible: an aura is a
 * commitment to a *particular* place, and picking one at random from the map
 * would make it a menu.
 */
const doAura = () => {
  /**
   * WHAT AN AURA CAN BE, AND WHAT THIS GOD CAN CURRENTLY EXPRESS.
   *
   * `AURA_VERBS` is `strengthen · weaken · rain` and it is right: all three are
   * real, all three are held by `kindle`, and `test/aura.ts` exercises them.
   * What was wrong was here. The refusal read *"only strengthen, weaken and
   * rain can"* — naming two verbs **the player has no way to arm**, because
   * the palette is the four starters (`DECISIONS.md`) and Weaken left the bar
   * while Rain became a Signature. So the intersection of *what an aura
   * accepts* and *what a god can hold* is exactly `{strengthen}`, and the
   * message was advertising a capability the interface does not offer.
   *
   * The list is not narrowed and the arming is not widened. Narrowing would
   * delete simulation the design keeps on purpose — Weaken is a depth
   * destination and `sim.act` still casts it. Widening would mean new
   * modifier chords, and the commit that carried this one **deleted** the last
   * modifier-arming path for being exactly that kind of second grammar.
   *
   * So: an aura takes what the god is holding, and today that is one verb.
   * When Weaken or Rain get a way to be expressed, this needs no change —
   * `hud.armed` will simply be able to say them. `WIRING.md` carries the row.
   */
  const armed: AuraVerb = 'strengthen';

  const mine = sim.anchors.all().filter((a) => a.godId === sim.pantheon.player.id);
  const picked = chosen?.kind === 'anchor'
    ? mine.find((a) => a.id === chosen!.id)
    : mine.filter((a) => Math.hypot(a.x - presence.x, a.z - presence.z) <= 240)
        .sort((a, b) => Math.hypot(a.x - presence.x, a.z - presence.z)
                      - Math.hypot(b.x - presence.x, b.z - presence.z))[0];

  if (!picked) {
    notice(mine.length
      ? 'choose one of your stones, or go and stand near it'
      : 'you are bound to no place yet — press V where you mean it');
    return;
  }

  if (picked.aura) {
    sim.douseAura(picked.id);
    notice('you take yourself back out of the place');
    return;
  }

  const r = sim.kindleAura(picked.id, armed, clock.t);
  if (r.ok) {
    const ground = Math.max(groundAt(picked.x, picked.z), 0);
    verbFx.spawn(armed, picked.x, ground, picked.z, AURA_RADIUS);
    sound.cast(armed, 0);
    // deliberately vague about the rate: the player should learn what an aura
    // is worth by leaving and coming back, which is the whole mechanic
    notice(`you leave something of yourself here — it will work while you are away`);
  } else {
    notice(r.reason === 'too-poor'
      ? `not enough of you left to hold a place — ${Math.round(sim.pantheon.player.power * 100)}%`
      : 'that is not yours to hold');
  }
};
addEventListener('keydown', (e) => {
  if (modes.mode !== 'deity') return;
  if (e.shiftKey && e.key.toLowerCase() === 'v') { e.preventDefault(); doAura(); }
});

/* ── taking hold of the ground itself ──────────────────────────────────── */

/*
 * This used to be a SECOND earth verb on shift-G (`scooping` / `doEarthTake` /
 * `doEarthDrop`), with the hold priced as a share of the god and the depth
 * solved from it. It is gone, and deliberately: the dig above supersedes it.
 *
 * Two implementations of §20's headline verb existed at once because the merge
 * that brought this file together took one side's binding and the other side's
 * call sites. The dig is the one that survives — it is law 20's argument made
 * literal (`hold G on a thing and you lift the thing; hold G on the ground and
 * you take hold of the ground` — ONE key, one gesture, two scales), it is what
 * `render/reticle.ts`'s `dig()` was written for, and it reaches `moveEarth`'s
 * `presence`/`arm` arguments, which the shift-G version never passed.
 *
 * What the deleted version knew, kept because it is measured and still true:
 * the hold picks the SPEND and the depth is solved from it, never the other way
 * round — deriving depth from the focus radius costs twelve times a whole god
 * at the wheel's default 134 m, so `moveEarth` refused every gesture, silently,
 * because a refusal notice needs a frame to paint. `scoopFor` in `world/earth.ts`
 * carries that rule now.
 */

/**
 * Shaping the land — **Y**, with a verb armed (law 28, `world/geology.ts`).
 *
 * The counterpart to Grasp, and the pair is the design: `moveEarth` puts a
 * hill wherever you like and charges for every cubic metre, this only makes
 * the land do more of what it was already doing — and on ground that is doing
 * it hard, about 20× the earth per unit of god. A god that understands its
 * valley moves more than a god that is strong.
 *
 * It refuses on the 42% of the island where nothing is happening, and says so
 * in the land's own terms rather than as an error.
 */
const doShape = () => {
  const verb = (hud.armed === 'weaken' ? 'weaken' : 'strengthen') as 'strengthen' | 'weaken';
  // `hovered` whenever there is one, not only when it is a *thing* — the same
  // fault `aimPoint`'s own comment describes, still live here. Aiming at bare
  // ground is the ordinary case for shaping terrain, and `isThing` refuses it,
  // so Y was quietly hurrying the ground under the god's feet instead of the
  // hillside being pointed at.
  const x = hovered ? hovered.x : presence.x;
  const z = hovered ? hovered.z : presence.z;

  const reading = sim.geologyAt(x, z);
  if (reading.rate <= 0) {
    notice('this ground is going nowhere — nothing here to hurry');
    return;
  }

  const r = sim.shapeLand(verb, x, z, Math.min(focusRadius, focusCap), clock.t);
  if (!r.ok) {
    notice(r.reason === 'too-weak'
      ? `not enough of you left — ${Math.round(sim.pantheon.player.power * 100)}% of` +
        ` ${Math.round((r.cost ?? 0) * 100)}% needed`
      : 'nothing here to hurry');
    return;
  }
  const ground = Math.max(groundAt(x, z), 0);
  verbFx.spawn(verb, x, ground, z, 120);
  sound.cast(verb, 0);
  // named for what the land did, never for what the god spent
  notice(r.process === 'slump'
      ? (verb === 'strengthen' ? 'the hillside lets go' : 'the hillside holds')
    : r.process === 'incise'
      ? (verb === 'strengthen' ? 'the water cuts deeper' : 'the channel fills')
      : (verb === 'strengthen' ? 'the flat gathers what the river carries' : 'the flat gives it back'));
};
addEventListener('keydown', (e) => {
  if (modes.mode !== 'deity') return;
  if (e.key.toLowerCase() === 'y' && !e.metaKey && !e.ctrlKey) doShape();
});

/* ── the far view reads history too ────────────────────────────────────────
 *
 * `applyEnv`, `applyOverlay` and `applyEarth` are CHUNK transforms, so
 * everything they do stops at the streamed ring — three systems each recorded
 * "invisible past the ring" as a separate open problem. The horizon mesh now
 * samples the same stores (`world/farField.ts`), which leaves only the
 * question of when to make it look again.
 *
 * Not every frame: a full pass is 102k vertices with four colour taps each.
 * So it watches the stores' own version tags — they already keep them as
 * cache tags — accumulates the box of whatever changed, and redoes only that
 * box, at most a few times a second. A blessing that decays a little every
 * half-hour of world time must not drag the far mesh with it every frame.
 */
let farSeen = farVersion({ earth: sim.earth, env: sim.env, overlay: sim.overlay });
let farBox: FarBounds | null = null;
let farNextAt = 0;
const FAR_MIN_GAP = 400;   // ms between far rebuilds, at most

const noteFarChange = (keys: readonly string[]) => {
  const b = boundsOfChunks(keys, CHUNK_SIZE);
  if (!b) return;
  farBox = farBox ? {
    x0: Math.min(farBox.x0, b.x0), z0: Math.min(farBox.z0, b.z0),
    x1: Math.max(farBox.x1, b.x1), z1: Math.max(farBox.z1, b.z1),
  } : b;
};

const refreshFarField = () => {
  const v = farVersion({ earth: sim.earth, env: sim.env, overlay: sim.overlay });
  if (v === farSeen && !farBox) return;
  const now = performance.now();
  if (now < farNextAt) return;
  farNextAt = now + FAR_MIN_GAP;
  farSeen = v;
  horizon.refresh(clock.t, farBox);
  farBox = null;
};

/**
 * What each verb would cost, right now, as a category.
 *
 * The same `costOf` the act pays, fed the same focus, the same distance from
 * the presence and the same ground — so the pip under a verb and the power
 * the cast actually takes can never disagree. Reading it writes nothing: law
 * 9 covers previews as much as it covers hovering.
 */
/**
 * Where a cast would actually land, which is what the preview must price.
 *
 * The first version used the hovered point only when it was a *thing*, and
 * fell back to the presence otherwise — so pointing at a hillside a kilometre
 * off quoted the ground under your own feet, and `costOf`'s reach term (the
 * one that makes distance expensive) read zero. `Target` carries coordinates
 * for bare ground too; `isThing` only decides whether the FOCUS collapses onto
 * a thing's own footprint, which is a different question.
 */
/**
 * THE TWO HALVES OF WHAT A GOD HAS CARRIED OFF, kept apart because two
 * different renderers draw them and each owns one vocabulary.
 *
 * `CarriedStore.kind` is deliberately a bare string — "in whatever vocabulary
 * its own layer uses" — which is what let relics into the store with no
 * snapshot change at all. It is also what makes this split necessary:
 * `nearDetail`'s carried pass indexes `KIND_INDEX[c.kind] ?? K_STONE`, so a
 * standing stone handed to it would come out as a very large pebble, while
 * `render/relics.ts` went on drawing the same stone at the position the
 * lattice grew it. The thing in two places at once is the one failure this
 * whole path had to avoid.
 */
const RELIC_KIND = new Set<string>(RELIC_KINDS);
const carriedLitter = (x: number, z: number) =>
  sim.carried.near(x, z, 260).filter((c) => !RELIC_KIND.has(c.kind));
const movedRelics = () =>
  sim.carried.all().filter((c) => RELIC_KIND.has(c.kind));

const aimPoint = () => {
  const aim = hovered && isThing(hovered) ? hovered : null;
  const x = hovered ? hovered.x : presence.x;
  const z = hovered ? hovered.z : presence.z;
  const site = region.siteAt(x, z);
  const env = sim.env.at(x, z, clock.t);
  return {
    x, z,
    focus: focusFor_(aim),
    cond: { rain: site.rain, biomass: env?.biomass ?? 0, moisture: env?.moisture ?? 0 },
    away: Math.hypot(presence.x - x, presence.z - z),
  };
};

/**
 * Why the armed verb costs what it does, in the land's own words.
 *
 * `costReasons` has been in `leverage.ts` since the economy work and nothing
 * has ever shown it, so the 6.8× spread between working with the land and
 * against it was true and unlearnable. Now the panel says *with the land* or
 * *against the land* while you are aiming, which is the only way a player
 * finds out that where they stand is worth more than how hard they push.
 */
const armedCostWhy = (): string[] => {
  const v = hud.armed;
  if (!v) return [];
  const a = aimPoint();
  return costReasons(costOf(v, a.focus.radius, a.away, a.cond, Infinity, castModsFor(hovered?.kind)));
};

/**
 * WHAT THE BAR SHOWS, priced for the bar that is actually there.
 *
 * This priced `strengthen · weaken · omen` — the pre-`DECISIONS.md` palette —
 * long after the four starters became `strengthen · strike · omen · grasp`.
 * So arming `2` or `4` printed a literal `—` where the price band goes: two of
 * the four starter verbs quoted the player nothing at all, on the one surface
 * whose whole job is to say what an act would take of them.
 *
 * `weaken` stays priced although it is off the bar, because it is still
 * castable through the modifier path and `hud.armed`'s row reads this map for
 * whatever is armed, modifier-armed included.
 *
 * **And Grasp is priced from a different ledger, so it asks a different
 * function.** `costOf` is radius and distance; a grasp is
 * `graspCost(mass, attachment)`, which is why the bar printed a literal dash
 * under it — the one verb whose price varies by three orders of magnitude
 * between a twig and a standing stone, quoting nothing. `sim.graspPrice`
 * resolves the subject through the same `graspableAt` the act itself uses, so
 * the quote and the bill cannot name different things.
 *
 * Absent over ground with nothing on it, and that is honest rather than a
 * gap: there the gesture is the EARTH grasp, whose price is decided by how
 * long the hand is held, and no band can be quoted before the hold begins.
 */
const verbCostBands = (): Partial<Record<Verb, CostBand>> => {
  const a = aimPoint();
  const out: Partial<Record<Verb, CostBand>> = {};
  for (const v of ['strengthen', 'strike', 'omen', 'weaken'] as CastVerb[]) {
    out[v] = costBand(costOf(v, a.focus.radius, a.away, a.cond, Infinity, castModsFor(hovered?.kind)).total);
  }
  if (hovered && LIFTABLE.has(hovered.kind)) {
    const p = sim.graspPrice(hovered.x, hovered.z, clock.t);
    if (p) out.grasp = costBand(p.cost);
  }
  return out;
};


// dev-only debug handle for poking at the live scene from the console
declare global { interface Window { __terra?: unknown } }
if (import.meta.env.DEV) {
  window.__terra = {
    renderer, scene, camera, chunks, region, clock, farCanopy, landmarks,
    // the aura: `__terra.aura.radius = 320` re-spends the detail budget live
    aura,
    // the displayed (glided) party positions, keyed by party id — a dev
    // window onto `ui/glide.ts`'s output, for measuring motion continuity
    // against the sim's hourly stair without touching the renderer
    glide: partyGlide,
    // `sim` is reassigned whenever a world is adopted, so this must be a
    // getter — captured by value it silently hands out the world that was
    // replaced, which reads exactly like the adoption having failed
    get sim() { return sim; },
    get god() { return sim.pantheon.player; },
    get pantheon() { return sim.pantheon; },
    // the presence is the player's body; `controls` stays as an alias so old
    // console habits and probes keep working
    presence, controls: presence,
    // what the pointer is on, what has been chosen, and the pick itself —
    // so a probe can ask the running world what is under a screen point
    // without a mouse. Reading any of this changes nothing.
    get hovered() { return hovered; },
    get chosen() { return chosen; },
    /**
     * Selection, and the eye that follows it — the REAL entrypoints, not a copy
     * of their arithmetic. `choose` is what a bare click calls, so a probe can
     * put the card in front of every kind of subject and watch the camera go
     * without synthesising a pointer-lock click; `attention` is what the eye is
     * currently doing about it, which is otherwise unobservable from outside.
     *
     * The same reasoning as `earthTake`/`earthDrop`: a binding nobody can drive
     * from a harness is how `moveEarth` came to ship with no input path at all.
     */
    choose: (t: Target | null) => choose(t),
    get attention() { return attention; },
    /**
     * THE TWO BODIES (2026-08-19). Which one the player is in, the mortal's
     * feet, and the doors a harness needs — choose a life by id and descend
     * into it, or ascend — through the SAME `enterMortal`/`ascend` the bar's
     * control calls, so a probe walks exactly what a player walks.
     */
    get mode() { return modes.mode; },
    modes, mortal,
    personTarget,
    inhabit: (personId?: string) => {
      if (personId) choose(personTarget(personId));
      enterMortal();
      return modes.mode;
    },
    ascend: () => { ascend(); return modes.mode; },
    get card() { return cardFor(chosen, presence); },
    /**
     * The arrival — the eye looking around on a fresh world (`ui/arrival.ts`).
     * `arrival` is the tour in progress or null; `arrive(x, z)` plans and
     * starts one over a place, exactly as boot does, so the first ten minutes
     * can be replayed from a harness without reloading the world.
     */
    get arrival() { return arrival; },
    arrive: (x: number, z: number) => { openOver(x, z); arrivalPending = { x, z }; },
    /** Every hook the arrival would consider from a place, unplanned — for a probe. */
    arrivalHooks: (x: number, z: number) => gatherHooks({
      sim, landmarks, herds: wildlifeViews, now: clock.t, events: sim.events.all(),
      stirring: sim.wildlife.stirring(), drawnFalls: falls.drawnIds,
      heightAt: (qx, qz) => region.heightAt(qx, qz),
    }, { home: { x, z }, eye: vantageOver({ x, z }, Math.max(region.heightAt(x, z), 0), presence) }),
    /**
     * The herds the eye can currently reach, and the renderer's own body
     * placement. Individual animals are pointable now, and their identity is
     * `(herd seed, index)` rather than anything stored — so without these a
     * probe cannot ask "is the animal the pointer named standing where it is
     * drawn?", which is the only question that matters about the seam.
     */
    /**
     * The relics as the RENDERER holds them — the drawn list in lattice order,
     * which is the only way a probe can ask "is the stone the god moved being
     * drawn where the god put it?". Without it the render half of A6 is
     * unverifiable from outside, which is the state the whole verb was in.
     */
    get relics() { return relics.all; },
    get moved() { return sim.carried.all(); },
    get wildlifeViews() { return wildlifeViews as readonly WildlifeView[]; },
    bodyAt, bodiesInView,
    /** What `?pheno=` armed, if anything — the lab's parse, for a probe. */
    phenoLab,
    /** The strain field and its reads — the wake's stores, for probes. */
    get strains() { return sim.strains; },
    get strainReads() { return sim.strainReads; },
    /**
     * The seconds the herds were last DRAWN at — real seconds since load, not
     * `clock.t`. A probe that reaches for the world clock here computes where
     * the animals would be in a different universe and then agrees with itself
     * about it; see `drawnAtSeconds`.
     */
    get drawnAtSeconds() { return drawnAtSeconds; },
    /**
     * Where anything stands: `surfaceHeight` at full detail plus whatever a
     * god has done to the ground. A probe that reaches for `region.heightAt`
     * instead is reading the COARSE field, and the two differ by a median
     * 7.4 m (`tools/pick.ts`) — which at a shallow look-down angle throws a
     * projected screen position metres sideways and makes a probe measure its
     * own aim rather than the game's answer.
     */
    groundAt: (x: number, z: number) => groundAt(x, z),
    /**
     * The two halves of the earth gesture, so a probe can exercise the REAL
     * handlers rather than a copy of their arithmetic. Synthetic
     * `KeyboardEvent`s are not a reliable way to drive a held key from a test
     * harness, and a binding nobody can test is how `moveEarth` came to have
     * no input path in the first place.
     *
     * `heldSeconds` is honoured by back-dating the hold's own start, so the
     * probe drives the SAME `digScoop` the release reads rather than a second
     * arithmetic path that could drift from it.
     */
    earthTake: () => doDigTake(),
    /**
     * And the hand on a THING, for the same reason: `doGraspTake` reads
     * `hovered`, which a harness cannot set, so a probe hands it the target
     * the pointer would have resolved (`pickAtScreen` returns that shape).
     */
    graspTake: (t?: Target | null) => {
      if (t) hovered = t;
      doGraspTake();
      return heldNow();
    },
    graspDrop: () => doGraspDrop(),
    earthDrop: (heldSeconds?: number) => {
      const d0 = digNow();
      if (d0 && heldSeconds !== undefined) {
        session.dig = { ...d0, at: performance.now() - heldSeconds * 1000 };
      }
      doDigDrop();
    },
    /**
     * …and what the hand is doing, which is the only piece of this a probe
     * cannot see from outside: a hold in progress writes nothing anywhere by
     * design, so without this there is no way to tell "the dig was refused"
     * from "the key never arrived".
     */
    get holding() {
      const d1 = digNow();
      return { thing: heldNow()?.name ?? null, digging: d1 && { ...d1 },
               scoop: d1 ? digScoop(d1) : null,
               arm: digArm() };
    },
    pickAtScreen: (px: number, py: number) => {
      const r = stage.getBoundingClientRect();
      return pickAtClient(r.left + px, r.top + py);
    },
    // focus is a control surface of its own now: read it, set it, or drive a
    // cast straight through it, without having to reach for the wheel
    focusFor, focusForRadius, weatherAt, boltsAtMinute,
    // how much weather is actually being drawn somewhere other than here
    get curtains() { return curtains.drawn; },
    // the Web: `__terra.web` is the allocation, `__terra.godhood` the panel
    web: godhoodWeb, godhood, post,
    // the light rig's dials (sun/fill/hemisphere balance and colours) — dial,
    // look, then write the number into `LIGHT`; nothing here is saved
    light: LIGHT,
    // the far view's own state, so a probe can check what the horizon and the
    // far canopy are actually being told rather than guessing from a frame
    horizon, canopyField,
    farField: () => ({ version: farVersion({ earth: sim.earth, env: sim.env, overlay: sim.overlay }),
                       texelAt: (x: number, z: number) => {
                         const n = region.n, cell = region.cell, half = n * cell / 2;
                         const i = Math.floor((x + half) / cell), j = Math.floor((z + half) / cell);
                         const k = (j * n + i) * 4;
                         return { i, j, R: canopyField[k], G: canopyField[k + 1],
                                  B: canopyField[k + 2],
                                  biomass: (canopyField[k + 1] - 128) / 127,
                                  lift: (canopyField[k + 2] - 128) * 0.5 };
                       } }),
    getFocus: () => focusForRadius(Math.min(focusRadius, focusCap)),
    setFocus: (r: number) => { presence.zoomTo(camForFocus(r)); syncFocus(); },
    /**
     * Dev-only deterministic time advance — `PLAYABLE.md` P7's whole harness.
     *
     *     __terra.advance.day()      __terra.advance.season()
     *     __terra.advance.year()     __terra.advance.years(20)
     *
     * Named units rather than a raw number because the units are the point: a
     * playtest asks "what does this look like a year on", not "advance
     * 31536000". See `devAdvance` for the two traps this exists to avoid —
     * chiefly that moving `clock.t` by hand advances the sun and not the world.
     */
    advance: {
      day: () => devAdvance(DAY),
      season: () => devAdvance(DAY * 91),
      year: () => devAdvance(DAY * 365),
      years: (n: number) => devAdvance(DAY * 365 * n),
      worldSeconds: (s: number) => devAdvance(s),
    },
    /**
     * A cast, through the real entrypoint.
     *
     * `onChosen` is not a convenience. Without it this could only ever cast at
     * BARE GROUND — `cast()` takes an optional target and `Simulation.act`
     * only presses on people when one is a person — so the single most
     * important interaction in the build, *aim a verb at somebody you have
     * chosen*, was undrivable from a harness. Which is the same failure mode
     * `earthTake`/`earthDrop` exist for, one verb over: a binding nobody can
     * drive from a probe is how a path comes to ship untested.
     *
     * It routes through `chosen` rather than taking a Target, so a probe casts
     * on exactly what the card is open on and cannot accidentally construct a
     * subject the pick would never have produced.
     */
    /**
     * ANOTHER GOD IN THIS WORLD — `PLAYABLE.md` P6, in the half that needs no
     * server, and a binding a probe can actually drive.
     *
     * The rival substrate has existed since v0.3.2: `GodState` carries its own
     * reach, purse, seat and strands, `Pantheon.add` is a `Map.set`, every
     * entry point takes a `deityId`, and the whole thing round-trips through
     * the snapshot. It was instantiated by exactly one file — `test/deity.ts` —
     * so the thing that gates the entire multiplayer proof had never been
     * switched on in a running world.
     *
     * What this proves, and it is P6's first three clauses minus the wire:
     * another god acts in the same authoritative world; the consequence
     * reaches this client's ground and this client's Chronicle; and **the
     * attribution does not** — `perceive` reduces a divine cause to
     * `unexplained` before anything here sees it, so a rival's Strike and the
     * sky's own bolt are the same picture and the same sentence.
     *
     * Dev-only, like every other door on `__terra`.
     */
    rival: (opts?: { at?: { x: number; z: number }; power?: number }) => {
      const seat = opts?.at ?? { x: presence.x + 900, z: presence.z + 900 };
      const g = sim.pantheon.get('the-other') ?? sim.pantheon.add(
        new GodState(0.6, { id: 'the-other', kind: 'npc',
                            power: opts?.power ?? 1, impulse: 0.4, seat }));
      g.power = opts?.power ?? 1;
      return { id: g.id, seat, power: g.power };
    },
    /** …and it acts. The same `sim.act` the player's own click reaches. */
    rivalCasts: (verb: CastVerb, x: number, z: number) => {
      const g = sim.pantheon.get('the-other');
      if (!g) return 'no rival in this world — call __terra.rival() first';
      const r = sim.act(verb, x, z, clock.t, focusForRadius(240), g.id, undefined,
                        { x: g.seat?.x ?? x, z: g.seat?.z ?? z });
      return { ...r, theirPowerLeft: g.power, yoursUntouched: sim.pantheon.player.power };
    },
    /**
     * The hosted world, when there is one — `?server=…`. Null on every
     * ordinary boot.
     *
     * Here because it is the only piece of attachment a probe cannot see from
     * outside: a client that has silently LOST its server goes on predicting a
     * world nobody else is in, and from the browser that looks exactly like a
     * client that is still attached. Now it does not.
     */
    get attached() {
      return attached && {
        worldId: attached.worldId, deityId: attached.deityId,
        name: attached.name ?? null,
        connected: attached.conn.connected,
        session: attached.conn.session,
        worldTime: attached.worldTimeNow(),
        lag: attached.conn.lag,
        stale: attached.stale,
        others: attached.others,
      };
    },
    castAt: (verb: CastVerb, x: number, z: number, onChosen = false) => {
      const t = onChosen ? chosen : null;
      const p0 = t ? new THREE.Vector3(t.x, t.y, t.z)
        : new THREE.Vector3(x, surfaceHeight(region, x, z, 1), z);
      cast(verb, p0, t);
      return { focus: focusFor_(t), channelled: channelled(t) };
    },
  };
}
